<template>
  <!--
    หน้า detail = หน้า graph editor ของ nexa ที่ลูกค้าเห็นตอนนำเสนอผ่าน 
    (nexa-rs/public/index.html — แถบกราฟ + แถบค้นหา + แผง Graph Commands)
    เครื่องมือที่เดิมเป็นแท็บ ย้ายมาเปิดเป็น dialog จากปุ่มในแผง/ปุ่ม Manage
  -->
  <div class="kgc-root" data-testid="editor-root">
    <!-- ── แถบกราฟ ─────────────────────────────────────────────── -->
    <div class="kgc-toolbar">
      <span class="kgc-label">{{ $t('graphRagGraphLabel') }}</span>
      <input v-model="graph.name" class="kgc-name" data-testid="canvas-graph-name" :disabled="!canEdit" />

      <span class="kgc-tag kgc-tag-cloud">☁ {{ $t('graphRagStorageCloud') }}</span>
      <span class="kgc-tag kgc-tag-type">{{ (graph.graphType || '').toUpperCase() }}</span>

      <select v-model="selectedVersion" class="kgc-version" data-testid="canvas-version">
        <option :value="graph.currentVersionNo">
          v{{ graph.currentVersionNo || 1 }} · {{ $t('graphRagCurrent') }}
        </option>
        <option v-for="v in previousVersions" :key="v.versionNo" :value="v.versionNo">v{{ v.versionNo }}</option>
      </select>

      <!-- เลือกเวอร์ชันเก่าจาก dropdown แล้วกด ↩ = ย้อนกลับ (spec S4-P1: [เวอร์ชัน ▾] [↩ ย้อนกลับ]) -->
      <button
        type="button"
        class="kgc-btn"
        data-testid="canvas-version-revert"
        :title="selectedVersionItem && !selectedVersionItem.hasArchive ? $t('graphRagNoArchive') : $t('graphRagRevert')"
        :disabled="!canRevertSelected"
        @click="canRevertSelected && revertTo(selectedVersionItem)"
      >
        ↩ {{ $t('graphRagRevert') }}
      </button>

      <!-- โมเดลที่ใช้ตอบคำถามจากแถบค้นหา (config key model) — เลือกแล้วบันทึกทันที ไม่ต้องเปิด Search Settings -->
      <div class="kgc-ask-model" :title="$t('graphRagAskModelHint')">
        <span class="kgc-label">{{ $t('graphRagAskModel') }}</span>
        <!--
          v-autocomplete: มีลูกศร dropdown + ไอคอนบอท + พิมพ์ค้นได้ทั้งเลข model id และชื่อ (title = "{ModelDisplay} {ชื่อ}")
          ชื่อที่เลือกอยู่เป็นข้อความจริงในช่อง (ไม่ใช้ slot selection) ⇒ แก้/ลบทีละตัวอักษรได้เหมือนช่องข้อความทั่วไป
          คลิกแล้วเลือกข้อความทั้งช่องให้ (selectModelText) ⇒ พิมพ์ทับเป็นคำค้นได้ทันที ไม่ต่อท้ายชื่อเดิม
        -->
        <v-autocomplete
          ref="askModelSelect"
          v-model="toolbarAskModel"
          v-model:menu="askModelMenu"
          :items="askModelItems"
          item-title="title"
          item-value="value"
          class="kgc-ask-model-select"
          data-testid="canvas-ask-model"
          variant="outlined"
          density="compact"
          hide-details
          single-line
          prepend-inner-icon="mdi-robot"
          :loading="askModelsLoading"
          :disabled="!canEdit || !configLoaded || savingAskModel"
          :menu-props="{ maxHeight: 360 }"
          :no-data-text="$t('noDataAvailable')"
          @update:focused="selectModelText($event, 'askModelSelect')"
          @keydown="onModelKeydown($event, askModelTitle)"
        >
          <template #item="{ props: itemProps, item }">
            <!-- ไอคอนอยู่ในบรรทัดเดียวกับเลข (ไม่ใช้ slot prepend ที่เว้นช่องกว้าง 32px) ให้ระยะเท่ากับในช่อง -->
            <v-list-item v-bind="itemProps" :title="undefined" density="compact">
              <span class="kgc-ask-model-text">
                <v-icon size="18" :color="item.raw.value ? '#C32E0E' : 'grey'">{{ item.raw.value ? 'mdi-robot' : 'mdi-robot-off' }}</v-icon>
                <span v-if="item.raw.displayId != null" class="kgc-ask-model-id">{{ item.raw.displayId }}</span>
                {{ item.raw.name }}
              </span>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>

      <span class="kgc-spacer" />

      <!-- Save = บันทึกชื่อ → สร้างเวอร์ชัน → re-embed โหนดที่ข้อความเปลี่ยนเป็นงานเบื้องหลัง (เหมือน /save-stream ) -->
      <button type="button" class="kgc-btn kgc-btn-primary" :disabled="!canEdit || savingName" data-testid="canvas-save" @click="canEdit && !savingName && saveGraph()">
        💾 {{ $t('save') }}
      </button>

      <span v-if="embedJob" class="kgc-embed" data-testid="canvas-embed-progress">
        ⚡ {{ $t('graphRagEmbedRunning', { processed: embedJob.processedUnits || 0, total: embedJob.totalUnits || 0 }) }}
        <button
          type="button"
          class="kgc-embed-cancel"
          data-testid="canvas-embed-cancel"
          :title="$t('graphRagEmbedCancel')"
          :aria-label="$t('graphRagEmbedCancel')"
          :disabled="!canEdit || embedCancelling"
          @click="canEdit && !embedCancelling && cancelEmbedding()"
        >
          ✕
        </button>
      </span>

      <button type="button" class="kgc-btn" :disabled="!canEdit" data-testid="canvas-merge" @click="openMergeDialog">
        ⑂ {{ $t('graphRagMerge') }}
      </button>

      <button type="button" class="kgc-btn" data-testid="canvas-manage" @click="showManage = true">⚙ {{ $t('manage') }}</button>

      <!-- เปิดผืนกราฟอยู่ = ปุ่มเดียวกันเป็น "ออกจากผืนกราฟ" (กลับไปแผง Graph Commands) -->
      <button type="button" class="kgc-btn" :aria-pressed="String(canvasOn)" data-testid="canvas-render-toggle" @click="toggleCanvas">
        {{ canvasOn ? `✕ ${$t('graphRagExitCanvas')}` : `🖼️ ${$t('graphRagCanvasView')}` }}
      </button>
    </div>

    <!-- ── แถบค้นหา ────────────────────────────────────────────── -->
    <div class="kgc-searchbar">
      <div class="kgc-effort">
        <button
          v-for="e in efforts"
          :key="e.value"
          type="button"
          :class="{ active: effort === e.value }"
          :data-testid="`canvas-effort-${e.value}`"
          @click="effort = e.value"
        >
          {{ e.text }}
        </button>
      </div>

      <input
        v-model="canvasQuery"
        class="kgc-query"
        :placeholder="$t('graphRagAskHere')"
        data-testid="canvas-query"
        @keydown.enter="runCanvasSearch"
      />

      <button type="button" class="kgc-btn kgc-btn-primary" :disabled="!canSearch" data-testid="canvas-search" @click="canSearch && runCanvasSearch()">
        {{ $t('search') }}
      </button>
      <button type="button" class="kgc-btn" data-testid="canvas-reset" @click="resetCanvasSearch">{{ $t('reset') }}</button>
    </div>

    <!-- ── เนื้อหาหลัก (ผืนกราฟ / Graph Commands) + แผงคำตอบด้านขวา (Graph Ask) —   .container [main | .sidebar] ──
         จอแคบ (< 960px) แผงลงไปอยู่ใต้เนื้อหาหลัก · ผืนกราฟแคบลงตามแผง ⇒ แผงรายละเอียดโหนด (absolute มุมขวาล่างใน GraphCanvas) อยู่ในกรอบผืนกราฟเสมอ ไม่ซ้อนแผงคำตอบ ·
         vis ปรับขนาดตาม container เอง (autoResize = ResizeObserver) และคงจุดกึ่งกลางกล้องไว้ — ไม่ต้องสั่ง redraw/fit -->
    <div class="kgc-body">
      <!-- ── ผืนกราฟ (เปิดด้วย Canvas View) ──────────────────────── -->
      <div v-if="canvasOn" class="kgc-canvas">
        <GraphCanvas
          ref="graphCanvas"
          :nodes="canvasNodes"
          :edges="canvasEdges"
          :highlight="canvasHits"
          :total="canvasTotal"
          :type-colors="canvasTypeColors"
          :type-labels="canvasTypeLabels"
          :graph-key="graphKey"
          :can-edit="canEdit"
          :load-state="canvasLoadState"
          :loaded-count="canvasLoadedCount"
          :answer-path="askPath"
          @node-click="onCanvasNodeClick"
          @edit-node="onCanvasEditNode"
          @move-node="onCanvasMoveNode"
          @load-change="onCanvasLoadChange"
        />
      </div>

      <!-- ── Graph Commands (ค่าเริ่มต้นตอนไม่วาดกราฟ) ───────────── -->
      <div v-else class="gcc" data-testid="graph-commands">
        <div class="gcc-inner">
          <div class="gcc-header">
            <div>
              <div class="gcc-title">{{ $t('graphRagCommands') }}</div>
              <div class="gcc-subtitle">{{ $t('graphRagCommandsHint') }}</div>
            </div>
            <div class="gcc-badge" data-testid="graph-commands-badge">{{ graphKey }} · {{ $t('graphRagRendererOff') }}</div>
          </div>

          <div class="gcc-grid">
            <!-- สร้าง -->
            <div class="gcc-card">
              <div class="gcc-card-title">{{ $t('graphRagCreate') }}</div>
              <div class="gcc-card-text">{{ $t('graphRagCreateHint') }}</div>
              <div class="gcc-actions">
                <button type="button" class="gcc-btn primary" data-testid="cmd-create-from" :disabled="!canEdit" @click="openIngest">
                  {{ $t('graphRagCreateGraphFrom') }}
                </button>
                <button type="button" class="gcc-btn" data-testid="cmd-duplicate-from" :disabled="!canEdit" @click="openDuplicateFrom">
                  {{ $t('graphRagDuplicateFrom') }}
                </button>
                <button type="button" class="gcc-btn" data-testid="cmd-merge-graphs" :disabled="!canEdit" @click="openMergeDialog">
                  {{ $t('graphRagMergeGraphs') }}
                </button>
                <!-- ปุ่ม Import Under Node ID ถูกลบออกตามคำสั่งผู้ใช้ 2026-10-01  -->
              </div>
              <div class="gcc-result" :class="{ error: createResultError }" data-testid="cmd-create-result">{{ createResult }}</div>
            </div>

            <!-- จัดการ -->
            <div class="gcc-card">
              <div class="gcc-card-title">{{ $t('manage') }}</div>
              <div class="gcc-card-text">{{ $t('graphRagManageHint') }}</div>
              <div class="gcc-actions">
                <button type="button" class="gcc-btn primary" data-testid="cmd-add-node" :disabled="!canEdit" @click="openNode(null)">
                  {{ $t('graphRagAddNode') }}
                </button>
                <button type="button" class="gcc-btn" data-testid="cmd-create-graph-node" :disabled="!canEdit" @click="openGraphNode">
                  {{ $t('graphRagCreateGraphNode') }}
                </button>
                <button type="button" class="gcc-btn" data-testid="cmd-manage-nodes" @click="openNodeManager">{{ $t('graphRagManageNodes') }}</button>
                <button type="button" class="gcc-btn" data-testid="cmd-manage-edges" @click="openEdgeManager()">{{ $t('graphRagManageEdges') }}</button>
              </div>

              <div class="gcc-card-text">{{ $t('graphRagNodeIdHint') }}</div>
              <div class="gcc-input-row">
                <!-- ค้นโหนดจาก label แทนการพิมพ์ node id (ฟอร์มแก้โหนดไม่แสดง node id ผู้ใช้จึงไม่รู้ค่านี้) ·
                     ค้นฝั่ง backend (GetAll search = label / secondary label / node key) · label ซ้ำได้ ⇒ เลือกจากรายการ ได้ nodeKey ที่ตรงตัวเสมอ -->
                <v-autocomplete
                  v-model="commandNode"
                  v-model:search="commandNodeSearch"
                  :items="commandNodeOptions"
                  :loading="commandNodeLoading"
                  item-title="label"
                  item-value="nodeKey"
                  return-object
                  no-filter
                  clearable
                  hide-details
                  density="compact"
                  variant="outlined"
                  class="gcc-node-picker"
                  :placeholder="$t('graphRagNodeIdField')"
                  :no-data-text="commandNodeSearch ? $t('graphRagNodeNotFound') : $t('graphRagTypeToSearchNode')"
                  data-testid="cmd-node-id"
                  @update:model-value="setCommandResult('', false)"
                >
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props" :title="item.raw.label || item.raw.nodeKey" :subtitle="commandNodeSubtitle(item.raw)" />
                  </template>
                  <!-- ต้องมี slot นี้: ไม่มีแล้ว Vuetify 3.8 รีเซ็ตคำค้นเป็นชื่อของค่าที่เลือกทุกครั้งที่ items เปลี่ยน
                       (VAutocomplete watch(model) → search = title) ⇒ ข้อความที่ผู้ใช้กำลังพิมพ์หายทันทีที่ผลค้นหากลับมา ·
                       ซ่อนชื่อที่เลือกไว้ระหว่างพิมพ์คำค้นใหม่ ไม่งั้นสองข้อความต่อกันในช่อง (blur แล้ว Vuetify ล้างคำค้น ⇒ ชื่อเดิมกลับมา) -->
                  <template #selection="{ item }">
                    <span v-if="!commandNodeSearch" class="gcc-node-picker-selection">{{ item.raw.label || item.raw.nodeKey }}</span>
                  </template>
                </v-autocomplete>
                <button type="button" class="gcc-btn" data-testid="cmd-node-preview" @click="previewNode">{{ $t('graphRagPreview') }}</button>
                <button type="button" class="gcc-btn" data-testid="cmd-node-edit" :disabled="!canEdit" @click="editNodeById">{{ $t('edit') }}</button>
                <button type="button" class="gcc-btn" data-testid="cmd-node-add-edge" :disabled="!canEdit" @click="addEdgeFromNode">
                  {{ $t('graphRagAddEdgeFromHere') }}
                </button>
                <button type="button" class="gcc-btn" data-testid="cmd-node-move" :disabled="!canEdit" @click="moveNodeFromCommand">
                  {{ $t('graphRagMoveNode') }}
                </button>
                <button type="button" class="gcc-btn danger" data-testid="cmd-node-delete" :disabled="!canEdit" @click="deleteNodeById">{{ $t('delete') }}</button>
                <button type="button" class="gcc-btn danger" data-testid="cmd-node-delete-subtree" :disabled="!canEdit" @click="deleteSubtree">
                  {{ $t('graphRagDeleteSubtree') }}
                </button>
              </div>
              <div ref="cmdNodeResult" class="gcc-result" :class="{ error: nodeResultError }" data-testid="cmd-node-result">{{ nodeResult }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── แผงคำตอบ (Graph Ask) — อยู่นอก v-if ของผืนกราฟ ⇒ เห็นทั้งโหมด canvas และ Graph Commands · พับเหลือแถบแคบได้ (askCollapsed) · ✕ = closeAsk ──
           คำตอบจาก LLM ไม่ผ่าน v-html เลย: แตกเป็น segment (ข้อความ / เลขอ้างอิง) แล้ว render ทีละชิ้น ⇒ HTML ในคำตอบแสดงเป็นตัวอักษรเฉย ๆ -->
      <aside v-if="askOpen" class="kgc-ask" :class="{ 'kgc-ask-collapsed': askCollapsed }" :aria-label="$t('graphRagAskTitle')" data-testid="canvas-ask">
        <!-- พับอยู่: เหลือแถบแคบ กดทั้งแถบเพื่อขยาย · ยังรอคำตอบอยู่ = มี spinner ให้เห็นว่ากำลังทำงาน -->
        <button
          v-if="askCollapsed"
          ref="askExpandBtn"
          type="button"
          class="kgc-ask-rail"
          aria-expanded="false"
          :title="$t('graphRagAskExpand')"
          :aria-label="$t('graphRagAskExpand')"
          data-testid="canvas-ask-expand"
          @click="expandAsk"
        >
          <v-icon size="18" class="kgc-ask-rail-icon">mdi-chevron-double-left</v-icon>
          <v-progress-circular v-if="searching" indeterminate size="16" width="2" color="#C32E0E" />
          <span class="kgc-ask-rail-label">{{ $t('graphRagAskTitle') }}</span>
        </button>

        <template v-else>
          <div class="kgc-ask-head">
            <span class="kgc-ask-title">{{ $t('graphRagAskTitle') }}</span>
            <span class="kgc-spacer" />
            <button v-if="!searching && askOutcome === 'answered' && askAnswerText" type="button" class="kgc-ask-btn" data-testid="canvas-ask-copy" @click="copyAskAnswer">
              {{ $t('graphRagAskCopy') }}
            </button>
            <button
              ref="askCollapseBtn"
              type="button"
              class="kgc-ask-close kgc-ask-icon"
              aria-expanded="true"
              :title="$t('graphRagAskCollapse')"
              :aria-label="$t('graphRagAskCollapse')"
              data-testid="canvas-ask-collapse"
              @click="collapseAsk"
            >
              <v-icon size="18" class="kgc-ask-collapse-icon">mdi-chevron-double-right</v-icon>
            </button>
            <button type="button" class="kgc-ask-close" data-testid="canvas-ask-close" :title="$t('close')" :aria-label="$t('close')" @click="closeAsk">✕</button>
          </div>

          <!-- เนื้อหาเลื่อนในแผงเอง หัวแผง (คัดลอก / พับ / ปิด) ค้างอยู่บนสุดเสมอ -->
          <div class="kgc-ask-body">
            <div v-if="searching" class="kgc-ask-loading" data-testid="canvas-ask-loading">
              <v-progress-circular indeterminate size="18" width="2" color="#C32E0E" />
              <span>{{ $t('graphRagAskWorking') }}</span>
            </div>

            <div v-else-if="askError" class="kgc-ask-error" data-testid="canvas-ask-error">{{ askError }}</div>

            <template v-else-if="askResult">
              <!-- answered — เลขอ้างอิงที่ไม่มี source ตรงกันแสดงเป็นข้อความธรรมดา (ใน askSegments) -->
              <div v-if="askOutcome === 'answered'" class="kgc-ask-answer" data-testid="canvas-ask-answer">
                <template v-if="askAnswerText">
                  <template v-for="seg in askSegments" :key="seg.key">
                    <v-tooltip v-if="seg.type === 'cite'" location="top" max-width="360">
                      <template #activator="{ props }">
                        <button
                          v-bind="props"
                          type="button"
                          class="kgc-ask-cite"
                          :data-testid="`canvas-ask-cite-${seg.index}`"
                          :aria-label="$t('graphRagAskCiteLabel', { index: seg.index, label: seg.source.label || seg.source.nodeKey })"
                          @click="openAskSource(seg.source)"
                        >{{ seg.index }}</button>
                      </template>
                      <div class="kgc-ask-tip">
                        <div class="kgc-ask-tip-title">[{{ seg.index }}] {{ seg.source.label || seg.source.nodeKey }}</div>
                        <div class="kgc-ask-tip-meta">{{ seg.source.nodeType || 'Node' }}</div>
                        <div v-if="seg.source.excerpt" class="kgc-ask-tip-excerpt">{{ seg.source.excerpt }}</div>
                      </div>
                    </v-tooltip>
                    <span v-else>{{ seg.text }}</span>
                  </template>
                </template>
                <span v-else class="kgc-ask-muted">{{ $t('graphRagAskEmptyAnswer') }}</span>
              </div>

              <div v-else-if="askOutcome === 'no_context'" class="kgc-ask-notice" data-testid="canvas-ask-no-context">
                <div class="kgc-ask-notice-title">{{ $t('graphRagAskNoContext') }}</div>
                <div class="kgc-ask-muted">{{ $t('graphRagAskNoContextHint', { mode: $t('graphRagEffortThinking') }) }}</div>
              </div>

              <div v-else-if="askOutcome === 'no_model'" class="kgc-ask-notice" data-testid="canvas-ask-no-model">
                <!-- มี message = ตั้งไว้แต่บอทถูกลบ/ปิดใช้งาน (ไม่ใช่ยังไม่ได้ตั้ง) -->
                <div class="kgc-ask-notice-title">{{ askResult.message ? $t('graphRagAskModelGone') : $t('graphRagAskNoModel') }}</div>
                <template v-if="canEdit">
                  <div class="kgc-ask-muted">{{ $t('graphRagAskNoModelHint', { settings: $t('graphRagAskModel') }) }}</div>
                  <div>
                    <button type="button" class="kgc-btn" data-testid="canvas-ask-set-model" :disabled="!canEdit" @click="canEdit && focusAskModel()">
                      {{ $t('graphRagAskSetModel') }}
                    </button>
                  </div>
                </template>
                <div v-else class="kgc-ask-muted">{{ $t('graphRagAskNoModelAskOwner', { settings: $t('graphRagAskModel') }) }}</div>
              </div>

              <div v-else-if="askOutcome === 'llm_failed'" class="kgc-ask-error" data-testid="canvas-ask-llm-failed">
                <div>{{ $t('graphRagAskLlmFailed') }}</div>
                <div v-if="askResult.message" class="kgc-ask-error-detail">{{ askResult.message }}</div>
              </div>

              <!-- โหนดที่ใช้ตอบไม่ได้ถูกวาดบนผืนกราฟ (โหมด Auto ตัดไว้ ≤ 1,000 โหนด) — เดิมเป็น toast ของ runCanvasSearch -->
              <div v-if="askHitsNotDrawn" class="kgc-ask-warn" data-testid="canvas-ask-not-drawn">{{ $t('graphRagSearchHitNotLoaded', { count: askHitKeys.length }) }}</div>

              <div v-if="askSources.length && (askOutcome === 'answered' || askOutcome === 'llm_failed')" class="kgc-ask-sources" data-testid="canvas-ask-sources">
                <div class="kgc-ask-sources-head">
                  <span class="kgc-ask-subtitle">{{ $t('graphRagAskSources') }}</span>
                  <button v-if="canvasOn && askFocusKey" type="button" class="kgc-ask-link" data-testid="canvas-ask-show-all" @click="showAllAskHits">{{ $t('graphRagAskShowAll') }}</button>
                </div>
                <button
                  v-for="s in askSources"
                  :key="s.citationIndex"
                  type="button"
                  class="kgc-ask-source"
                  :class="{ active: canvasOn && askFocusKey === s.nodeKey }"
                  :data-testid="`canvas-ask-source-${s.citationIndex}`"
                  @click="openAskSource(s)"
                >
                  <span class="kgc-ask-source-no">[{{ s.citationIndex }}]</span>
                  <span class="kgc-ask-source-label">{{ s.label || s.nodeKey }}</span>
                  <span class="kgc-ask-muted">· {{ s.nodeType || 'Node' }}</span>
                </button>
              </div>

              <div v-if="askMetaItems.length" class="kgc-ask-meta" data-testid="canvas-ask-meta">
                <span v-for="(m, i) in askMetaItems" :key="i" class="kgc-ask-meta-item">{{ m }}</span>
              </div>
            </template>
          </div>
        </template>
      </aside>
    </div>

    <!-- ── ⚙ Manage — รวมเครื่องมือระดับกราฟ (เดิมเป็นแท็บ) ────── -->
    <v-dialog v-model="showManage" max-width="640">
      <v-card>
        <v-card-title class="d-flex align-center" style="gap: 8px">
          <v-icon color="#C32E0E">mdi-cog</v-icon>
          {{ $t('manage') }} — {{ graph.name || graphKey }}
        </v-card-title>
        <v-list density="comfortable">
          <!-- โหนด / เส้นเชื่อม — เดิมมีแค่ในแผง Graph Commands (ต้องปิด Canvas View ก่อนถึงจะเห็น) ⇒ เปิดจาก ⚙ Manage ได้ทั้งสองโหมด -->
          <v-list-item :disabled="!canEdit" data-testid="manage-add-node" @click="canEdit && openTool('addNode')">
            <template #prepend><v-icon>mdi-plus-circle-outline</v-icon></template>
            <v-list-item-title>{{ $t('graphRagAddNode') }}</v-list-item-title>
          </v-list-item>
          <v-list-item data-testid="manage-nodes" @click="openTool('nodes')">
            <template #prepend><v-icon>mdi-format-list-bulleted</v-icon></template>
            <v-list-item-title>{{ $t('graphRagManageNodes') }}</v-list-item-title>
          </v-list-item>
          <v-list-item data-testid="manage-edges" @click="openTool('edges')">
            <template #prepend><v-icon>mdi-vector-polyline</v-icon></template>
            <v-list-item-title>{{ $t('graphRagManageEdges') }}</v-list-item-title>
          </v-list-item>
          <v-divider class="my-1" />
          <v-list-item data-testid="manage-ontology" @click="openTool('ontology')">
            <template #prepend><v-icon>mdi-shape-outline</v-icon></template>
            <v-list-item-title>{{ $t('graphRagOntology') }}</v-list-item-title>
          </v-list-item>
          <!-- Filter Sets ซ่อนไว้ก่อน — ดู CONST_FILTER_SETS_ENABLED -->
          <v-list-item v-if="filtersEnabled" data-testid="manage-filters" @click="openTool('filters')">
            <template #prepend><v-icon>mdi-filter-variant</v-icon></template>
            <v-list-item-title>{{ $t('graphRagFilters') }}</v-list-item-title>
          </v-list-item>
          <v-list-item data-testid="manage-settings" @click="openTool('settings')">
            <template #prepend><v-icon>mdi-tune</v-icon></template>
            <v-list-item-title>{{ $t('graphRagSettings') }}</v-list-item-title>
          </v-list-item>
          <v-list-item data-testid="manage-versions" @click="openTool('versions')">
            <template #prepend><v-icon>mdi-history</v-icon></template>
            <v-list-item-title>{{ $t('graphRagVersionHistory') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="!canEdit" data-testid="manage-snapshot" @click="canEdit && openFromManage('showSnapshot')">
            <template #prepend><v-icon>mdi-content-save-outline</v-icon></template>
            <v-list-item-title>{{ $t('graphRagSnapshot') }}</v-list-item-title>
          </v-list-item>
          <!-- ดาวน์โหลดอย่างเดียว ไม่มีหน้าต่างให้กลับมา ⇒ ค้างเมนูไว้ให้ทำอย่างอื่นต่อ -->
          <v-list-item data-testid="manage-export" @click="exportGraph()">
            <template #prepend><v-icon>mdi-download-outline</v-icon></template>
            <v-list-item-title>{{ $t('graphRagExport') }}</v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="!canEdit" data-testid="manage-import" @click="canEdit && openFromManage('showImport')">
            <template #prepend><v-icon>mdi-upload-outline</v-icon></template>
            <v-list-item-title>{{ $t('graphRagImport') }}</v-list-item-title>
          </v-list-item>
          <!-- Graph Access ถูกย้ายไปตั้งที่หน้า Portal User (ช่อง Knowledge Graph) เมื่อ 2026-10-01 -->
        </v-list>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="manage-close" @click="showManage = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── จัดการโหนด ──────────────────────────────────────────── -->
    <v-dialog v-model="showNodeManager" max-width="1100" scrollable>
      <v-card>
        <v-card-title>{{ $t('graphRagManageNodes') }}</v-card-title>
        <v-card-text>
          <v-alert v-if="!canEdit" type="info" color="#C32E0E" variant="tonal" density="compact" class="mb-3">{{ $t('graphRagReadOnly') }}</v-alert>
          <div class="d-flex justify-space-between align-center mb-3" style="gap: 12px">
            <v-text-field
              v-model="nodeSearch"
              data-testid="node-search"
              :placeholder="$t('graphRagLabel')"
              hide-details
              clearable
              density="compact"
              variant="outlined"
              style="max-width: 320px"
              @input="onNodeSearch"
              @click:clear="onNodeSearch"
            />
            <div class="d-flex" style="gap: 8px">
              <v-btn
                v-if="selectedNodeKeys.length"
                color="error"
                variant="outlined"
                size="small"
                data-testid="node-delete-selected"
                :disabled="!canEdit"
                @click="canEdit && deleteSelectedNodes()"
              >
                {{ $t('delete') }} ({{ selectedNodeKeys.length }})
              </v-btn>
              <v-btn color="#C32E0E" size="small" data-testid="node-add" :disabled="!canEdit" @click="canEdit && openNode(null)">{{ $t('graphRagAddNode') }}</v-btn>
            </div>
          </div>

          <v-data-table-server
            v-model="selectedNodeKeys"
            v-model:options="nodeOptions"
            show-select
            item-value="nodeKey"
            :items-per-page="nodeOptions.itemsPerPage"
            :items-length="nodeTotal"
            :items="nodes"
            :headers="nodeHeaders"
            :loading="loadingNodes"
            @update:options="loadNodes"
          >
            <!-- node key ย้ายมาเป็นบรรทัดรองใต้ชื่อ — key ภาษาไทยยาวมากจนดันคอลัมน์ Manage ตกขอบตาราง -->
            <template #[`item.label`]="{ item }">
              <div class="ge-node-cell">
                <div>{{ item.label || item.nodeKey }}</div>
                <div class="ge-node-key" :title="item.nodeKey">{{ item.nodeKey }}</div>
              </div>
            </template>
            <!-- เปลี่ยนชนิดในแถวได้ทันที (บันทึกทันที) — เปลี่ยนแค่โหนดนี้ ลูกหลานไม่เลื่อนตาม (ต้องการให้เลื่อนตาม = ใช้ "ย้าย…") -->
            <template #[`item.nodeType`]="{ item }">
              <v-select
                v-if="canEdit"
                :model-value="item.nodeType"
                :items="nodeTypeOptionsFor(item.nodeType)"
                :loading="!!nodeTypeSaving[item.nodeKey]"
                :disabled="!!nodeTypeSaving[item.nodeKey]"
                density="compact"
                variant="outlined"
                hide-details
                class="ge-node-type-select"
                :data-testid="`node-type-${item.nodeKey}`"
                @update:model-value="changeNodeType(item, $event)"
              />
              <span v-else>{{ item.nodeType }}</span>
            </template>
            <template #[`item.parent`]="{ item }">
              <div class="ge-node-parent" :data-testid="`node-parent-${item.nodeKey}`">
                <span v-if="item.parentNodeKey" class="ge-node-parent-name" :title="item.parentNodeKey">
                  {{ item.parentLabel || item.parentNodeKey }}<span v-if="item.parentCount > 1" class="text-medium-emphasis"> +{{ item.parentCount - 1 }}</span>
                </span>
                <span v-else class="text-medium-emphasis">{{ $t('graphRagNoParent') }}</span>
                <!-- ระดับไม่ต่อกับแม่ (เช่น Heading3 ใต้ Heading1) — เตือนเฉย ๆ เพราะเอกสารจริงข้ามระดับได้ · กดเพื่อเปิดกล่องแก้โหนด -->
                <v-icon
                  v-if="rowLevelMismatch(item)"
                  icon="mdi-alert-outline"
                  color="#D98E04"
                  size="small"
                  class="ge-node-level-warn"
                  :title="$t('graphRagLevelMismatchTip', { type: item.nodeType, parentType: item.parentNodeType || '—' })"
                  :aria-label="$t('graphRagLevelMismatchTip', { type: item.nodeType, parentType: item.parentNodeType || '—' })"
                  :data-testid="`node-level-warn-${item.nodeKey}`"
                  @click="canEdit && openNode(item)"
                />
                <v-btn
                  v-if="canEdit"
                  size="x-small"
                  variant="tonal"
                  color="#C32E0E"
                  :data-testid="`node-move-${item.nodeKey}`"
                  @click="openMoveNode(item)"
                >{{ $t('graphRagMoveNodeShort') }}</v-btn>
              </div>
            </template>
            <template #[`item.aliases`]="{ item }">
              <v-chip v-for="a in item.aliases" :key="a" size="x-small" class="mr-1">{{ a }}</v-chip>
            </template>
            <template #[`item.manage`]="{ item }">
              <v-btn size="small" variant="text" :data-testid="`node-edit-${item.nodeKey}`" :disabled="!canEdit" @click="canEdit && openNode(item)">{{ $t('edit') }}</v-btn>
            </template>
          </v-data-table-server>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="node-manager-close" @click="showNodeManager = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── จัดการเส้นเชื่อม ────────────────────────────────────── -->
    <v-dialog v-model="showEdgeManager" max-width="1000" scrollable>
      <v-card>
        <v-card-title>{{ $t('graphRagManageEdges') }}</v-card-title>
        <v-card-text>
          <div class="d-flex justify-end mb-3">
            <v-btn color="#C32E0E" size="small" data-testid="edge-add" :disabled="!canEdit" @click="canEdit && openEdge(null)">{{ $t('graphRagAddEdge') }}</v-btn>
          </div>
          <v-data-table :items="edges" :headers="edgeHeaders" :loading="loadingEdges">
            <template #[`item.source`]="{ item }">{{ item.sourceLabel || item.sourceNodeKey }}</template>
            <template #[`item.target`]="{ item }">{{ item.targetLabel || item.targetNodeKey }}</template>
            <template #[`item.note`]="{ item }">{{ edgeNoteOf(item) }}</template>
            <template #[`item.manage`]="{ item }">
              <v-btn size="small" variant="text" :data-testid="`edge-edit-${item.id}`" :disabled="!canEdit" @click="canEdit && openEdge(item)">{{ $t('edit') }}</v-btn>
              <v-btn size="small" variant="text" color="error" :data-testid="`edge-delete-${item.id}`" :disabled="!canEdit" @click="canEdit && deleteEdge(item)">{{ $t('delete') }}</v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="edge-manager-close" @click="showEdgeManager = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── ontology ────────────────────────────────────────────── -->
    <v-dialog v-model="showOntology" max-width="900" scrollable>
      <v-card>
        <v-card-title>{{ $t('graphRagOntology') }}</v-card-title>
        <v-card-text>
          <div class="d-flex justify-end mb-3">
            <v-btn color="#C32E0E" size="small" data-testid="schema-add" :disabled="!canEdit" @click="canEdit && openSchema(null)">{{ $t('add') }}</v-btn>
          </div>
          <v-data-table :items="schemaTypes" :headers="schemaHeaders" :loading="loadingSchema">
            <template #[`item.color`]="{ item }">
              <span class="colour-dot" :style="{ background: item.color }" />
              {{ item.color }}
            </template>
            <template #[`item.details`]="{ item }">
              <span class="text-caption">{{ schemaDetailsText(item) }}</span>
            </template>
            <template #[`item.manage`]="{ item }">
              <v-btn size="small" variant="text" :data-testid="`schema-edit-${item.typeId}`" :disabled="!canEdit" @click="canEdit && openSchema(item)">{{ $t('edit') }}</v-btn>
              <v-btn size="small" variant="text" :data-testid="`schema-delete-${item.typeId}`" color="error" :disabled="!canEdit" @click="canEdit && deleteSchema(item)">{{ $t('delete') }}</v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="ontology-close" @click="showOntology = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── ชุดตัวกรอง ──────────────────────────────────────────── -->
    <v-dialog v-model="showFilters" max-width="900" scrollable>
      <v-card>
        <v-card-title>{{ $t('graphRagFilters') }}</v-card-title>
        <v-card-text>
          <div class="d-flex justify-end mb-3">
            <v-btn color="#C32E0E" size="small" data-testid="filter-add" :disabled="!canEdit" @click="canEdit && openFilter(null)">{{ $t('add') }}</v-btn>
          </div>
          <v-data-table :items="filters" :headers="filterHeaders" :loading="loadingFilters">
            <template #[`item.isActive`]="{ item }">
              <v-chip v-if="item.isActive" size="small" color="#C32E0E">{{ $t('graphRagActive') }}</v-chip>
            </template>
            <template #[`item.manage`]="{ item }">
              <v-btn size="small" variant="text" :data-testid="`filter-edit-${item.id}`" :disabled="!canEdit" @click="canEdit && openFilter(item)">{{ $t('edit') }}</v-btn>
              <v-btn
                size="small"
                variant="text"
                :data-testid="`filter-activate-${item.id}`"
                :disabled="!canEdit || item.isActive"
                @click="canEdit && !item.isActive && activateFilter(item)"
              >
                {{ $t('graphRagActivate') }}
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="filters-close" @click="showFilters = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── ค่าการค้นหา ─────────────────────────────────────────── -->
    <v-dialog v-model="showSettings" max-width="1000" scrollable>
      <v-card>
        <v-card-title>{{ $t('graphRagSettings') }}</v-card-title>
        <v-card-text>
          <v-alert type="info" color="#C32E0E" variant="tonal" density="compact" class="mb-4">{{ $t('graphRagSettingsHint') }}</v-alert>
          <v-row>
            <v-col cols="12" md="4">
              <v-select v-model="config.search_mode" data-testid="config-search-mode" :items="searchModes" :label="$t('graphRagSearchMode')" density="comfortable" :disabled="!canEdit" />
            </v-col>
            <v-col v-for="field in numberFields" :key="field.key" cols="12" md="4">
              <v-text-field
                v-model.number="config[field.key]"
                :data-testid="`config-${field.key}`"
                type="number"
                :label="$t(field.label)"
                :step="field.step || 1"
                density="comfortable"
                :disabled="!canEdit"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="config.parent_context_mode"
                data-testid="config-parent-context-mode"
                :items="parentContextModes"
                :label="$t('graphRagParentContextMode')"
                density="comfortable"
                :disabled="!canEdit"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-switch v-model="config.tie_aware_cutoff_enabled" data-testid="config-tie-cutoff" :label="$t('graphRagTieCutoff')" color="#C32E0E" :disabled="!canEdit" />
            </v-col>
            <v-col cols="12" md="4">
              <v-switch v-model="config.reranker_enabled" data-testid="config-reranker" :label="$t('graphRagReranker')" color="#C32E0E" :disabled="!canEdit" />
            </v-col>
            <v-col cols="12" md="4">
              <v-switch v-model="config.graph_canvas_render_enabled" data-testid="config-canvas" :label="$t('graphRagCanvasRender')" color="#C32E0E" :disabled="!canEdit" />
            </v-col>
            <!-- โมเดลที่ใช้ตอบคำถามย้ายไปอยู่บนแถบกราฟข้างปุ่ม Revert (canvas-ask-model) -->
          </v-row>

          <!-- สถานะเวกเตอร์ — ย้ายมาจากกล่องนำเข้า เพราะเป็นสถานะระดับกราฟ ไม่ใช่ขั้นตอนอัปโหลด -->
          <v-divider class="my-4" />
          <div class="d-flex align-center mb-2" style="gap: 8px">
            <v-icon size="small" color="#C32E0E">mdi-vector-polyline</v-icon>
            <strong style="font-size: 15px">{{ $t('graphRagEmbedding') }}</strong>
          </div>

          <div v-if="embedding" data-testid="settings-embedding">
            <!-- ต้องบอกว่ายังไม่พร้อมและทำไม ไม่ใช่โชว์ 0/0 ที่ดูเหมือนไม่มีอะไรต้องทำ -->
            <v-alert v-if="!embedding.vectorStoreReady" type="warning" variant="tonal" density="compact" class="mb-3" data-testid="settings-vector-not-ready">
              {{ $t('graphRagVectorStoreNotReady') }}
            </v-alert>

            <div style="font-size: 13px">
              {{ $t('graphRagEmbeddedCount') }} {{ embedding.embeddedCount }} ·
              {{ $t('graphRagMissingCount') }} {{ embedding.missingCount }} / {{ embedding.totalNodes }}
            </div>
            <div style="font-size: 12px; color: #646464">{{ embedding.embedProfile }} · {{ embedding.dimensions }} dim</div>

            <div class="d-flex justify-end mt-3">
              <v-btn
                size="small"
                color="#C32E0E"
                data-testid="ingest-start-embedding"
                :disabled="!canEdit || !embedding.vectorStoreReady || embedding.missingCount === 0"
                @click="canEdit && embedding.vectorStoreReady && embedding.missingCount !== 0 && startEmbedding()"
              >
                {{ $t('graphRagStartEmbedding') }}
              </v-btn>
            </div>
          </div>
          <div v-else style="color: #646464" data-testid="settings-embedding-empty">{{ $t('noDataAvailable') }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="settings-close" @click="showSettings = false">{{ $t('close') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="config-save" :loading="savingConfig" :disabled="!canEdit" @click="canEdit && saveConfig()">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── ประวัติเวอร์ชัน ─────────────────────────────────────── -->
    <v-dialog v-model="showVersions" max-width="1000" scrollable>
      <v-card>
        <v-card-title>{{ $t('graphRagVersionHistory') }}</v-card-title>
        <v-card-text>
          <v-data-table :items="versions" :headers="versionHeaders" :loading="loadingVersions">
            <template #[`item.versionNo`]="{ item }">v{{ item.versionNo }}</template>
            <template #[`item.manage`]="{ item }">
              <v-tooltip location="top" :disabled="item.hasArchive">
                <template #activator="{ props }">
                  <span v-bind="props">
                    <v-btn
                      size="small"
                      variant="text"
                      :data-testid="`version-revert-${item.versionNo}`"
                      :disabled="!canEdit || !item.hasArchive"
                      @click="canEdit && item.hasArchive && revertTo(item)"
                    >
                      {{ $t('graphRagRevert') }}
                    </v-btn>
                  </span>
                </template>
                <span>{{ $t('graphRagNoArchive') }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="versions-close" @click="showVersions = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── สร้างกราฟจากเอกสาร (Create graph from...) ───────────── -->
    <!-- state ของคิว/ความคืบหน้า/timer อยู่ในหน้านี้ (มติ D10) เพราะ Vuetify unmount เนื้อใน dialog ตอนปิด -->
    <v-dialog :model-value="showIngest" max-width="1200" scrollable :fullscreen="$vuetify.display.smAndDown" @update:model-value="onIngestDialogToggle">
      <v-card>
        <v-toolbar density="comfortable" flat>
          <v-toolbar-title>{{ ingestParentNodeKey ? $t('graphRagImportUnderNodeTitle') : $t('graphRagCreateGraphFrom') }}</v-toolbar-title>
          <v-spacer />
          <v-tooltip :text="$t('graphRagWizMinimize')">
            <template #activator="{ props }">
              <v-btn v-if="wizBusy" v-bind="props" icon="mdi-window-minimize" variant="text" :aria-label="$t('graphRagWizMinimize')" data-testid="wiz-minimize" @click="wizMinimize" />
            </template>
          </v-tooltip>
          <v-tooltip :text="$t('close')">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="mdi-close" variant="text" :aria-label="$t('close')" data-testid="wiz-close" @click="wizRequestClose" />
            </template>
          </v-tooltip>
        </v-toolbar>

        <ImportWizard
          :state="wiz"
          :graph-key="graphKey"
          :can-edit="canEdit"
          :max-files="wizMaxFiles"
          @patch="wizPatch"
          @switch-mode="wizSwitchMode"
          @add-files="wizAddFiles"
          @remove-file="wizRemoveFile"
          @retry-file="wizRetryFile"
          @clear-queue="wizClearQueue"
          @create="wizCreate"
          @cancel-job="wizCancelJob"
          @close="wizRequestClose"
          @applied="wizOnApplied"
          @notify="onChildNotify"
        />
      </v-card>
    </v-dialog>

    <!-- ยืนยันก่อนปิดกล่องนำเข้ากลางคัน — อยู่นอก dialog ตัวนอกเพื่อให้ถามได้ก่อนปิดจริง -->
    <v-dialog v-model="wizConfirm" max-width="440" data-testid="wiz-confirm-close">
      <v-card>
        <v-card-title style="font-size: 16px">{{ $t('graphRagWizConfirmCloseTitle') }}</v-card-title>
        <v-card-text style="font-size: 13px">{{ wizConfirmText }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="wiz-confirm-keep" @click="wizConfirm = false">{{ $t('graphRagWizKeepWorking') }}</v-btn>
          <v-btn v-if="wizBusy" variant="text" data-testid="wiz-confirm-minimize" @click="wizConfirmMinimize">{{ $t('graphRagWizMinimizeInstead') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="wiz-confirm-discard" @click="wizConfirmDiscard">{{ $t('graphRagWizDiscardClose') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ย่อหน้าต่างแล้วงานยังเดินต่อ — badge ต้องอยู่นอก dialog ถึงจะไม่ถูก unmount -->
    <Teleport to="body">
      <v-card v-if="wizShowBadge" elevation="8" class="wiz-mini" data-testid="wiz-mini-badge">
        <div class="wiz-mini-text">{{ wizMiniText }}</div>
        <v-btn size="small" variant="text" color="#C32E0E" data-testid="wiz-mini-restore" @click="showIngest = true">{{ $t('graphRagWizRestore') }}</v-btn>
      </v-card>
    </Teleport>

    <!-- โหนด -->
    <v-dialog v-model="showNode" max-width="640" persistent>
      <v-card>
        <v-card-title>{{ nodeForm.nodeKey ? $t('edit') : $t('graphRagAddNode') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="nodeForm.label" data-testid="node-form-label" :label="$t('graphRagLabel')" density="comfortable" />
          <v-text-field v-model="nodeForm.secondaryLabel" data-testid="node-form-secondary" :label="$t('graphRagSecondaryLabel')" density="comfortable" />
          <!-- ✏️ 2026-10-20 เลือกจากรายการ (เดิมพิมพ์เอง) — Product · Heading1–6 · ชนิดใน Ontology · GraphNode · ค่าเดิมของโหนด -->
          <v-select v-model="nodeForm.nodeType" :items="nodeTypeItems" data-testid="node-form-type" :label="$t('graphRagNodeType')" density="comfortable" />
          <!-- โครงสร้าง (เฉพาะโหนดที่มีอยู่แล้ว): โหนดแม่ + คำแนะนำเมื่อระดับ Heading ไม่ต่อกับแม่ + เลื่อนลูกหลานตาม
               เปลี่ยนแม่ / เลื่อนลูกหลาน = บันทึกผ่าน MoveNode (เส้น · ชนิด · lineage ในครั้งเดียว + บันทึกเวอร์ชันก่อน)
               เปลี่ยนชนิดอย่างเดียว = บันทึกแบบเดิม — ไม่ย้ายให้เองเพราะโหนดแม่ที่เป็นไปได้มีหลายตัว -->
          <div v-if="nodeForm.nodeKey" class="ge-node-hier mb-4" data-testid="node-form-hierarchy">
            <!-- พิมพ์ค้นโหนดใดก็ได้ หรือเลือกจาก dropdown — ตัวที่ระดับพอดีกับชนิดที่เลือกขึ้นก่อนพร้อมป้าย "แนะนำ"
                 เปลี่ยน Heading แล้วระดับไม่ต่อกับแม่ ⇒ dropdown เปิดให้เอง (watch nodeForm.nodeType) -->
            <v-autocomplete
              ref="nodeParentSelect"
              v-model="nodeForm.parentKey"
              v-model:search="nodeParentSearch"
              v-model:menu="nodeParentMenu"
              :items="nodeParentItems"
              :loading="nodeHierarchyLoading || nodeParentLoading"
              item-title="label"
              item-value="nodeKey"
              no-filter
              :label="$t('graphRagParentNode')"
              :placeholder="$t('graphRagTypeToSearchNode')"
              :no-data-text="nodeParentSearch ? $t('graphRagNodeNotFound') : $t('graphRagTypeToSearchNode')"
              :hint="nodeParentHint"
              persistent-hint
              density="comfortable"
              :menu-props="nodePickerMenuProps"
              data-testid="node-form-parent"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item
                  v-bind="itemProps"
                  :title="item.raw.label || item.raw.nodeKey"
                  :subtitle="`${item.raw.nodeType || 'Node'} · ${item.raw.nodeKey}`"
                  :data-testid="item.raw.suggested ? `node-form-suggest-${item.raw.nodeKey}` : undefined"
                >
                  <template v-if="item.raw.suggested" #append>
                    <v-chip size="x-small" color="#D98E04" variant="tonal">{{ $t('graphRagSuggested') }}</v-chip>
                  </template>
                </v-list-item>
              </template>
              <!-- ซ่อนชื่อที่เลือกไว้ระหว่างพิมพ์คำค้นใหม่ — เหตุผลเดียวกับช่องค้นโหนดในแผง Graph Commands -->
              <template #selection="{ item }">
                <span v-if="!nodeParentSearch" class="gcc-node-picker-selection">{{ item.raw.label || item.raw.nodeKey }}</span>
              </template>
            </v-autocomplete>

            <v-alert v-if="nodeLevelAdvice" type="warning" variant="tonal" density="compact" class="mt-2" data-testid="node-form-level-advice">
              <div>{{ nodeLevelAdvice }}</div>
              <div v-if="nodeParentSuggestions.length" class="text-caption mt-1">
                {{ $t('graphRagPickSuggestedHint', { count: nodeParentSuggestions.length }) }}
                <a href="#" class="ge-node-open-parent" data-testid="node-form-open-suggestions" @click.prevent="openNodeParentMenu">{{ $t('graphRagShowSuggestions') }}</a>
              </div>
            </v-alert>

            <v-checkbox
              v-if="showShiftDescendants"
              v-model="nodeForm.shiftDescendants"
              :label="$t('graphRagShiftDescendants', { count: nodeHierarchy.descendantCount })"
              density="compact"
              hide-details
              class="mt-1"
              data-testid="node-form-shift"
            />
            <div v-if="nodeNeedsMove" class="text-caption text-medium-emphasis mt-1" data-testid="node-form-version-note">{{ $t('graphRagHierarchyVersionNote') }}</div>
          </div>
          <v-textarea v-model="nodeForm.description" data-testid="node-form-description" :label="$t('description')" rows="2" density="comfortable" />
          <v-text-field v-model="nodeForm.aliasText" data-testid="node-form-aliases" :label="$t('graphRagAliases')" :hint="$t('graphRagAliasesHint')" persistent-hint density="comfortable" />
          <v-textarea v-model="nodeForm.attributesJson" data-testid="node-form-attributes" :label="$t('graphRagAttributes')" rows="3" density="comfortable" class="mt-4" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="node-form-cancel" @click="showNode = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="node-form-save" :loading="saving" @click="saveNode">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
      เส้นเชื่อม — ✏️ 2026-10-20 ทำให้ใกล้ Edge Manager (Create Custom Edge):
      ค้นแล้วเลือกโหนดแทนพิมพ์ key · ⇄ สลับต้นทาง/ปลายทาง · relation จัดลำดับตามกติกา From/To ของ Ontology
      · Note (attributesJson.note) · สร้างเส้นย้อนกลับด้วย (bidirectional) · ตารางอ้างอิงชนิดเส้น (Edge Type Reference)
      relation นอกกติกาแค่เตือน ไม่บล็อก — กราฟส่วนใหญ่ยังไม่มีชนิด relation ใน Ontology ถ้าบล็อกจะสร้างเส้นไม่ได้เลย
    -->
    <v-dialog v-model="showEdge" max-width="760" persistent>
      <v-card>
        <v-card-title>{{ edgeForm.id ? $t('edit') : $t('graphRagAddEdge') }}</v-card-title>
        <v-card-text>
          <div class="ge-edge-ends">
            <v-autocomplete
              v-model="edgeForm.sourceNodeKey"
              v-model:search="edgeSourceSearch"
              :items="edgeSourceOptions"
              :loading="edgeSourceLoading"
              item-title="label"
              item-value="nodeKey"
              no-filter
              :label="$t('graphRagSource')"
              :placeholder="$t('graphRagTypeToSearchNode')"
              :no-data-text="edgeSourceSearch ? $t('graphRagNodeNotFound') : $t('graphRagTypeToSearchNode')"
              :hint="edgeEndHint(edgeSourceNode)"
              persistent-hint
              density="comfortable"
              class="ge-edge-end"
              data-testid="edge-form-source"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :title="item.raw.label || item.raw.nodeKey" :subtitle="`${item.raw.nodeType || 'Node'} · ${item.raw.nodeKey}`" />
              </template>
              <!-- ซ่อนชื่อที่เลือกไว้ระหว่างพิมพ์คำค้นใหม่ — เหตุผลเดียวกับช่องค้นโหนดในแผง Graph Commands -->
              <template #selection="{ item }">
                <span v-if="!edgeSourceSearch" class="gcc-node-picker-selection">{{ item.raw.label || item.raw.nodeKey }}</span>
              </template>
            </v-autocomplete>

            <v-btn icon="mdi-swap-horizontal" variant="text" class="ge-edge-swap" :title="$t('graphRagEdgeSwap')" :aria-label="$t('graphRagEdgeSwap')" data-testid="edge-form-swap" @click="swapEdgeEnds" />

            <v-autocomplete
              v-model="edgeForm.targetNodeKey"
              v-model:search="edgeTargetSearch"
              :items="edgeTargetOptions"
              :loading="edgeTargetLoading"
              item-title="label"
              item-value="nodeKey"
              no-filter
              :label="$t('graphRagTarget')"
              :placeholder="$t('graphRagTypeToSearchNode')"
              :no-data-text="edgeTargetSearch ? $t('graphRagNodeNotFound') : $t('graphRagTypeToSearchNode')"
              :hint="edgeEndHint(edgeTargetNode)"
              persistent-hint
              density="comfortable"
              class="ge-edge-end"
              data-testid="edge-form-target"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :title="item.raw.label || item.raw.nodeKey" :subtitle="`${item.raw.nodeType || 'Node'} · ${item.raw.nodeKey}`" />
              </template>
              <template #selection="{ item }">
                <span v-if="!edgeTargetSearch" class="gcc-node-picker-selection">{{ item.raw.label || item.raw.nodeKey }}</span>
              </template>
            </v-autocomplete>
          </div>

          <!-- เลือกจากรายการหรือพิมพ์ relation ใหม่ก็ได้ · รายการ: ตรงกติกา From/To ก่อน → ชนิดอื่นใน Ontology → ที่กราฟนี้ใช้อยู่ -->
          <v-combobox
            v-model="edgeForm.relation"
            :items="edgeRelationItems"
            item-title="title"
            item-value="value"
            :return-object="false"
            :label="$t('graphRagRelation')"
            :hint="edgeRelationHint"
            persistent-hint
            density="comfortable"
            class="mt-3"
            data-testid="edge-form-relation"
          >
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
            </template>
          </v-combobox>

          <v-text-field v-model="edgeForm.note" :label="$t('graphRagEdgeNote')" density="comfortable" class="mt-3" data-testid="edge-form-note" />

          <!-- สร้างใหม่เท่านั้น — แก้เส้นเดิมไม่สร้างเส้นที่สอง · ค่าเริ่มต้นติ๊กเมื่อ relation = suggests  -->
          <v-checkbox
            v-if="!edgeForm.id"
            :model-value="edgeForm.bidirectional"
            :label="$t('graphRagEdgeBidirectional')"
            :hint="$t('graphRagEdgeBidirectionalHint')"
            persistent-hint
            color="#C32E0E"
            density="compact"
            data-testid="edge-form-bidirectional"
            @update:model-value="edgeForm.bidirectional = !!$event; edgeForm.bidirTouched = true"
          />

          <v-expansion-panels variant="accordion" class="mt-4">
            <v-expansion-panel data-testid="edge-type-reference">
              <v-expansion-panel-title>{{ $t('graphRagEdgeTypeReference') }} ({{ edgeTypeReference.length }})</v-expansion-panel-title>
              <v-expansion-panel-text>
                <div v-if="!edgeTypeReference.length" class="text-caption text-medium-emphasis">{{ $t('graphRagEdgeTypeReferenceEmpty') }}</div>
                <v-table v-else density="compact" class="ge-edge-ref">
                  <thead>
                    <tr>
                      <th>{{ $t('graphRagRelGroup') }}</th>
                      <th>{{ $t('graphRagRelation') }}</th>
                      <th>{{ $t('graphRagEdgeDirection') }}</th>
                      <th>{{ $t('graphRagRelCost') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- คลิกแถว = ใช้ relation นี้ -->
                    <tr v-for="r in edgeTypeReference" :key="r.typeId" :class="{ 'ge-edge-ref-fit': r.fits }" :data-testid="`edge-ref-${r.typeId}`" @click="edgeForm.relation = r.typeId">
                      <td>{{ r.group }}</td>
                      <td>{{ r.title }}</td>
                      <td>{{ r.direction }}</td>
                      <td>{{ r.cost }}</td>
                    </tr>
                  </tbody>
                </v-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="edge-form-cancel" @click="showEdge = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="edge-form-save" :loading="saving" @click="saveEdge">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ontology -->
    <v-dialog v-model="showSchema" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagOntology') }}</v-card-title>
        <v-card-text>
          <v-select v-model="schemaForm.typeKind" data-testid="schema-form-kind" :items="['node', 'relation']" :label="$t('graphRagTypeKind')" density="comfortable" :disabled="schemaForm.locked" />
          <v-text-field v-model="schemaForm.typeId" data-testid="schema-form-type-id" :label="$t('graphRagTypeId')" density="comfortable" :disabled="schemaForm.locked" />
          <v-text-field v-model="schemaForm.label" data-testid="schema-form-label" :label="$t('graphRagLabel')" density="comfortable" />
          <!-- ✏️ 2026-10-20 ช่องเสริมแบบ schema editor   — เก็บใน attributeSchemaJson  -->
          <v-text-field v-model="schemaForm.description" data-testid="schema-form-description" :label="$t('description')" density="comfortable" />
          <template v-if="schemaForm.typeKind === 'relation'">
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-combobox v-model="schemaForm.group" :items="relationGroupItems" :label="$t('graphRagRelGroup')" :hint="$t('graphRagRelGroupHint')" persistent-hint density="comfortable" data-testid="schema-form-group" />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field v-model="schemaForm.cost" type="number" min="0" step="1" :label="$t('graphRagRelCost')" :hint="$t('graphRagRelCostHint')" persistent-hint :rules="[schemaCostRule]" density="comfortable" data-testid="schema-form-cost" />
              </v-col>
            </v-row>
            <v-autocomplete v-model="schemaForm.fromTypes" :items="schemaNodeTypeIds" multiple chips closable-chips :label="$t('graphRagRelFromTypes')" density="comfortable" class="mt-3" data-testid="schema-form-from" />
            <v-autocomplete v-model="schemaForm.toTypes" :items="schemaNodeTypeIds" multiple chips closable-chips :label="$t('graphRagRelToTypes')" :hint="$t('graphRagRelFromToHint')" persistent-hint density="comfortable" data-testid="schema-form-to" />
          </template>
          <v-text-field v-model="schemaForm.color" data-testid="schema-form-color" :label="$t('graphRagColor')" density="comfortable" class="mt-2" />
          <v-text-field v-model.number="schemaForm.sortOrder" data-testid="schema-form-sort" type="number" :label="$t('graphRagSortOrder')" density="comfortable" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="schema-form-cancel" @click="showSchema = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="schema-form-save" :loading="saving" @click="saveSchema">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ชุดตัวกรอง -->
    <v-dialog v-model="showFilter" max-width="620" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagFilters') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="filterForm.name" data-testid="filter-form-name" :label="$t('graphRagFilterName')" density="comfortable" />
          <v-textarea v-model="filterForm.filterSpecJson" data-testid="filter-form-spec" :label="$t('graphRagFilterSpec')" rows="6" density="comfortable" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="filter-form-cancel" @click="showFilter = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="filter-form-save" :loading="saving" @click="saveFilter">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- นำเข้า -->
    <v-dialog v-model="showImport" max-width="620" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagImport') }}</v-card-title>
        <v-card-text>
          <v-file-input v-model="importFile" data-testid="import-file" accept="application/json,.json" :label="$t('graphRagImport')" density="comfortable" />
          <v-radio-group v-model="importMode" data-testid="import-mode" inline>
            <v-radio :label="$t('graphRagImportReplace')" value="replace" />
            <v-radio :label="$t('graphRagImportMerge')" value="merge" />
          </v-radio-group>
          <v-alert type="warning" variant="tonal" density="compact">
            {{ importMode === 'merge' ? $t('graphRagImportMergeHint') : $t('graphRagImportReplaceHint') }}
          </v-alert>
          <div v-if="importWarnings.length" class="mt-4">
            <strong>{{ $t('graphRagImportWarnings') }}</strong>
            <ul>
              <li v-for="(w, i) in importWarnings" :key="i">{{ w }}</li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="import-cancel" @click="showImport = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="import-confirm" :loading="importing" @click="importGraph">{{ $t('graphRagImport') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- บันทึกเวอร์ชัน -->
    <v-dialog v-model="showSnapshot" max-width="520" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagSnapshot') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="snapshotNote" data-testid="snapshot-note" :label="$t('graphRagSnapshotNote')" density="comfortable" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="snapshot-cancel" @click="showSnapshot = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="snapshot-confirm" :loading="saving" @click="snapshot">{{ $t('save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ลบทั้งกิ่ง — ยืนยันหลังเห็นจำนวนโหนด/เส้นเชื่อมที่จะหายไป -->
    <v-dialog v-model="showSubtree" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagSubtreeConfirmTitle') }}</v-card-title>
        <v-card-text v-if="subtreePreview">
          <div class="mb-2" data-testid="subtree-node-key">{{ subtreeNodeName || subtreeNodeKey }}</div>
          <div class="mb-2" data-testid="subtree-summary">
            {{ $t('graphRagSubtreeSummary', { nodes: subtreePreview.nodeCount, edges: subtreePreview.edgeCount }) }}
          </div>
          <div v-if="subtreePreview.warnings && subtreePreview.warnings.length" class="mb-2" data-testid="subtree-warnings">
            <div v-for="(w, i) in subtreePreview.warnings" :key="i">{{ w }}</div>
          </div>
          <div v-if="subtreePreview.sampleNodeKeys && subtreePreview.sampleNodeKeys.length" data-testid="subtree-sample">
            <div>{{ $t('graphRagSubtreeSample') }}</div>
            <ul>
              <li v-for="k in subtreePreview.sampleNodeKeys" :key="k">{{ k }}</li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="subtree-cancel" @click="cancelDeleteSubtree">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="subtree-confirm" :loading="subtreeDeleting" @click="confirmDeleteSubtree">{{ $t('delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ย้ายไปไว้ใต้… — เลือกพ่อใหม่ → backend คำนวณแผน (dryRun) ให้เห็นก่อน → ยืนยันแล้วย้ายทั้งกิ่งในครั้งเดียว
         เส้นพ่อเดิมถูกย้ายปลาย (relation/ทิศเดิม) · HeadingN และ source_lineage_* ของทั้งกิ่งตามพ่อใหม่ · backend บันทึกเวอร์ชันก่อนย้าย -->
    <v-dialog v-model="showMove" max-width="640" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagMoveNodeTitle') }}</v-card-title>
        <v-card-text>
          <div class="mb-3" data-testid="move-node-name">
            <span class="text-medium-emphasis">{{ $t('graphRagMoveNodeMoving') }}</span>
            <strong>{{ moveForm.label || moveForm.nodeKey }}</strong>
            <span v-if="moveForm.nodeType" class="text-medium-emphasis"> · {{ moveForm.nodeType }}</span>
          </div>

          <v-autocomplete
            v-model="moveForm.parentKey"
            v-model:search="moveParentSearch"
            :items="moveParentOptions"
            :loading="moveParentLoading"
            item-title="label"
            item-value="nodeKey"
            no-filter
            autofocus
            :label="$t('graphRagMoveNodeParent')"
            :placeholder="$t('graphRagTypeToSearchNode')"
            :no-data-text="moveParentSearch ? $t('graphRagNodeNotFound') : $t('graphRagTypeToSearchNode')"
            :hint="edgeEndHint(moveParentNode)"
            persistent-hint
            density="comfortable"
            :menu-props="nodePickerMenuProps"
            data-testid="move-node-parent"
          >
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps" :title="item.raw.label || item.raw.nodeKey" :subtitle="`${item.raw.nodeType || 'Node'} · ${item.raw.nodeKey}`" />
            </template>
            <!-- ซ่อนชื่อที่เลือกไว้ระหว่างพิมพ์คำค้นใหม่ — เหตุผลเดียวกับช่องค้นโหนดในแผง Graph Commands -->
            <template #selection="{ item }">
              <span v-if="!moveParentSearch" class="gcc-node-picker-selection">{{ item.raw.label || item.raw.nodeKey }}</span>
            </template>
          </v-autocomplete>

          <v-checkbox
            v-model="moveForm.adjustLevels"
            :label="$t('graphRagMoveNodeAdjust')"
            :hint="$t('graphRagMoveNodeAdjustHint')"
            persistent-hint
            density="compact"
            class="mt-2"
            data-testid="move-node-adjust"
          />

          <v-progress-linear v-if="movePlanLoading" indeterminate color="#C32E0E" class="mt-3" />
          <v-alert v-else-if="movePlanError" type="error" variant="tonal" density="compact" class="mt-3" data-testid="move-node-error">{{ movePlanError }}</v-alert>
          <div v-else-if="movePlan" class="ge-move-plan mt-3" data-testid="move-node-plan">
            <div data-testid="move-node-parent-change">
              {{ $t('graphRagMoveNodeParentChange', { from: movePlan.oldParentLabel || movePlan.oldParentNodeKey || '—', to: movePlan.newParentLabel || movePlan.newParentNodeKey }) }}
            </div>
            <div data-testid="move-node-type-change">
              {{ movePlan.oldNodeType === movePlan.newNodeType
                ? $t('graphRagMoveNodeTypeSame', { type: movePlan.newNodeType || '—' })
                : $t('graphRagMoveNodeTypeChange', { from: movePlan.oldNodeType, to: movePlan.newNodeType }) }}
            </div>
            <div data-testid="move-node-edge">{{ $t('graphRagMoveNodeEdge', { edge: moveEdgeText }) }}</div>
            <div data-testid="move-node-counts">
              {{ $t('graphRagMoveNodeCounts', { nodes: movePlan.subtreeNodes, retyped: movePlan.retypedNodes, lineage: movePlan.relineagedNodes }) }}
            </div>
            <!-- ลูกหลานที่เปลี่ยนชนิดด้วย — ตัวที่ย้ายเองแสดงอยู่บรรทัดบนแล้ว -->
            <ul v-if="movePlan.changes && movePlan.changes.length > 1" class="ge-move-changes" data-testid="move-node-changes">
              <li v-for="c in movePlan.changes" :key="c.nodeKey">{{ c.label || c.nodeKey }}: {{ c.oldNodeType }} → {{ c.newNodeType }}</li>
            </ul>
            <v-alert
              v-for="(w, i) in movePlan.warnings || []"
              :key="i"
              type="warning"
              variant="tonal"
              density="compact"
              class="mt-2"
              data-testid="move-node-warning"
            >{{ w }}</v-alert>
            <div class="text-caption text-medium-emphasis mt-2">{{ $t('graphRagMoveNodeVersionNote') }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="move-node-cancel" :disabled="moveSaving" @click="closeMoveNode">{{ $t('cancel') }}</v-btn>
          <v-btn color="#C32E0E" data-testid="move-node-confirm" :loading="moveSaving" :disabled="!movePlan || movePlanLoading" @click="confirmMoveNode">
            {{ $t('graphRagMoveNodeConfirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ทำสำเนาจากกราฟอื่น — เนื้อหาของกราฟนี้จะถูกแทนที่ทั้งหมด (ชื่อ/รหัส/สิทธิ์ไม่เปลี่ยน) -->
    <v-dialog v-model="showDuplicateFrom" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ $t('graphRagDuplicateFromTitle') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="duplicateFromSource"
            data-testid="duplicate-from-source"
            :items="duplicateFromGraphs"
            item-title="name"
            item-value="graphKey"
            :label="$t('graphRagDuplicateFromSource')"
            :loading="duplicateFromLoading"
            :no-data-text="$t('graphRagDuplicateFromNoSource')"
            density="comfortable"
            variant="outlined"
          />
          <v-alert type="warning" variant="tonal" class="mb-2" data-testid="duplicate-from-warning">
            {{ $t('graphRagDuplicateFromWarning') }}
          </v-alert>
          <div class="mb-1" data-testid="duplicate-from-keep-note">{{ $t('graphRagDuplicateFromKeepNote') }}</div>
          <div data-testid="duplicate-from-revert-note">{{ $t('graphRagDuplicateFromRevertNote') }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="duplicate-from-cancel" @click="cancelDuplicateFrom">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="#C32E0E"
            data-testid="duplicate-from-confirm"
            :loading="duplicateFromRunning"
            :disabled="!duplicateFromSource || duplicateFromRunning"
            @click="confirmDuplicateFrom"
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- รวมกราฟอื่นเข้ากราฟนี้ — ของเดิมไม่ถูกทับ คีย์ที่ซ้ำถูกข้าม (S11-P1 §1) -->
    <v-dialog v-model="showMerge" max-width="560" persistent>
      <v-card data-testid="merge-dialog">
        <v-card-title>{{ $t('graphRagMergeTitle') }}</v-card-title>
        <v-card-text>
          <div class="mb-2">{{ $t('graphRagMergeSelect') }}</div>

          <div v-if="mergeLoading" class="mb-2">{{ $t('loading') }}</div>
          <div v-else-if="!mergeGraphs.length" class="mb-2" data-testid="merge-no-source">{{ $t('graphRagMergeNoSource') }}</div>

          <div v-else data-testid="merge-source-list">
            <div v-for="g in mergeGraphs" :key="g.graphKey" class="d-flex align-center">
              <v-checkbox
                v-model="mergeSelected"
                :value="g.graphKey"
                :label="g.name || g.graphKey"
                :disabled="mergeRunning"
                density="compact"
                hide-details
              />
              <template v-if="mergedCountOf(g.graphKey) > 0">
                <v-chip class="ml-2" size="small" data-testid="merge-already-chip">
                  {{ $t('graphRagMergeAlready', { count: mergedCountOf(g.graphKey) }) }}
                </v-chip>
                <v-btn
                  class="ml-2"
                  size="small"
                  variant="text"
                  data-testid="merge-unmerge-btn"
                  :loading="unmergeRunningKey === g.graphKey"
                  :disabled="mergeRunning || !!unmergeRunningKey"
                  @click="unmergeSource(g.graphKey)"
                >
                  {{ $t('graphRagMergeUnmerge') }}
                </v-btn>
              </template>
            </div>
          </div>

          <div class="mt-3" data-testid="merge-hint">{{ $t('graphRagMergeHint') }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" data-testid="merge-cancel" @click="cancelMerge">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="#C32E0E"
            data-testid="merge-confirm"
            :loading="mergeRunning"
            :disabled="!mergeSelected.length || mergeRunning || !!unmergeRunningKey"
            @click="confirmMerge"
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="toast.show" :color="toast.colour" timeout="5000" data-testid="canvas-toast">{{ toast.text }}</v-snackbar>
  </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue';
import ImportWizard from '@/components/GraphRAG/ImportWizard.vue';

/** เพดานไฟล์ต่อครั้งฝั่ง client (มติ D11) — backend ไม่มีเพดานจำนวนไฟล์ให้พึ่ง */
const CONST_WIZ_MAX_FILES = 100;
/** เพดานต่อไฟล์ให้ตรงกับ GraphRagIngestController.CONST_MAX_UPLOAD_BYTES */
const CONST_WIZ_MAX_BYTES = 50 * 1024 * 1024;
const CONST_WIZ_ACCEPT = ['.md', '.pdf', '.docx', '.txt', '.csv', '.xlsx', '.pptx'];
/** ต้องตรงกับ GraphRagContentBal.CONST_MAX_PAGE_SIZE — ขอมากกว่านี้ backend ตัดให้เท่านี้อยู่ดี */
const CONST_CANVAS_PAGE_SIZE = 200;
/** ช่วงเวลาถามความคืบหน้างาน re-embed หลังกด Save */
const CONST_EMBED_POLL_MS = 1500;
/** ช่องค้นโหนดด้วย label ในแผง Graph Commands — หน่วงก่อนยิงค้น และจำนวนตัวเลือกต่อครั้ง */
const CONST_NODE_SEARCH_DEBOUNCE_MS = 300;
const CONST_NODE_SEARCH_PAGE_SIZE = 20;
/** เครื่องมือในเมนู ⚙ Manage → flag ของหน้าต่างหลักที่มันเปิด (ปิดหน้าต่างนี้ = กลับมาที่เมนู) */
const CONST_MANAGE_TOOL_FLAGS = {
  addNode: 'showNode',
  nodes: 'showNodeManager',
  edges: 'showEdgeManager',
  ontology: 'showOntology',
  filters: 'showFilters',
  settings: 'showSettings',
  versions: 'showVersions',
};

/** รอให้เมนูของช่อง Node Type ปิดก่อนเปิด dropdown ของช่องโหนดแม่ */
const CONST_NODE_PARENT_MENU_DELAY_MS = 250;
/** dropdown เลือกโหนดในกล่องแก้โหนด/ย้ายโหนด — node key ยาวมากทำให้เมนูกว้างทะลุกล่อง ⇒ จำกัดความกว้าง (บรรทัดรองตัดด้วย …) */
const CONST_NODE_PICKER_MENU_MAX_WIDTH = 560;
/** APIStatus.Duplicate ของ backend (Model/BaseModel/ResponseModel.cs) */
const CONST_API_STATUS_DUPLICATE = 3;
/** โหมด Auto ขอโหนดไม่เกินเท่านี้จาก GraphRagNode/GetCanvas (มติ D2 · S14-P1) — backend เลือกตามโครงสร้าง · โหมด All ไม่มีเพดาน */
const CONST_CANVAS_AUTO_MAX_NODES = 1000;
/**
 * เมนู ⚙ Manage → Filter Sets ซ่อนไว้ก่อน (2026-10-01 ตามคำสั่งผู้ใช้) — ไม่ใช่ฟีเจอร์หลัก และหน้าปัจจุบันให้พิมพ์ JSON เอง
 * ไม่มีตรวจ field/op ไม่มีปิด/ลบ พิมพ์ผิดแล้วแชทค้นจากกราฟไม่เจอทันที · backend ยังใช้ชุดที่ Active อยู่ตามเดิม
 * เปิดกลับ = ตั้งเป็น true (dialog / method / API ยังอยู่ครบ)
 */
const CONST_FILTER_SETS_ENABLED = false;
/** ตัวเลือกหลักของช่อง Node Type ในฟอร์มโหนด (ผู้ใช้ขอ 2026-10-20 · ) — ชนิดใน Ontology ของกราฟต่อท้ายให้เอง */
const CONST_NODE_FORM_TYPES = ['Product', 'Heading1', 'Heading2', 'Heading3', 'Heading4', 'Heading5', 'Heading6'];
/** ชนิดของโหนดที่เปิดด้วย Create Graph Node / ค่าเริ่มต้นของ Add Node — ต่อท้ายรายการเสมอ */
const CONST_NODE_TYPE_GRAPH = 'GraphNode';
/** กลุ่ม relation ตั้งต้น — ชื่อเดียวกับ legend A–F ของ Edge Type Reference ใน  · ว่าง = Custom (addRelationType ของ  */
const CONST_RELATION_GROUPS = ['Hierarchy', 'Semantics', 'Cross-entity', 'Service', 'Community', 'Custom'];
const CONST_RELATION_GROUP_DEFAULT = 'Custom';
/** ต้นทุนการเดินกราฟค่าเริ่มต้น — เท่ากับ GraphSchemaModel.RelayCost เมื่อไม่ได้ตั้ง (และ rt-cost ของ ) */
const CONST_RELATION_COST_DEFAULT = 1;
/** relation ติ๊ก bidirectional ให้เป็นค่าเริ่มต้น (updateBidirForRelation) */
const CONST_EDGE_BIDIR_DEFAULT_RELATION = 'suggests';

/** อ่าน JSON object จากสตริง — เสีย/ไม่ใช่ object = {} (ห้าม throw ในหน้าจอ) */
function parseJsonObject(text) {
  if (!text) return {};
  try {
    const value = JSON.parse(text);
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  } catch {
    return {};
  }
}

/** ค่า from_types / to_types ให้เป็น array ของสตริงเสมอ */
/** ระดับของชนิด HeadingN — ชนิดอื่นคืน 0 (กติกาเดียวกับ GraphNodeMovePlanner.HeadingLevel ฝั่ง backend) */
function headingLevelOf(nodeType) {
  const m = /^Heading(\d+)$/.exec(String(nodeType || '').trim());
  return m ? Number(m[1]) : 0;
}

function asStringList(value) {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return value.split(',').map((v) => v.trim()).filter(Boolean);
  return [];
}

/**
 * วันนี้ตามเวลาเครื่องผู้ใช้ในรูป 'yyyy-MM-dd' (รูปแบบค่าของ input type="date")
 * ไม่ใช้ toISOString เพราะคืนวันที่ UTC — เวลาไทย 00:00–06:59 จะได้วันที่ของเมื่อวาน
 */
function localTodayIso() {
  const d = new Date();

  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** ค่าเริ่มต้นของสถานะ wizard — ใช้ทั้งตอน data() และตอนล้างคิว */
function emptyWizardState() {
  return {
    step: 1,
    maxStep: 1,
    mode: 'files',
    files: [],
    converting: false,
    sessionExpired: false,
    tooMany: false,
    skipped: [],
    labelRule: 'none',
    hierarchyDirection: 'bottom_up',
    useParentLineage: false,
    step2Selected: [],
    step2Prompt: '',
    step2ViewMode: 'preview',
    step2Busy: false,
    step2Blocked: false,
    step2BlockReason: '',
    // ค่าเริ่มต้น = วันนี้ (ผู้ใช้ลบหรือเปลี่ยนเองได้ที่ขั้น 3) · ใช้เป็นข้อมูลประกอบโหนดและชุดกรองเท่านั้น ไม่ได้ซ่อนโหนดจากการค้นหา
    effectiveDate: localTodayIso(),
    expiryDate: '',
    importMode: 'create',
    step3Blocked: false,
    step3BlockReason: '',
    levelMapping: [],
    aiFilledLevels: [],
    folderNodeTypes: [],
    // ค่าที่ backend เก็บลง RequestJson มีสองค่าเท่านั้นตาม §Folder Mode — level (ตามความลึก) / folder (ราย path)
    folderRelationMode: 'level',
    folderRelationsByLevel: [],
    folderRelations: [],
    // ── โหมดตาราง CSV/XLSX (7-A) ──
    /** ชนิดโหนดของแถวในตาราง (โหมด standard) — ทั้งไฟล์ / ทั้งชีต / แต่ละแถว */
    tabularTypes: { file: '', sheet: '', row: '' },
    /** ชีตของไฟล์ตารางแบบกำหนดเอง — [{ key, fileKey, fileName, sessionId, sheetName, include, mapping, matchStrategy, onMissing }] */
    structuredSheets: [],
    /** key ของไฟล์ที่กำลังอ่านรายชื่อชีตอยู่ — ใช้โชว์สถานะเท่านั้น */
    sheetsLoading: [],
    step4Busy: false,
    step4Blocked: false,
    step4BlockReason: '',
    models: [],
    modelId: null,
    /** Answer model ของกราฟที่อยู่ในลิสต์ (null = กราฟไม่ได้ตั้ง/ตัวที่ตั้งไม่อยู่ในลิสต์) — ขั้น 5 ใช้บอกว่าค่าที่เลือกตรงกับกราฟไหม */
    graphModelId: null,
    /** กราฟตั้ง Answer model ไว้ไหม (แม้ตัวที่ตั้งจะไม่อยู่ในลิสต์) — false ⇒ นำเข้าสำเร็จแล้วตั้งโมเดลที่ใช้ให้เป็น Answer model */
    graphModelSet: false,
    /** ผู้ใช้เลือกโมเดลเองที่ขั้น 5 ในการนำเข้ารอบนี้ — Answer model ห้ามมาทับ (ล้างเมื่อเริ่มรอบใหม่) */
    modelPicked: false,
    creating: false,
    progStep: 0,
    job: null,
    jobId: null,
    createError: '',
    duplicateJob: false,
    /** true (ค่าเริ่มต้น) = แตกโหนดเสร็จแล้วเปิดหน้าข้อเสนอให้ Reject → กลับขั้น 5 → กด Create nodes ถึงเขียนลงกราฟ · false = นำเข้าทันทีแบบ nexa */
    reviewBeforeApply: true,
    /** โมเดลที่ใช้แตกโหนดของงานปัจจุบัน — ขั้น 5 เทียบกับ modelId เพื่อรู้ว่าเปลี่ยนโมเดลหลังตรวจไหม */
    jobModelId: null,
    /** สรุปผลการตรวจ { nodes, edges, rejected } — ขั้น 5 โชว์ก่อนกด Create nodes */
    reviewSummary: null,
    /** ผลนำเข้าอัตโนมัติหลังงานแตกโหนดจบ (ResponseApplyProposals + คำเตือนของ job) — มีคำเตือนเมื่อไหร่ ขั้น 5 ค้างโชว์ */
    applyResult: null,
    showProposals: false,
    /** นำเข้าจากหน้าข้อเสนอเอง (ทางสำรอง) — ปิดหน้านั้นแล้วต้องปิด wizard ตาม · แค่เปิดดูแล้วปิดต้องกลับมาที่ขั้น 5 */
    appliedFromReview: false,
    parentNodeKey: '',
    applied: false,
  };
}

export default {
  name: 'GraphEditor',
  components: { ImportWizard, GraphCanvas: defineAsyncComponent(() => import('@/components/GraphRAG/GraphCanvas.vue')) },
  data() {
    return {
      // ผืนกราฟปิดไว้ก่อนเหมือน nexa (renderer off) แล้วโชว์แผง Graph Commands แทน
      canvasOn: false,
      canvasNodes: [],
      /** จำนวนโหนดทั้งกราฟที่ backend รายงาน — ใช้บอกบนผืนกราฟว่าที่วาดอยู่ครบหรือยัง */
      canvasTotal: 0,
      /** auto = ≤ 1,000 โหนดที่ backend เลือกตามโครงสร้าง · all = ไล่ดึงจนครบ (ตรงกับ dropdown "โหลด" บนผืนกราฟ) */
      canvasLoadMode: 'auto',
      canvasEdges: [],
      /** { [typeId]: '#hex' } จาก GraphRagNode/GetTypeColors — สีสำรองของชนิดที่ ontology ไม่มี (canvasTypeColors) */
      canvasAutoColors: {},
      /** idle | loading | error — ส่งให้ GraphCanvas โชว์ overlay กำลังโหลด/ล้ม (S14-P1 D18) */
      canvasLoadState: 'idle',
      /** จำนวนโหนดที่ไล่หน้ามาแล้วในรอบปัจจุบัน — ป้าย "กำลังโหลดโหนด {loaded}/{total}" */
      canvasLoadedCount: 0,
      canvasHits: [],
      canvasQuery: '',
      effort: 'hybrid',
      /** กำลังรอคำตอบของ Graph Ask — ปุ่ม Search disabled + การ์ดโชว์ spinner */
      searching: false,
      // แผงคำตอบใต้แถบค้นหา (Graph Ask) — askResult = ResponseGraphAsk · askError = request ล้ม / status ≠ 0
      askOpen: false,
      askResult: null,
      askError: '',
      /** nodeKey ที่คลิกจากเลขอ้างอิงบนผืนกราฟ (ไฮไลต์เหลือโหนดเดียว) — ว่าง = ไฮไลต์ทุกแหล่งของคำตอบ */
      askFocusKey: '',
      /** แผงคำตอบด้านขวาพับเหลือแถบแคบ (ไฮไลต์/เส้นทางบนผืนกราฟคงไว้) — ถามใหม่ = กางกลับ */
      askCollapsed: false,
      savingName: false,
      /** งาน re-embed ที่กำลังติดตาม (ResponseIngestJob) — null = ไม่มีงาน · ชิปข้างปุ่ม Save อ่านจากตัวนี้ */
      embedJob: null,
      embedCancelling: false,
      selectedVersion: null,

      // แผง Graph Commands — โหนดที่เลือกจากช่องค้นหาด้วย label (ResponseGraphNodeItem)
      commandNode: null,
      commandNodeSearch: '',
      commandNodeOptions: [],
      commandNodeLoading: false,
      createResult: '',
      createResultError: false,
      nodeResult: '',
      nodeResultError: false,

      graph: {},
      saving: false,
      exporting: false,
      importing: false,
      savingConfig: false,

      // สถานะเวกเตอร์ของกราฟ — อยู่ในกล่อง "ค่าการค้นหา" ไม่ใช่กล่องนำเข้า
      embedding: null,

      nodes: [],
      nodeTotal: 0,
      nodeSearch: '',
      nodeSearchTimer: null,
      nodeOptions: { page: 1, itemsPerPage: 10, sortBy: [] },
      selectedNodeKeys: [],
      // nodeKey → true ระหว่างบันทึกชนิดจากแถวในตาราง (กันกดซ้ำ + โชว์ loading ที่ช่องนั้น)
      nodeTypeSaving: {},
      loadingNodes: false,

      edges: [],
      loadingEdges: false,

      schemaTypes: [],
      loadingSchema: false,

      filtersEnabled: CONST_FILTER_SETS_ENABLED,
      filters: [],
      loadingFilters: false,

      config: {},
      configLoaded: false,
      // config ตามที่บันทึกล่าสุด — ช่องโมเดลบนแถบกราฟบันทึกทับค่านี้ ไม่ใช่ this.config ที่อาจมีค่าค้างจาก Search Settings ที่ยังไม่ได้กด Save
      savedConfig: {},
      savingAskModel: false,
      askModelMenu: false,
      // รายการโมเดลของช่อง "โมเดลที่ใช้ตอบคำถาม" บนแถบกราฟ — โหลดครั้งเดียวตอนเปิดหน้า
      askModels: [],
      askModelsLoaded: false,
      askModelsLoading: false,
      versions: [],
      loadingVersions: false,

      // เครื่องมือที่เดิมเป็นแท็บ ตอนนี้เปิดเป็น dialog
      showManage: false,
      // ชื่อ flag ของหน้าต่างที่เปิดจากเมนู ⚙ Manage — ปิดหน้าต่างนั้นแล้วเปิดเมนูกลับมา ('' = ไม่ได้เปิดจากเมนู)
      manageReturnFlag: '',
      showNodeManager: false,
      showEdgeManager: false,
      showOntology: false,
      showFilters: false,
      showSettings: false,
      showVersions: false,
      showIngest: false,

      // นำเข้าใต้ Node ID — ว่าง = นำเข้าที่รากเหมือนปุ่ม Create graph from...
      ingestParentNodeKey: '',

      // Import Wizard (S12-P1) — เจ้าของ state/คิว/timer อยู่ที่หน้านี้ ไม่ใช่ใน dialog (D10)
      wiz: emptyWizardState(),
      wizConfirm: false,
      wizConfirmKind: '',
      wizConfirmCount: 0,
      wizMaxFiles: CONST_WIZ_MAX_FILES,

      showNode: false,
      showEdge: false,
      showSchema: false,
      showFilter: false,
      showImport: false,
      showSnapshot: false,

      // ลบทั้งกิ่ง — preview มาจาก backend แล้วค่อยให้ผู้ใช้ยืนยัน
      showSubtree: false,
      subtreeNodeKey: '',
      /** label ของโหนดที่จะลบทั้งกิ่ง — แสดงในกล่องยืนยันแทน node id */
      subtreeNodeName: '',
      subtreePreview: null,
      subtreeLoading: false,
      subtreeDeleting: false,
      // ย้ายไปไว้ใต้… — movePlan = ผล dryRun ล่าสุด · movePlanSeq ทิ้งคำตอบเก่าที่มาช้า
      showMove: false,
      moveForm: { nodeKey: '', label: '', nodeType: '', parentKey: null, adjustLevels: true },
      moveParentSearch: '',
      moveParentOptions: [],
      moveParentLoading: false,
      moveParentTimer: null,
      movePlan: null,
      movePlanError: '',
      movePlanLoading: false,
      movePlanSeq: 0,
      moveSaving: false,
      // กล่องแก้โหนด — โครงสร้างรอบโหนด (GraphRagNodeHierarchy) และช่องค้นโหนดแม่
      nodeHierarchy: null,
      nodeHierarchyLoading: false,
      nodeParentSearch: '',
      nodeParentOptions: [],
      nodeParentLoading: false,
      nodeParentTimer: null,
      nodeParentMenu: false,
      nodePickerMenuProps: { maxWidth: CONST_NODE_PICKER_MENU_MAX_WIDTH },

      // ทำสำเนาจากกราฟอื่น — เลือกกราฟต้นทางแล้วทับเนื้อหาของกราฟนี้
      showDuplicateFrom: false,
      duplicateFromSource: null,
      duplicateFromGraphs: [],
      duplicateFromLoading: false,
      duplicateFromRunning: false,

      // รวม / ถอดกราฟอื่นเข้ากราฟนี้ (S11-P1 §1)
      showMerge: false,
      mergeGraphs: [],
      mergeSelected: [],
      mergedSources: [],
      mergeLoading: false,
      mergeRunning: false,
      unmergeRunningKey: '',

      nodeForm: {},
      edgeForm: {},
      /** ช่องค้นโหนดในฟอร์มเส้น — ผลค้น (โหนดที่เลือกอยู่ถูกใส่ไว้ด้วยเสมอ ช่องจะได้แสดงชื่อ) */
      edgeSourceOptions: [],
      edgeTargetOptions: [],
      edgeSourceSearch: '',
      edgeTargetSearch: '',
      edgeSourceLoading: false,
      edgeTargetLoading: false,
      schemaForm: {},
      filterForm: {},

      importFile: null,
      importMode: 'replace',
      importWarnings: [],
      snapshotNote: '',

      toast: { show: false, text: '', colour: 'success' },
      searchModes: ['fuzzy-first', 'semantic-first', 'bm25-node-name', 'hybrid-union', 'hybrid-rerank', 'baseline-vector-only'],
      parentContextModes: ['counted', 'context_only'],
      numberFields: [
        { key: 'fuzzy_threshold', label: 'graphRagFuzzyThreshold' },
        { key: 'semantic_threshold', label: 'graphRagSemanticThreshold', step: 0.01 },
        { key: 'max_hops', label: 'graphRagMaxHops' },
        { key: 'max_context_nodes', label: 'graphRagMaxContextNodes' },
        { key: 'max_context_edges', label: 'graphRagMaxContextEdges' },
        { key: 'similar_to_skip_threshold', label: 'graphRagSimilarToSkip' },
        { key: 'anchor_candidate_pool_size', label: 'graphRagAnchorPool' },
        { key: 'anchor_final_top_k', label: 'graphRagAnchorTopK' },
        { key: 'graph_branch_top_k', label: 'graphRagBranchTopK' },
        { key: 'baseline_top_k', label: 'graphRagBaselineTopK' },
      ],
    };
  },
  computed: {
    efforts() {
      return [
        { value: 'never', text: this.$t('graphRagEffortFast') },
        { value: 'hybrid', text: this.$t('graphRagEffortBalanced') },
        { value: 'always', text: this.$t('graphRagEffortThinking') },
      ];
    },
    graphKey() {
      return this.$route.params.graphKey;
    },
    /** เงื่อนไขเดียวกันทั้ง :disabled ของปุ่ม Search, @click และ Enter ในช่องค้นหา */
    canSearch() {
      return !this.searching && !!(this.canvasQuery || '').trim();
    },
    askOutcome() {
      return this.askResult?.outcome || '';
    },
    askAnswerText() {
      return this.askResult?.answer || '';
    },
    askSources() {
      return Array.isArray(this.askResult?.sources) ? this.askResult.sources : [];
    },
    /** โหนดที่ไฮไลต์ได้ของคำตอบ — sources ก่อน แล้วตามด้วย anchors ที่ยังไม่ซ้ำ */
    askHitKeys() {
      if (!this.askResult) return [];

      const anchors = Array.isArray(this.askResult.anchors) ? this.askResult.anchors : [];
      const keys = [...this.askSources, ...anchors].map((item) => item?.nodeKey).filter(Boolean);

      return [...new Set(keys)];
    },
    /**
     * เส้นทางที่ใช้ตอบ → GraphCanvas :answer-path — โหนด = anchors + sources (askHitKeys) · เส้น = pathEdges (backend เก่าไม่มี field นี้ = [])
     * เฉพาะผลที่มี context (answered / llm_failed) และแผงคำตอบเปิดอยู่ · Reset / ปิดแผง / ถามใหม่ ⇒ null = ผืนกราฟคืนสีเดิม (พับแผงไม่นับเป็นปิด)
     */
    askPath() {
      if (!this.askOpen || (this.askOutcome !== 'answered' && this.askOutcome !== 'llm_failed')) return null;

      const rawEdges = Array.isArray(this.askResult?.pathEdges) ? this.askResult.pathEdges : [];
      const edges = rawEdges.filter((e) => e?.source && e?.target).map((e) => ({ source: String(e.source), target: String(e.target), relation: e.relation ? String(e.relation) : '' }));
      if (!this.askHitKeys.length && !edges.length) return null;

      return { nodeKeys: this.askHitKeys, edges };
    },
    /**
     * แตกคำตอบเป็นชิ้น ๆ ให้ v-for render แทน v-html (กัน XSS จากข้อความของ LLM) — [n] / [^n] ที่มี source citationIndex ตรงกัน = ชิปอ้างอิง
     * เลขที่ไม่มี source (LLM แต่งเอง) คงเป็นข้อความธรรมดา · ข้อความติดกันรวมเป็นชิ้นเดียว
     */
    askSegments() {
      const text = this.askAnswerText;
      const byIndex = new Map(this.askSources.map((s) => [Number(s.citationIndex), s]));
      const segments = [];
      const pushText = (value) => {
        if (!value) return;

        const last = segments[segments.length - 1];
        if (last && last.type === 'text') last.text += value;
        else segments.push({ type: 'text', text: value, key: `t${segments.length}` });
      };
      const re = /\[\^?(\d{1,3})\]/g;
      let lastIndex = 0;
      let match = re.exec(text);

      while (match !== null) {
        pushText(text.slice(lastIndex, match.index));

        const index = Number(match[1]);
        const source = byIndex.get(index);
        if (source) segments.push({ type: 'cite', index, source, key: `c${segments.length}` });
        else pushText(match[0]);

        lastIndex = re.lastIndex;
        match = re.exec(text);
      }
      pushText(text.slice(lastIndex));

      return segments;
    },
    /** โหมด canvas: มีโหนดที่ใช้ตอบแต่ไม่มีตัวไหนถูกวาดเลย (โหมด Auto ตัดไว้) — ผืนกราฟยังโหลดอยู่ไม่นับ */
    askHitsNotDrawn() {
      if (!this.canvasOn || this.canvasLoadState === 'loading' || !this.askHitKeys.length) return false;

      const drawn = new Set(this.canvasNodes.map((n) => n.nodeKey));

      return !this.askHitKeys.some((key) => drawn.has(key));
    },
    /** แถบข้อมูลเล็กใต้คำตอบ — effort / โหมดค้น / คำค้นที่ LLM สร้าง / คำสำคัญ / token / เวลา (แสดงเฉพาะค่าที่มี) */
    askMetaItems() {
      const r = this.askResult;
      if (!r) return [];

      const items = [];
      if (r.effort) {
        const effort = this.efforts.find((e) => e.value === r.effort);
        items.push(this.$t('graphRagAskEffort', { effort: effort ? effort.text : r.effort }));
      }
      if (r.searchMode) items.push(this.$t('graphRagAskSearchMode', { mode: r.searchMode }));
      if (r.rewritten && r.retrievalQuery) items.push(this.$t('graphRagAskRetrievalQuery', { query: r.retrievalQuery }));
      if (Array.isArray(r.keywords) && r.keywords.length) items.push(this.$t('graphRagAskKeywords', { keywords: r.keywords.join(', ') }));
      if (typeof r.tokenUsage?.total === 'number') items.push(this.$t('graphRagAskTokens', { count: r.tokenUsage.total.toLocaleString() }));
      if (typeof r.timing?.totalMs === 'number') {
        const ms = r.timing.totalMs;
        items.push(this.$t('graphRagAskTime', { time: ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(1)} s` }));
      }

      return items;
    },
    canEdit() {
      return this.graph.accessLevel === 'edit' || this.graph.accessLevel === 'manage';
    },
    /**
     * ค่า config.model ในรูปที่ v-select ใช้ — ไม่มี key / สตริงว่าง = null (ตัวเลือก "ไม่ตั้ง")
     * id ต้องเป็น string เสมอ เพราะ validator ฝั่ง backend รับ model เป็นข้อความ และ GetConfig คืนกลับมาเป็นข้อความ
     */
    askModel: {
      get() {
        const value = this.config.model;

        return value == null || value === '' ? null : String(value);
      },
      set(value) {
        this.config.model = value == null || value === '' ? null : String(value);
      },
    },
    /** ค่าของช่องโมเดลบนแถบกราฟ — "ไม่ตั้ง" เป็นสตริงว่าง (v-select ถือ null ว่าไม่ได้เลือก จะไม่แสดง "ไม่ตั้ง") · เลือกแล้วบันทึกทันที */
    toolbarAskModel: {
      get() {
        return this.askModel || '';
      },
      set(value) {
        this.saveAskModel(value);
      },
    },
    /**
     * ตัวเลือกโมเดล + "ไม่ตั้ง"
     *  - value = GPTModel.ModelID (รหัสภายในที่บันทึกลง config model — GraphRagLlmClient หาโมเดลด้วยค่านี้)
     *  - เลขที่แสดง (displayId) = GPTModel.ModelDisplay ตัวเดียวกับคอลัมน์ "Model ID" ของหน้า Model Management
     *    (ไม่ใช่ value — JAYTEST คือ ModelDisplay 536 แต่ ModelID 1711)
     * บอทที่ถูกแก้จนได้เวอร์ชันใหม่ (ModelID ใหม่) ไม่หลุดมาที่นี่ — GetConfig คืน config.model เป็นเวอร์ชันปัจจุบันให้แล้ว
     * ค่าที่ยังไม่อยู่ในลิสต์ (บอทถูกลบ/ไม่มีสิทธิ์เห็น) แสดงเป็นรหัสภายในพร้อม # ให้รู้ว่าไม่ใช่เลขบอท
     * และบอกว่าไม่พบเมื่อโหลดลิสต์เสร็จแล้วเท่านั้น (ระหว่างโหลดยังไม่รู้)
     */
    /** ชื่อตัวที่เลือกอยู่ตามที่ช่อง Answer model แสดง — onModelKeydown ใช้รู้ว่าข้อความในช่องยังไม่ถูกแก้ */
    askModelTitle() {
      const hit = this.askModelItems.find((i) => i.value === this.toolbarAskModel);

      return hit ? hit.title : '';
    },
    askModelItems() {
      const none = this.$t('graphRagAskModelNone');
      const items = [
        { title: none, value: '', displayId: null, name: none },
        ...this.askModels.map((m) => ({
          title: `${m.modelDisplay ?? ''} ${m.name}`.trim(),
          value: String(m.id),
          displayId: m.modelDisplay ?? null,
          name: m.name,
        })),
      ];
      const current = this.askModel;

      if (current && !items.some((item) => item.value === current)) {
        const name = this.askModelsLoaded ? this.$t('graphRagAskModelMissing') : '';

        items.push({ title: `#${current} ${name}`.trim(), value: current, displayId: '#' + current, name });
      }

      return items;
    },
    /** เวอร์ชันใน dropdown นอกจากเวอร์ชันปัจจุบัน — ตัวปัจจุบันมี option "· current" ของตัวเองแล้ว ไม่งั้นจะขึ้นซ้ำสองแถว */
    previousVersions() {
      return this.versions.filter((v) => v.versionNo !== this.graph.currentVersionNo);
    },
    selectedVersionItem() {
      return this.versions.find((v) => v.versionNo === this.selectedVersion) || null;
    },
    /** ย้อนได้เฉพาะเวอร์ชันเก่าที่มีสำเนาเก็บไว้ — เลือกเวอร์ชันปัจจุบันอยู่ไม่มีอะไรให้ย้อน */
    canRevertSelected() {
      const item = this.selectedVersionItem;

      return this.canEdit && !!item && item.versionNo !== this.graph.currentVersionNo && item.hasArchive;
    },
    /**
     * สีโหนดของ canvas — ontology ของกราฟชนะเสมอ · ชนิดที่ ontology ไม่มีใช้สี builtin/palette แบบ nexa จาก GetTypeColors
     * (ไม่งั้นกราฟที่นำเข้าโดยยังไม่ได้ตั้ง ontology จะเป็นสีเทาทั้งผืน) · เปลี่ยน schemaTypes (บันทึก/ลบใน dialog Ontology) แล้ว canvas recolor เอง
     */
    canvasTypeColors() {
      const out = { ...this.canvasAutoColors };
      for (const t of this.schemaTypes) if (t.typeKind === 'node' && t.typeId && t.color) out[t.typeId] = t.color;

      return out;
    },
    /** ชื่อแสดงของชนิดโหนด / relation ใน legend 🔍 ของ canvas — จาก ontology (nexa nodeTypeLabel / relLabel) */
    canvasTypeLabels() {
      const out = { node: {}, relation: {} };
      for (const t of this.schemaTypes) if (t.typeId && t.label && out[t.typeKind]) out[t.typeKind][t.typeId] = t.label;

      return out;
    },
    nodeHeaders() {
      return [
        { title: `${this.$t('graphRagLabel')} / ${this.$t('graphRagNodeKey')}`, value: 'label', sortable: false, width: '32%' },
        { title: this.$t('graphRagNodeType'), value: 'nodeType', sortable: false, width: '18%' },
        { title: this.$t('graphRagParentNode'), value: 'parent', sortable: false, width: '26%' },
        { title: this.$t('graphRagAliases'), value: 'aliases', sortable: false, width: '14%' },
        { title: this.$t('manage'), value: 'manage', align: 'center', sortable: false, width: '10%' },
      ];
    },
    edgeHeaders() {
      return [
        { title: this.$t('graphRagSource'), value: 'source', sortable: false },
        { title: this.$t('graphRagRelation'), value: 'relation', sortable: false },
        { title: this.$t('graphRagTarget'), value: 'target', sortable: false },
        { title: this.$t('graphRagEdgeNote'), value: 'note', sortable: false },
        { title: this.$t('manage'), value: 'manage', align: 'center', sortable: false, width: '18%' },
      ];
    },
    /**
     * ตัวเลือกช่อง Node Type: Product · Heading1–6 → ชนิดโหนดใน Ontology ของกราฟ → GraphNode 
     * ค่าเดิมของโหนดที่ไม่อยู่ในรายการ (เช่นชนิดที่ AI แตกมา) ใส่ท้ายสุด — แก้โหนดแล้วชนิดเดิมต้องไม่หาย
     */
    nodeTypeItems() {
      return this.nodeTypeOptionsFor(this.nodeForm.nodeType);
    },
    /** ชนิดมาตรฐาน + ชนิดใน Ontology + GraphNode — ไม่รวมค่าปัจจุบันของแต่ละแถว (nodeTypeOptionsFor เติมให้) */
    nodeTypeBaseOptions() {
      const ids = [...CONST_NODE_FORM_TYPES];
      const labels = {};
      for (const t of this.schemaTypes) {
        if (t.typeKind !== 'node' || !t.typeId) continue;
        if (t.label && t.label !== t.typeId) labels[t.typeId] = t.label;
        if (!ids.includes(t.typeId)) ids.push(t.typeId);
      }
      if (!ids.includes(CONST_NODE_TYPE_GRAPH)) ids.push(CONST_NODE_TYPE_GRAPH);

      return ids.map((id) => ({ value: id, title: labels[id] ? `${labels[id]} (${id})` : id }));
    },
    /** ตัวเลือก From / To Types — ชนิดโหนดใน Ontology + Product · Heading1–6 + GraphNode (ชุดเดียวกับช่อง Node Type) */
    schemaNodeTypeIds() {
      const ids = [...CONST_NODE_FORM_TYPES];
      for (const t of this.schemaTypes) if (t.typeKind === 'node' && t.typeId && !ids.includes(t.typeId)) ids.push(t.typeId);
      if (!ids.includes(CONST_NODE_TYPE_GRAPH)) ids.push(CONST_NODE_TYPE_GRAPH);
      for (const id of [...(this.schemaForm.fromTypes || []), ...(this.schemaForm.toTypes || [])]) if (id && !ids.includes(id)) ids.push(id);

      return ids;
    },
    /** กลุ่ม relation ที่มีอยู่แล้วในกราฟนี้ + กลุ่มตั้งต้น */
    relationGroupItems() {
      const groups = [...CONST_RELATION_GROUPS];
      for (const t of this.schemaTypes) {
        const group = t.typeKind === 'relation' ? parseJsonObject(t.attributeSchemaJson).group : '';
        if (group && !groups.includes(group)) groups.push(group);
      }

      return groups;
    },
    /** ชนิด relation ใน Ontology พร้อมค่าเสริมที่อ่านแล้ว — ใช้ทั้งรายการ relation และตารางอ้างอิงในฟอร์มเส้น */
    relationTypeDefs() {
      return this.schemaTypes
        .filter((t) => t.typeKind === 'relation' && t.typeId)
        .map((t) => {
          const attrs = parseJsonObject(t.attributeSchemaJson);

          return {
            typeId: t.typeId,
            title: t.label && t.label !== t.typeId ? `${t.label} (${t.typeId})` : t.typeId,
            group: attrs.group || CONST_RELATION_GROUP_DEFAULT,
            cost: Number.isInteger(attrs.cost) ? attrs.cost : CONST_RELATION_COST_DEFAULT,
            fromTypes: asStringList(attrs.from_types),
            toTypes: asStringList(attrs.to_types),
            description: attrs.description || '',
          };
        });
    },
    edgeSourceNode() {
      return this.edgeSourceOptions.find((n) => n.nodeKey === this.edgeForm.sourceNodeKey) || null;
    },
    edgeTargetNode() {
      return this.edgeTargetOptions.find((n) => n.nodeKey === this.edgeForm.targetNodeKey) || null;
    },
    /**
     * รายการในช่องโหนดแม่: ยังไม่พิมพ์ = ตัวที่แนะนำ (ระดับพอดี) ขึ้นก่อน แล้วตามด้วยแม่ / แม่ของแม่ / พี่น้อง
     * พิมพ์ค้น = ผลค้นทั้งกราฟ · ทั้งสองแบบติดป้าย "แนะนำ" ให้ตัวที่ระดับพอดี
     */
    /** หน้าต่างที่เปิดจากเมนู ⚙ Manage ยังเปิดอยู่ไหม — หน้าต่างย่อยที่เปิดซ้อนจากมัน (เช่นแก้โหนดจากตาราง) ไม่นับ */
    manageReturnOpen() {
      return this.manageReturnFlag ? !!this[this.manageReturnFlag] : false;
    },
    nodeParentItems() {
      // ป้าย "แนะนำ" = ระดับพอดีกับชนิดที่เลือก (รวมผลค้นที่อยู่กิ่งอื่น) · แสดงเฉพาะตอนที่ระดับยังไม่ต่อกับแม่ที่เลือก
      const expected = this.nodeExpectedParentLevel;
      const fits = (n) => !!this.nodeLevelAdvice && expected !== null && n.nodeKey !== this.nodeForm.nodeKey && headingLevelOf(n.nodeType) === expected;
      // ตัวที่เลือกอยู่ขึ้นบนสุด — Vuetify เลื่อนเมนูไปหาตัวที่เลือกตอนเปิด ถ้าอยู่ท้ายรายการ ตัวที่แนะนำด้านบนจะถูกเลื่อนพ้นจอ
      const source = this.nodeParentSearch
        ? this.nodeParentOptions
        : [this.nodeParentNode, ...this.nodeParentSuggestions, ...this.nodeParentOptions];
      const seen = new Set();
      const items = [];
      for (const n of source) {
        if (!n || seen.has(n.nodeKey)) continue;
        seen.add(n.nodeKey);
        items.push({ ...n, suggested: fits(n) });
      }

      // ผลค้น: ตัวที่แนะนำขึ้นก่อน (sort ของ JS คงลำดับเดิมภายในกลุ่ม)
      if (this.nodeParentSearch) items.sort((a, b) => Number(b.suggested) - Number(a.suggested));

      return items;
    },
    nodeParentNode() {
      return this.nodeParentOptions.find((n) => n.nodeKey === this.nodeForm.parentKey) || null;
    },
    nodeTypeChanged() {
      return !!this.nodeForm.nodeKey && this.nodeForm.nodeType !== this.nodeForm.originalType;
    },
    nodeParentChanged() {
      return !!this.nodeForm.nodeKey && (this.nodeForm.parentKey || null) !== (this.nodeForm.originalParentKey || null);
    },
    /** เปลี่ยนจาก HeadingN เป็น HeadingM และมีลูกหลาน — ถามว่าจะเลื่อนลูกหลานตามไหม */
    showShiftDescendants() {
      return (
        this.nodeTypeChanged &&
        (this.nodeHierarchy?.descendantCount || 0) > 0 &&
        headingLevelOf(this.nodeForm.originalType) > 0 &&
        headingLevelOf(this.nodeForm.nodeType) > 0
      );
    },
    /** บันทึกต้องผ่าน MoveNode (เปลี่ยนแม่ หรือเลื่อนลูกหลาน) — ที่เหลือบันทึกแบบเดิม */
    nodeNeedsMove() {
      return this.nodeParentChanged || (this.showShiftDescendants && !!this.nodeForm.shiftDescendants);
    },
    nodeParentHint() {
      const count = this.nodeHierarchy?.parents?.length || 0;
      if (count > 1) return this.$t('graphRagMultiParentHint', { count });
      if (!this.nodeForm.parentKey) return this.$t('graphRagNoParentHint');

      return this.nodeParentNode?.nodeType ? this.$t('graphRagEdgeEndType', { type: this.nodeParentNode.nodeType }) : '';
    },
    /** ระดับที่ "ควร" เป็นของโหนดแม่: HeadingN ⇒ Heading(N-1) · Heading1 ⇒ โหนดที่ไม่ใช่ Heading (0) */
    nodeExpectedParentLevel() {
      const level = headingLevelOf(this.nodeForm.nodeType);
      return level > 0 ? level - 1 : null;
    },
    /** เตือนเมื่อระดับของโหนดไม่ต่อกับโหนดแม่ที่เลือก — ไม่บังคับ เพราะเอกสารจริงข้ามระดับได้ */
    nodeLevelAdvice() {
      const expected = this.nodeExpectedParentLevel;
      const parent = this.nodeParentNode;
      if (expected === null || !parent || headingLevelOf(parent.nodeType) === expected) return '';

      return this.$t('graphRagLevelAdvice', {
        type: this.nodeForm.nodeType,
        expected: expected > 0 ? `Heading${expected}` : this.$t('graphRagNonHeadingParent'),
        parent: parent.label || parent.nodeKey,
        parentType: parent.nodeType || '—',
      });
    },
    /** โหนดแม่ที่ระดับพอดี: แม่ของแม่ (เลื่อนขึ้น) · พี่น้อง (เลื่อนลง) · แม่เดิม — ไม่เกิน 8 ตัว */
    nodeParentSuggestions() {
      const expected = this.nodeExpectedParentLevel;
      const h = this.nodeHierarchy;
      if (expected === null || !h || !this.nodeLevelAdvice) return [];

      const seen = new Set([this.nodeForm.nodeKey, this.nodeForm.parentKey]);
      const result = [];
      for (const c of [h.grandparent, ...(h.parents || []), ...(h.siblings || [])]) {
        if (!c || seen.has(c.nodeKey) || headingLevelOf(c.nodeType) !== expected) continue;
        seen.add(c.nodeKey);
        result.push(c);
      }

      return result.slice(0, 8);
    },
    moveParentNode() {
      return this.moveParentOptions.find((n) => n.nodeKey === this.moveForm.parentKey) || null;
    },
    /** เส้นพ่อหลังย้ายแบบอ่านง่าย — ปลายเป็นได้แค่โหนดที่ย้ายหรือพ่อใหม่ */
    moveEdgeText() {
      const plan = this.movePlan;
      if (!plan) return '';

      const name = (key) => (key === plan.nodeKey ? plan.nodeLabel : plan.newParentLabel) || key;

      return `${name(plan.edgeSourceNodeKey)} —${plan.relation}→ ${name(plan.edgeTargetNodeKey)}`;
    },
    /** relation นี้ตั้งไว้สำหรับคู่ชนิดโหนดที่เลือกไหม */
    edgeRelationFits() {
      const src = this.edgeSourceNode?.nodeType;
      const tgt = this.edgeTargetNode?.nodeType;

      return (def) => !!src && !!tgt && def.fromTypes.includes(src) && def.toTypes.includes(tgt);
    },
    /** รายการ relation: ตรงกติกา From/To ก่อน → ชนิดอื่นใน Ontology → relation ที่กราฟนี้ใช้อยู่แล้ว */
    edgeRelationItems() {
      const fits = [];
      const others = [];
      for (const def of this.relationTypeDefs) {
        const fit = this.edgeRelationFits(def);
        (fit ? fits : others).push({ value: def.typeId, title: def.title, subtitle: fit ? this.$t('graphRagEdgeRelFits') : def.group });
      }

      const seen = new Set([...fits, ...others].map((i) => i.value));
      const used = [];
      for (const e of [...this.edges, ...this.canvasEdges]) {
        const rel = e?.relation;
        if (!rel || seen.has(rel)) continue;
        seen.add(rel);
        used.push({ value: rel, title: rel, subtitle: this.$t('graphRagEdgeRelUsed') });
      }

      return [...fits, ...others, ...used];
    },
    /** คำอธิบาย relation + เตือนเมื่อไม่ตรงกติกา From/To (ไม่บล็อก) */
    edgeRelationHint() {
      const rel = String(this.edgeForm.relation || '').trim();
      const def = rel && this.relationTypeDefs.find((d) => d.typeId === rel);
      if (!def) return '';

      const parts = [];
      if (def.description) parts.push(def.description);
      const hasRule = def.fromTypes.length || def.toTypes.length;
      if (hasRule && this.edgeSourceNode && this.edgeTargetNode && !this.edgeRelationFits(def)) {
        parts.push(this.$t('graphRagEdgeRelOutOfRule', { from: def.fromTypes.join(', ') || '-', to: def.toTypes.join(', ') || '-' }));
      }

      return parts.join(' · ');
    },
    /** ตารางอ้างอิงชนิดเส้น — ตัวที่ตรงคู่ชนิดที่เลือกขึ้นก่อน */
    edgeTypeReference() {
      return this.relationTypeDefs
        .map((def) => ({
          ...def,
          fits: this.edgeRelationFits(def),
          direction: `${def.fromTypes.join(' / ') || '*'} → ${def.toTypes.join(' / ') || '*'}`,
        }))
        .sort((a, b) => Number(b.fits) - Number(a.fits) || a.group.localeCompare(b.group) || a.typeId.localeCompare(b.typeId));
    },
    schemaHeaders() {
      return [
        { title: this.$t('graphRagTypeKind'), value: 'typeKind', sortable: false, width: '14%' },
        { title: this.$t('graphRagTypeId'), value: 'typeId', sortable: false, width: '22%' },
        { title: this.$t('graphRagLabel'), value: 'label', sortable: false, width: '24%' },
        { title: this.$t('graphRagColor'), value: 'color', sortable: false, width: '16%' },
        { title: this.$t('graphRagSortOrder'), value: 'sortOrder', sortable: false, width: '10%' },
        { title: this.$t('graphRagSchemaDetails'), value: 'details', sortable: false },
        { title: this.$t('manage'), value: 'manage', align: 'center', sortable: false, width: '14%' },
      ];
    },
    filterHeaders() {
      return [
        { title: this.$t('graphRagFilterName'), value: 'name', sortable: false, width: '34%' },
        { title: this.$t('graphRagActive'), value: 'isActive', sortable: false, width: '18%' },
        { title: this.$t('updatedBy'), value: 'updateBy', sortable: false, width: '24%' },
        { title: this.$t('manage'), value: 'manage', align: 'center', sortable: false, width: '24%' },
      ];
    },
    versionHeaders() {
      return [
        { title: this.$t('graphRagVersion'), value: 'versionNo', sortable: false, width: '10%' },
        { title: this.$t('graphRagNodes'), value: 'nodeCount', sortable: false, width: '10%' },
        { title: this.$t('graphRagEdges'), value: 'edgeCount', sortable: false, width: '10%' },
        { title: this.$t('graphRagNote'), value: 'note', sortable: false, width: '30%' },
        { title: this.$t('createdBy'), value: 'createBy', sortable: false, width: '18%' },
        { title: this.$t('manage'), value: 'manage', align: 'center', sortable: false, width: '22%' },
      ];
    },

    // ── Import Wizard ────────────────────────────────────────────────
    /** มีงานเดินอยู่จริงหรือไม่ — ใช้ตัดสินทั้งปุ่มย่อ badge และเส้นทางการปิด */
    wizBusy() {
      return this.wiz.converting || this.wiz.creating;
    },
    wizShowBadge() {
      return !this.showIngest && this.wizBusy;
    },
    wizConvertingCount() {
      return this.wiz.files.filter((f) => f.status === 'pending' || f.status === 'converting').length;
    },
    wizMiniText() {
      // ช่วงสร้างโหนดไม่มีไฟล์ไหนกำลังแปลงแล้ว ใช้ข้อความของคิวแปลงต่อจะได้ "แปลง 1/1 · " ชื่อไฟล์ว่าง
      if (this.wiz.creating) return this.wiz.importMode === 'update' ? this.$t('graphRagWizUpdating') : this.$t('graphRagWizCreating');

      const current = this.wiz.files.find((f) => f.status === 'converting');
      const done = this.wiz.files.filter((f) => f.status !== 'pending' && f.status !== 'converting').length;

      return this.$t('graphRagWizMiniWorking', { done: done, total: this.wiz.files.length, file: current ? current.name : '' });
    },
    wizConfirmText() {
      // ใช้จำนวนที่ตรึงไว้ตอนเปิดกล่อง ไม่ใช่ค่าสด — ระหว่าง fade ออกคิวถูกล้างแล้ว ข้อความจะกลายเป็น "0 ไฟล์"
      if (this.wizConfirmKind === 'converting') return this.$t('graphRagWizConfirmConverting', { count: this.wizConfirmCount });

      return this.$t('graphRagWizConfirmSettings');
    },
  },
  watch: {
    /** เปลี่ยน Answer model บน toolbar (หรือโหลด config เสร็จทีหลัง) ⇒ wizard ใช้ตัวใหม่ตาม */
    askModel() {
      this.wizSyncModel();
    },
    /** กดนำเข้าจากหน้าข้อเสนอแล้วปิดหน้านั้น = งานนำเข้ารอบนี้จบ ⇒ ปิด wizard ตาม */
    'wiz.showProposals'(open) {
      if (open) return;
      if (this.wiz.appliedFromReview && this.showIngest) return this.wizRequestClose();

      // โหมดตรวจก่อนสร้าง: ปิดหน้าข้อเสนอ = กลับมาขั้น 5 ⇒ สรุปผลการตรวจให้เห็นก่อนกด Create nodes
      if (this.wiz.reviewBeforeApply && this.wiz.jobId && !this.wiz.applied) this.wizLoadReviewSummary();

      return undefined;
    },
    edgeSourceSearch(term) {
      this.queueEdgeNodeSearch('source', term);
    },
    edgeTargetSearch(term) {
      this.queueEdgeNodeSearch('target', term);
    },
    /** ค่าเริ่มต้นของ "สร้างเส้นย้อนกลับด้วย" — ติ๊กเมื่อ relation = suggests จนกว่าผู้ใช้จะติ๊กเอง */
    'edgeForm.relation'(rel) {
      if (!this.showEdge || this.edgeForm.id || this.edgeForm.bidirTouched) return;

      this.edgeForm.bidirectional = String(rel || '').trim() === CONST_EDGE_BIDIR_DEFAULT_RELATION;
    },
    /** ค้นโหนดตาม label ที่พิมพ์ (หน่วงไว้กันยิงทุกตัวอักษร) — ข้อความเท่ากับ label ของโหนดที่เลือกอยู่ = Vuetify เติมหลังเลือก ไม่ต้องค้นซ้ำ */
    commandNodeSearch(term) {
      clearTimeout(this.commandNodeTimer);
      if (this.commandNode && term === (this.commandNode.label || this.commandNode.nodeKey)) return;

      this.commandNodeTimer = setTimeout(() => this.searchCommandNodes(term), CONST_NODE_SEARCH_DEBOUNCE_MS);
    },
    /** ค้นพ่อใหม่ตามที่พิมพ์ (หน่วงไว้) — ข้อความเท่ากับชื่อที่เลือกอยู่ = Vuetify เติมหลังเลือก ไม่ต้องค้นซ้ำ */
    /**
     * เปลี่ยนชนิดในกล่องแก้โหนดแล้วมีโหนดแม่ที่แนะนำ ⇒ เปิด dropdown ของช่องโหนดแม่ให้เลือกต่อได้ทันที
     * ไม่เปิดตอนเปิดกล่อง (ชนิด = ค่าเดิม) และหน่วงให้เมนูของช่อง Node Type ปิดก่อน ไม่งั้นสองเมนูชนกัน
     */
    /** ปิดหน้าต่างที่เปิดจากเมนู ⚙ Manage ⇒ เปิดเมนูกลับมา เผื่อตั้งค่าอย่างอื่นต่อ */
    manageReturnOpen(open) {
      if (open || !this.manageReturnFlag) return;

      this.manageReturnFlag = '';
      this.showManage = true;
    },
    'nodeForm.nodeType'(next) {
      if (!this.showNode || !this.nodeForm.nodeKey || next === this.nodeForm.originalType) return;

      setTimeout(() => {
        if (this.showNode && this.nodeParentSuggestions.length) this.openNodeParentMenu();
      }, CONST_NODE_PARENT_MENU_DELAY_MS);
    },
    /** ค้นโหนดแม่ในกล่องแก้โหนด (หน่วงไว้) — ข้อความเท่ากับชื่อที่เลือกอยู่ = Vuetify เติมหลังเลือก ไม่ต้องค้นซ้ำ */
    nodeParentSearch(term) {
      clearTimeout(this.nodeParentTimer);
      const selected = this.nodeParentNode;
      if (selected && term === (selected.label || selected.nodeKey)) return;

      this.nodeParentTimer = setTimeout(() => this.searchNodeParents(term), CONST_NODE_SEARCH_DEBOUNCE_MS);
    },
    moveParentSearch(term) {
      clearTimeout(this.moveParentTimer);
      const selected = this.moveParentNode;
      if (selected && term === (selected.label || selected.nodeKey)) return;

      this.moveParentTimer = setTimeout(() => this.searchMoveParents(term), CONST_NODE_SEARCH_DEBOUNCE_MS);
    },
    'moveForm.parentKey'() {
      this.loadMovePlan();
    },
    'moveForm.adjustLevels'() {
      this.loadMovePlan();
    },
    /** ปิดกล่องนำเข้าเมื่อไหร่ต้องลืมโหนดปลายทางเสมอ ไม่งั้นครั้งถัดไปจะพา parent เก่าติดไปด้วย */
    showIngest(open) {
      if (!open) {
        this.ingestParentNodeKey = '';
        return;
      }

      this.wizOnOpen();
    },
  },
  mounted() {
    // timer/controller ของ wizard เก็บนอก data เพื่อไม่ให้ถูกห่อเป็น reactive proxy
    this.wizJobTimer = null;
    this.wizAbort = null;
    // timer ติดตามงาน re-embed — non-reactive เหมือน wizJobTimer
    this.embedPollTimer = null;
    // timer หน่วงการค้นโหนดในแผง Graph Commands — non-reactive เหมือนกัน
    this.commandNodeTimer = null;
    // timer หน่วงการค้นโหนดในฟอร์มเส้น (ต้นทาง / ปลายทาง) — non-reactive
    this.edgeSearchTimers = { source: null, target: null };
    // ธงคุมคิวแปลง: running กันลูปซ้อน · stopped สั่งลูปที่กำลังคลี่ตัวให้เลิกเดินต่อ
    this.wizQueueRunning = false;
    this.wizQueueStopped = false;
    // เลขรอบโหลดผืนกราฟ — non-reactive (S14-P1 D11) ใช้ทิ้ง callback ของรอบเก่า
    this.canvasLoadSeq = 0;
    // เลขรอบของ Graph Ask — non-reactive · Reset ระหว่างรอคำตอบแล้วคำตอบเก่ามาถึงทีหลัง ต้องทิ้ง ไม่งั้นการ์ด/ไฮไลต์เด้งกลับ
    this.askSeq = 0;

    // รายการเวอร์ชันโหลดต่อจาก loadGraph เอง (เทียบเลขล่าสุดกับ currentVersionNo)
    this.loadGraph();
    this.loadConfig();
    // ช่องโมเดลอยู่บนแถบกราฟตลอด จึงต้องมีรายการตั้งแต่เปิดหน้า
    this.loadAskModels();
    globalThis.addEventListener('beforeunload', this.wizBeforeUnload);
  },
  beforeUnmount() {
    // ออกจากหน้าโดยไม่เคลียร์ จะเหลือ interval ยิง API ทิ้งไว้และคำขออัปโหลดค้าง
    clearInterval(this.wizJobTimer);
    clearTimeout(this.embedPollTimer);
    clearTimeout(this.commandNodeTimer);
    clearTimeout(this.edgeSearchTimers?.source);
    clearTimeout(this.edgeSearchTimers?.target);
    if (this.wizAbort) this.wizAbort.abort();
    globalThis.removeEventListener('beforeunload', this.wizBeforeUnload);
  },
  methods: {
    // ── แผง Graph Commands ───────────────────────────────────────────
    /** เปิด/ปิดการวาดผืนกราฟ เหมือนปุ่ม Canvas View ของ nexa */
    toggleCanvas() {
      this.canvasOn = !this.canvasOn;
      if (this.canvasOn) this.loadCanvas();
    },
    /** เปิดเครื่องมือจากเมนู ⚙ Manage แล้วปิดเมนูทิ้ง */
    openTool(tool) {
      this.showManage = false;
      // ปิดหน้าต่างนี้แล้วกลับมาที่เมนู ⚙ Manage (watch manageReturnOpen)
      this.manageReturnFlag = CONST_MANAGE_TOOL_FLAGS[tool] || '';

      // ปุ่มชุดเดียวกับการ์ด "จัดการ" ในแผง Graph Commands
      if (tool === 'addNode') return this.canEdit ? this.openNode(null) : undefined;
      if (tool === 'nodes') return this.openNodeManager();
      if (tool === 'edges') return this.openEdgeManager();
      if (tool === 'ontology') {
        this.showOntology = true;
        return this.loadSchema();
      }
      if (tool === 'filters') {
        this.showFilters = true;
        return this.loadFilters();
      }
      if (tool === 'settings') {
        this.showSettings = true;
        this.loadEmbeddingStatus();

        return this.loadConfig();
      }
      if (tool === 'versions') {
        this.showVersions = true;
        return this.loadVersions();
      }
      return undefined;
    },
    /** เปิด dialog ที่ไม่ต้องโหลดข้อมูลก่อน (บันทึกเวอร์ชัน / นำเข้าไฟล์) */
    openFromManage(flag) {
      this.showManage = false;
      this.manageReturnFlag = flag;
      this[flag] = true;
    },
    openIngest() {
      if (!this.canEdit) return;

      // ปุ่มนี้นำเข้าที่รากเสมอ — ล้างโหนดปลายทางที่อาจค้างจากงานนำเข้าใต้โหนดของรอบก่อน (wizard ยังรองรับ parentNodeKey)
      this.ingestParentNodeKey = '';
      this.showIngest = true;
    },
    openNodeManager() {
      this.showNodeManager = true;
      this.loadNodes();
      // ช่องชนิดโหนดในแถวใช้ชนิดจาก Ontology ด้วย — โหลดครั้งแรกที่เปิดตาราง
      if (!this.schemaTypes.length && !this.loadingSchema) this.loadSchema();
    },
    openEdgeManager(sourceNodeKey) {
      this.showEdgeManager = true;
      this.loadEdges();
      if (sourceNodeKey) this.openEdge({ id: 0, sourceNodeKey, targetNodeKey: '', relation: '' });
    },
    /** Create Graph Node = โหนดชนิด GraphNode (nexa: openNodeManager(null, {kind:'graphnode'})) */
    openGraphNode() {
      if (!this.canEdit) return;
      this.openNode(null);
      this.nodeForm.nodeType = CONST_NODE_TYPE_GRAPH;
    },
    setCommandResult(text, isError) {
      this.nodeResult = text;
      this.nodeResultError = !!isError;
    },
    /** แถบผลลัพธ์ของการ์ด "สร้าง" — คู่กับ setCommandResult ของการ์ด "จัดการ" */
    setCreateResult(text, isError) {
      this.createResult = text;
      this.createResultError = !!isError;
    },
    /** ตัวเลือกในช่องค้นโหนด — ค้นด้วย endpoint เดิม (search ครอบ label / secondary label / node key) */
    searchCommandNodes(term) {
      const search = (term || '').trim();
      if (!search) {
        this.commandNodeLoading = false;
        this.commandNodeOptions = this.commandNode ? [this.commandNode] : [];
        return;
      }

      this.commandNodeLoading = true;
      const query = new URLSearchParams({ graphKey: this.graphKey, search, page: 1, pageSize: CONST_NODE_SEARCH_PAGE_SIZE }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          // คำตอบของคำค้นเก่าที่มาช้า — ทิ้ง ไม่งั้นรายการกระโดดกลับ
          if (search !== (this.commandNodeSearch || '').trim()) return;

          this.commandNodeLoading = false;
          this.commandNodeOptions = res.status === 0 ? res.data?.data || [] : [];
        },
        () => {
          this.commandNodeLoading = false;
          this.commandNodeOptions = [];
        }
      );
    },
    /** บรรทัดรองของตัวเลือก — label ซ้ำในรายการเดียวกันต้องแยกได้ ⇒ ต่อ node id ให้เฉพาะตัวที่ label ชนกัน */
    commandNodeSubtitle(node) {
      const label = node.label || node.nodeKey;
      const duplicated = this.commandNodeOptions.filter((n) => (n.label || n.nodeKey) === label).length > 1;
      const parts = [node.nodeType || 'Node'];
      if (node.secondaryLabel) parts.push(node.secondaryLabel);
      if (duplicated) parts.push(node.nodeKey);

      return parts.join(' · ');
    },
    /** ชื่อที่ใช้แสดงในข้อความผลลัพธ์ / กล่องยืนยัน */
    commandNodeName(node) {
      return node?.label || node?.nodeKey || '';
    },
    /**
     * อ่านข้อมูลล่าสุดของโหนดที่เลือก (ค่าในช่องค้นอาจเก่าถ้าเพิ่งแก้ไป) — ค้นด้วย label แล้วจับคู่ nodeKey ให้ตรงตัว
     * label สั้น/กว้างจนตัวจริงหลุดจาก 20 แถวแรก → ลองค้นด้วย node key อีกรอบ
     */
    findNodeByKey(node, onFound) {
      const lookup = (search, onMiss) => {
        const query = new URLSearchParams({ graphKey: this.graphKey, search, page: 1, pageSize: CONST_NODE_SEARCH_PAGE_SIZE }).toString();

        this.$call.GraphRagGetNodes(
          query,
          (res) => {
            if (res.status !== 0) return this.setCommandResult(res.message, true);

            const found = (res.data?.data || []).find((n) => n.nodeKey === node.nodeKey);
            if (found) return onFound(found);

            return onMiss();
          },
          () => this.setCommandResult(this.$t('graphRagRequestFailed'), true)
        );
      };

      const notFound = () => this.setCommandResult(this.$t('graphRagNodeNotFound'), true);
      lookup(node.label || node.nodeKey, () => (node.label ? lookup(node.nodeKey, notFound) : notFound()));
    },
    /** โหนดที่เลือกในช่องค้น — คืน null พร้อมขึ้นข้อความถ้ายังไม่ได้เลือก */
    commandSelectedNode() {
      if (!this.commandNode?.nodeKey) {
        this.setCommandResult(this.$t('graphRagEnterNodeIdFirst'), true);
        return null;
      }

      return this.commandNode;
    },
    /** ล้างช่องค้นหลังลบโหนดที่เลือกไปแล้ว */
    clearCommandNode() {
      this.commandNode = null;
      this.commandNodeSearch = '';
      this.commandNodeOptions = [];
    },
    previewNode() {
      const selected = this.commandSelectedNode();
      if (!selected) return;

      this.setCommandResult(this.$t('graphRagLoad'), false);
      this.findNodeByKey(selected, (node) => {
        const head = `${this.commandNodeName(node)} · ${node.nodeType || 'Node'}`;
        this.setCommandResult(`${head}\n${node.description || this.$t('graphRagNoDescription')}`, false);
      });
    },
    editNodeById() {
      if (!this.canEdit) return;

      const selected = this.commandSelectedNode();
      if (!selected) return;

      this.findNodeByKey(selected, (node) => {
        this.setCommandResult('', false);
        this.openNode(node);
      });
    },
    addEdgeFromNode() {
      if (!this.canEdit) return;

      const selected = this.commandSelectedNode();
      if (!selected) return;

      this.findNodeByKey(selected, (node) => {
        this.setCommandResult('', false);
        this.openEdgeManager(node.nodeKey);
      });
    },
    deleteNodeById() {
      if (!this.canEdit) return;

      const selected = this.commandSelectedNode();
      if (!selected) return;

      const nodeKey = selected.nodeKey;
      const name = this.commandNodeName(selected);
      if (!globalThis.confirm(this.$t('graphRagDeleteNodeConfirm', { node: name }))) return;

      this.$call.GraphRagDeleteNodes(
        { graphKey: this.graphKey, nodeKeys: [nodeKey] },
        (res) => {
          if (res.status !== 0) return this.setCommandResult(res.message, true);

          this.clearCommandNode();
          this.setCommandResult(`${this.$t('graphRagDeleted')} · ${name}`, false);
          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.canvasOn) this.loadCanvas();

          return undefined;
        },
        () => this.setCommandResult(this.$t('graphRagRequestFailed'), true)
      );
    },
    /** ลบทั้งกิ่ง ขั้นที่ 1 — ขอ preview จาก backend ก่อน แล้วเปิดกล่องยืนยัน */
    deleteSubtree() {
      if (!this.canEdit) return;

      const selected = this.commandSelectedNode();
      if (!selected) return;

      const nodeKey = selected.nodeKey;
      this.subtreeLoading = true;
      this.$call.GraphRagSubtreePreview(
        this.graphKey,
        nodeKey,
        (res) => {
          this.subtreeLoading = false;
          if (res.status !== 0 || !res.data) return this.setCommandResult(res.message, true);

          this.subtreeNodeKey = nodeKey;
          this.subtreeNodeName = this.commandNodeName(selected);
          this.subtreePreview = res.data;
          this.showSubtree = true;
          this.setCommandResult('', false);

          return undefined;
        },
        () => {
          this.subtreeLoading = false;
          this.setCommandResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },
    /** ปุ่ม "ย้ายไปไว้ใต้…" ใน Graph Commands — ใช้โหนดที่เลือกในช่องค้น */
    moveNodeFromCommand() {
      if (!this.canEdit) return;

      const selected = this.commandSelectedNode();
      if (selected) this.openMoveNode(selected);
    },
    /** ปุ่มย้ายบนแผงรายละเอียดโหนดของผืนกราฟ */
    onCanvasMoveNode(nodeKey) {
      if (!this.canEdit) return;

      const node = this.canvasNodes.find((n) => n.nodeKey === nodeKey);
      if (node) this.openMoveNode(node);
    },
    /** เปิดกล่องย้ายของโหนดนี้ — ล้างผลรอบก่อนทุกครั้ง (พ่อใหม่ / แผน / ข้อความ error) */
    openMoveNode(node) {
      if (!this.canEdit || !node?.nodeKey) return;

      this.movePlanSeq += 1;
      this.moveForm = { nodeKey: node.nodeKey, label: node.label || '', nodeType: node.nodeType || '', parentKey: null, adjustLevels: true };
      this.moveParentSearch = '';
      this.moveParentOptions = [];
      this.movePlan = null;
      this.movePlanError = '';
      this.movePlanLoading = false;
      this.showMove = true;
    },
    closeMoveNode() {
      this.movePlanSeq += 1;
      clearTimeout(this.moveParentTimer);
      this.showMove = false;
      this.movePlan = null;
      this.movePlanError = '';
      this.movePlanLoading = false;
    },
    /** ค้นพ่อใหม่ด้วย endpoint เดิมของช่องค้นโหนด — ตัดโหนดที่กำลังย้ายออกจากรายการ (ลูกหลานยังเลือกได้ แต่ backend จะตอบว่าเกิดวงวน) */
    searchMoveParents(term) {
      const search = String(term || '').trim();
      const selected = this.moveParentNode;
      const keep = (list) => {
        const others = list.filter((n) => n.nodeKey !== this.moveForm.nodeKey);
        return selected && !others.some((n) => n.nodeKey === selected.nodeKey) ? [selected, ...others] : others;
      };

      if (!search) {
        this.moveParentLoading = false;
        this.moveParentOptions = keep([]);
        return;
      }

      this.moveParentLoading = true;
      const query = new URLSearchParams({ graphKey: this.graphKey, search, page: 1, pageSize: CONST_NODE_SEARCH_PAGE_SIZE }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          // คำตอบของคำค้นเก่าที่มาช้า — ทิ้ง ไม่งั้นรายการกระโดดกลับ
          if (search !== String(this.moveParentSearch || '').trim()) return;

          this.moveParentLoading = false;
          this.moveParentOptions = keep(res.status === 0 ? res.data?.data || [] : []);
        },
        () => {
          this.moveParentLoading = false;
        }
      );
    },
    moveRequest(dryRun) {
      return {
        graphKey: this.graphKey,
        nodeKey: this.moveForm.nodeKey,
        newParentNodeKey: this.moveForm.parentKey,
        adjustLevels: !!this.moveForm.adjustLevels,
        dryRun
      };
    },
    /** ขอแผนจาก backend (dryRun) ทุกครั้งที่เปลี่ยนพ่อใหม่ / ปรับระดับ — กติกาทั้งหมดอยู่ฝั่ง backend จอนี้แค่แสดงผล */
    loadMovePlan() {
      if (!this.showMove) return;

      this.movePlanSeq += 1;
      const seq = this.movePlanSeq;
      this.movePlan = null;
      this.movePlanError = '';

      if (!this.moveForm.parentKey) {
        this.movePlanLoading = false;
        return;
      }

      this.movePlanLoading = true;
      this.$call.GraphRagMoveNode(
        this.moveRequest(true),
        (res) => {
          if (seq !== this.movePlanSeq) return;

          this.movePlanLoading = false;
          if (res.status !== 0 || !res.data) {
            this.movePlanError = res.message || this.$t('graphRagRequestFailed');
            return;
          }

          this.movePlan = res.data;
        },
        () => {
          if (seq !== this.movePlanSeq) return;

          this.movePlanLoading = false;
          this.movePlanError = this.$t('graphRagRequestFailed');
        }
      );
    },
    /** ย้ายจริง — ส่งค่าชุดเดียวกับแผนที่เห็นอยู่ แล้วโหลดกราฟ/ตาราง/ผืนกราฟใหม่ */
    confirmMoveNode() {
      if (!this.canEdit || !this.movePlan || this.movePlanLoading || this.moveSaving) return;

      this.moveSaving = true;
      this.$call.GraphRagMoveNode(
        this.moveRequest(false),
        (res) => {
          this.moveSaving = false;
          if (res.status !== 0 || !res.data) {
            this.movePlan = null;
            this.movePlanError = res.message || this.$t('graphRagRequestFailed');
            return;
          }

          const text = this.$t('graphRagMoveNodeDone', {
            name: res.data.nodeLabel || res.data.nodeKey,
            parent: res.data.newParentLabel || res.data.newParentNodeKey,
            type: res.data.newNodeType || '—'
          });
          this.closeMoveNode();
          this.notify(text, 'success');
          this.setCommandResult(text, false);

          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.showEdgeManager) this.loadEdges();
          if (this.canvasOn) this.loadCanvas();
        },
        () => {
          this.moveSaving = false;
          this.movePlanError = this.$t('graphRagRequestFailed');
        }
      );
    },
    /** ยกเลิก — ต้องไม่เปลี่ยนอะไรเลยทั้งบนหน้าจอและบนกราฟ */
    cancelDeleteSubtree() {
      this.showSubtree = false;
      this.subtreePreview = null;
      this.subtreeNodeKey = '';
      this.subtreeNodeName = '';
    },
    /** ลบทั้งกิ่ง ขั้นที่ 2 — backend บันทึกเวอร์ชันก่อนลบให้เองเป็นทางถอย */
    confirmDeleteSubtree() {
      if (!this.canEdit) return;

      const nodeKey = this.subtreeNodeKey;
      if (!nodeKey) return;

      this.subtreeDeleting = true;
      this.$call.GraphRagDeleteSubtree(
        { graphKey: this.graphKey, nodeKey },
        (res) => {
          this.subtreeDeleting = false;
          if (res.status !== 0) return this.setCommandResult(res.message, true);

          this.showSubtree = false;
          this.subtreePreview = null;
          this.subtreeNodeKey = '';
          const name = this.subtreeNodeName || nodeKey;
          this.subtreeNodeName = '';
          this.clearCommandNode();

          this.notify(this.$t('graphRagSubtreeDeleted'), 'success');
          this.setCommandResult(`${this.$t('graphRagSubtreeDeleted')} · ${name}`, false);

          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.canvasOn) this.loadCanvas();

          return undefined;
        },
        () => {
          this.subtreeDeleting = false;
          this.setCommandResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },

    // ── ทำสำเนาจากกราฟอื่น ───────────────────────────────────────────
    /** เปิดกล่องเลือกกราฟต้นทาง — เนื้อหาของกราฟนี้จะถูกทับ จึงต้องมีสิทธิ์แก้ไข */
    openDuplicateFrom() {
      if (!this.canEdit) return;

      this.duplicateFromSource = null;
      this.duplicateFromGraphs = [];
      this.duplicateFromRunning = false;
      this.showDuplicateFrom = true;
      this.loadDuplicateFromGraphs();
    },
    loadDuplicateFromGraphs() {
      this.duplicateFromLoading = true;

      const query = new URLSearchParams({ page: 1, pageSize: 200 }).toString();

      this.$call.GraphRagGetGraphs(
        query,
        (res) => {
          this.duplicateFromLoading = false;
          if (res.status !== 0) return this.setCreateResult(res.message, true);

          // กราฟปัจจุบันเป็นปลายทาง จึงต้องไม่อยู่ในรายการต้นทาง
          this.duplicateFromGraphs = (res.data?.data || []).filter((g) => g.graphKey !== this.graphKey);
          if (!this.duplicateFromGraphs.length) this.setCreateResult(this.$t('graphRagDuplicateFromNoSource'), true);

          return undefined;
        },
        () => {
          this.duplicateFromLoading = false;
          this.setCreateResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },
    /** ยกเลิก — ต้องไม่เปลี่ยนอะไรเลยทั้งบนหน้าจอและบนกราฟ */
    cancelDuplicateFrom() {
      this.showDuplicateFrom = false;
      this.duplicateFromSource = null;
      this.duplicateFromGraphs = [];
      this.duplicateFromRunning = false;
    },
    /** ทับเนื้อหา — backend บันทึกเวอร์ชันก่อนทับให้เองเป็นทางถอย */
    confirmDuplicateFrom() {
      if (!this.canEdit || !this.duplicateFromSource || this.duplicateFromRunning) return;

      const sourceGraphKey = this.duplicateFromSource;

      this.duplicateFromRunning = true;
      this.$call.GraphRagDuplicateFromGraph(
        { targetGraphKey: this.graphKey, sourceGraphKey },
        (res) => {
          this.duplicateFromRunning = false;
          if (res.status !== 0) return this.setCreateResult(res.message, true);

          const summary = this.$t('graphRagDuplicateFromSummary', {
            nodes: res.data?.copiedNodes ?? 0,
            edges: res.data?.copiedEdges ?? 0,
          });

          this.showDuplicateFrom = false;
          this.duplicateFromSource = null;
          this.duplicateFromGraphs = [];

          this.notify(this.$t('graphRagDuplicateFromDone'), 'success');
          this.setCreateResult(`${this.$t('graphRagDuplicateFromDone')} · ${summary}`, false);

          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.canvasOn) this.loadCanvas();

          return undefined;
        },
        () => {
          this.duplicateFromRunning = false;
          this.setCreateResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },

    // ── รวม / ถอดกราฟอื่นเข้ากราฟนี้ ─────────────────────────────────
    /** เปิดกล่องเลือกกราฟต้นทาง — กราฟนี้เป็นปลายทางที่รับของเข้ามา จึงต้องมีสิทธิ์แก้ไข */
    openMergeDialog() {
      if (!this.canEdit) return;

      this.mergeSelected = [];
      this.mergeGraphs = [];
      this.mergedSources = [];
      this.mergeRunning = false;
      this.unmergeRunningKey = '';
      this.showMerge = true;

      this.loadMergeGraphs();
      this.loadMergedSources();
    },
    loadMergeGraphs() {
      this.mergeLoading = true;

      const query = new URLSearchParams({ page: 1, pageSize: 200 }).toString();

      this.$call.GraphRagGetGraphs(
        query,
        (res) => {
          this.mergeLoading = false;
          if (res.status !== 0) return this.setCreateResult(res.message, true);

          // กราฟปัจจุบันเป็นปลายทาง จึงต้องไม่อยู่ในรายการต้นทาง
          this.mergeGraphs = (res.data?.data || []).filter((g) => g.graphKey !== this.graphKey);
          if (!this.mergeGraphs.length) this.setCreateResult(this.$t('graphRagMergeNoSource'), true);

          return undefined;
        },
        () => {
          this.mergeLoading = false;
          this.setCreateResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },
    /** กราฟที่เคยรวมเข้ามาแล้ว — ใช้โชว์ชิป "รวมอยู่แล้ว" และเปิดปุ่มถอด */
    loadMergedSources() {
      this.$call.GraphRagGetMergedSources(
        this.graphKey,
        (res) => {
          if (res.status !== 0) return undefined;

          this.mergedSources = res.data || [];
          return undefined;
        },
        () => {
          this.mergedSources = [];
        }
      );
    },
    mergedCountOf(graphKey) {
      const row = (this.mergedSources || []).find((s) => s.graphKey === graphKey);
      return row ? row.nodeCount : 0;
    },
    /** ยกเลิก — ต้องไม่เปลี่ยนอะไรเลยทั้งบนหน้าจอและบนกราฟ */
    cancelMerge() {
      this.showMerge = false;
      this.mergeSelected = [];
      this.mergeGraphs = [];
      this.mergedSources = [];
      this.mergeRunning = false;
      this.unmergeRunningKey = '';
    },
    /** รวมเข้ามา — backend บันทึกเวอร์ชันก่อนรวมให้เองเป็นทางถอย */
    confirmMerge() {
      if (!this.canEdit || !this.mergeSelected.length || this.mergeRunning) return;

      this.mergeRunning = true;
      this.$call.GraphRagMergeGraphs(
        { graphKey: this.graphKey, sourceGraphKeys: this.mergeSelected.slice() },
        (res) => {
          this.mergeRunning = false;
          if (res.status !== 0) return this.setCreateResult(res.message, true);

          const summary = this.$t('graphRagMergeSummary', {
            added: res.data?.nodesAdded ?? 0,
            skipped: res.data?.nodesSkipped ?? 0,
            edges: res.data?.edgesAdded ?? 0,
          });

          this.showMerge = false;
          this.mergeSelected = [];
          this.mergeGraphs = [];
          this.mergedSources = [];

          this.notify(this.$t('graphRagMergeDone'), 'success');
          this.setCreateResult(`${this.$t('graphRagMergeDone')} · ${summary}`, false);

          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.canvasOn) this.loadCanvas();

          return undefined;
        },
        () => {
          this.mergeRunning = false;
          this.setCreateResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },
    /** ถอดของที่เคยรวมจากกราฟใบนี้ออก — เส้นเชื่อมที่ปลายห้อยถูกกวาดให้ด้วย */
    unmergeSource(sourceGraphKey) {
      if (!this.canEdit || this.mergeRunning || this.unmergeRunningKey) return;

      this.unmergeRunningKey = sourceGraphKey;
      this.$call.GraphRagUnmergeGraphs(
        { graphKey: this.graphKey, sourceGraphKeys: [sourceGraphKey] },
        (res) => {
          this.unmergeRunningKey = '';
          if (res.status !== 0) return this.setCreateResult(res.message, true);

          const summary = this.$t('graphRagUnmergeSummary', {
            nodes: res.data?.nodesRemoved ?? 0,
            edges: res.data?.edgesRemoved ?? 0,
          });

          this.notify(this.$t('graphRagUnmergeDone'), 'success');
          this.setCreateResult(`${this.$t('graphRagUnmergeDone')} · ${summary}`, false);

          // กล่องยังเปิดอยู่ — ต้องรีเฟรชชิป "รวมอยู่แล้ว" ให้ตรงกับของจริง
          this.loadMergedSources();

          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.canvasOn) this.loadCanvas();

          return undefined;
        },
        () => {
          this.unmergeRunningKey = '';
          this.setCreateResult(this.$t('graphRagRequestFailed'), true);
        }
      );
    },

    // ── ผืนกราฟ ──────────────────────────────────────────────────────
    /**
     * โหลดโหนดกับเส้นเชื่อมทั้งกราฟมาวาด
     *
     * ยิง GetAll (ไล่หน้า) กับ GetEdges ขนานกัน แล้วตั้ง canvasEdges/canvasNodes "พร้อมกัน" หลังทั้งสองกลับ —
     * ตั้งคนละจังหวะจะทำให้ GraphCanvas rebuild 2 รอบ (physics restart + overlay กระพริบ) (S14-P1 D11)
     * เลขรอบ canvasLoadSeq ทำให้ callback ของรอบเก่า (สลับ Auto/All, กด Canvas View ซ้ำ, import เสร็จแล้วเรียกซ้อน) ถูกทิ้ง
     */
    loadCanvas() {
      if (!this.graphKey) return;

      this.canvasLoadSeq += 1;
      const seq = this.canvasLoadSeq;
      const pending = { nodes: null, edges: null };
      // D18: ผืนกราฟโชว์ "กำลังโหลดโหนด …" แทน "ยังไม่มีโหนด" จนกว่าจะ commit หรือล้ม
      this.canvasLoadState = 'loading';
      this.canvasLoadedCount = 0;
      const commit = () => {
        if (pending.nodes === null || pending.edges === null) return;

        this.canvasEdges = pending.edges;
        this.canvasNodes = pending.nodes;
        this.canvasLoadState = 'idle';
      };
      // D18: ล้มข้างใดข้างหนึ่ง → บอกผู้ใช้ (key เดิม) ครั้งเดียวต่อรอบ · คงข้อมูลเดิมถ้ามี · รอบเก่าที่ถูกยกเลิกแล้วไม่ต้องแจ้ง
      const fail = () => {
        if (seq !== this.canvasLoadSeq) return;
        if (this.canvasLoadState === 'error') return;

        this.canvasLoadState = 'error';
        this.notify(this.$t('graphRagRequestFailed'), 'error');
      };

      // สีโหนดมาจาก ontology — วันนี้ loadSchema() ถูกเรียกเฉพาะตอนเปิด dialog Ontology
      if (!this.schemaTypes.length && !this.loadingSchema) this.loadSchema();
      this.loadTypeColors();

      const nodesDone = (rows) => {
        pending.nodes = rows;
        commit();
      };
      if (this.canvasLoadMode === 'all') {
        this.loadCanvasPage(1, [], seq, nodesDone, fail);
      } else {
        this.loadCanvasAuto(seq, nodesDone, fail);
      }

      this.$call.GraphRagGetEdges(
        this.graphKey,
        (res) => {
          if (seq !== this.canvasLoadSeq) return;
          if (res.status !== 0) return fail();

          pending.edges = res.data || [];
          commit();

          return undefined;
        },
        fail
      );
    },
    /**
     * โหมด Auto — ขอโหนดไม่เกิน CONST_CANVAS_AUTO_MAX_NODES ที่ backend เลือกตามโครงสร้าง (hierarchy root → breadth-first)
     * ไม่ไล่ GetAll ตามลำดับชื่อ เพราะ 1,000 ชื่อแรกแทบไม่มีเส้นถึงกัน ผืนกราฟจะเป็นวงแหวนจุดโดด
     * กราฟเล็กกว่างบ backend คืนครบทั้งกราฟ · total = จำนวนโหนดทั้งกราฟ ⇒ ป้าย partial ยังทำงานเหมือนเดิม
     */
    loadCanvasAuto(seq, done, fail) {
      const query = new URLSearchParams({ graphKey: this.graphKey, maxNodes: CONST_CANVAS_AUTO_MAX_NODES }).toString();

      this.$call.GraphRagGetCanvasNodes(
        query,
        (res) => {
          if (seq !== this.canvasLoadSeq) return; // รอบนี้ถูกยกเลิกแล้ว — ไม่แตะ state
          if (res.status !== 0) return fail();

          const rows = res.data?.data || [];
          this.canvasTotal = res.data?.total || rows.length;
          this.canvasLoadedCount = rows.length;
          done(rows);

          return undefined;
        },
        fail
      );
    },
    /**
     * ดึงโหนดของผืนกราฟทีละหน้า (โหมด All)
     *
     * backend clamp pageSize ไว้ที่ CONST_CANVAS_PAGE_SIZE เสมอ (GraphRagContentBal.CONST_MAX_PAGE_SIZE)
     * ไล่จนครบ · ส่งผลผ่าน done(rows) ครั้งเดียวตอนจบ — ผู้เรียกเป็นคนตั้ง canvasNodes (ไม่ตั้งทีละหน้า)
     */
    loadCanvasPage(page, acc, seq, done, fail) {
      const query = new URLSearchParams({ graphKey: this.graphKey, page: page, pageSize: CONST_CANVAS_PAGE_SIZE }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          if (seq !== this.canvasLoadSeq) return; // รอบนี้ถูกยกเลิกแล้ว — ไม่แตะ state
          if (res.status !== 0) return fail(); // D18: API ล้ม — บอกผู้ใช้

          const rows = res.data?.data || [];
          const next = acc.concat(rows);

          this.canvasTotal = res.data?.total || next.length;
          this.canvasLoadedCount = next.length; // D18: ป้าย "กำลังโหลดโหนด {loaded}/{total}…" ขยับทุกหน้า

          // rows ว่าง = กันวนไม่รู้จบเมื่อ total ไม่ตรงกับจำนวนแถวจริง
          if (rows.length > 0 && next.length < this.canvasTotal) {
            this.loadCanvasPage(page + 1, next, seq, done, fail);

            return undefined;
          }

          done(next);

          return undefined;
        },
        fail
      );
    },
    /** dropdown "โหลด" บนผืนกราฟ — auto = ≤ 1,000 โหนดที่ backend เลือกตามโครงสร้าง · all = ไล่ GetAll จนครบ */
    onCanvasLoadChange(mode) {
      this.canvasLoadMode = mode === 'all' ? 'all' : 'auto';
      this.loadCanvas();
    },
    /**
     * ถามคำถามจากแถบค้นหา (Graph Ask) — backend ค้นโหนด + ให้ LLM เรียบเรียงคำตอบพร้อมเลขอ้างอิง [n]
     * ผลทุกแบบ (ตอบได้ / ไม่พบข้อมูล / ไม่ได้ตั้งโมเดล / LLM ล้ม / request ล้ม) แสดงในการ์ดใต้แถบค้นหา ไม่ใช้ toast
     * ไฮไลต์บนผืนกราฟ = sources + anchors ของคำตอบ (คงไว้แม้ LLM ล้ม)
     */
    runCanvasSearch() {
      if (!this.canSearch) return;

      this.askSeq += 1;
      const seq = this.askSeq;
      this.searching = true;
      this.askOpen = true;
      this.askCollapsed = false;
      this.askResult = null;
      this.askError = '';
      this.askFocusKey = '';

      this.$call.GraphRagQueryAsk(
        {
          graphKey: this.graphKey,
          query: this.canvasQuery.trim(),
          overrides: { conversational_retrieval_mode: this.effort },
        },
        (res) => {
          if (seq !== this.askSeq) return; // ถูก Reset / ถามใหม่ไปแล้ว — ทิ้ง

          this.searching = false;
          if (res.status !== 0 || !res.data) {
            this.canvasHits = [];
            this.askError = res.message || this.$t('graphRagRequestFailed');

            return;
          }

          this.askResult = res.data;
          this.canvasHits = [...this.askHitKeys];
        },
        () => {
          if (seq !== this.askSeq) return;

          this.searching = false;
          this.canvasHits = [];
          this.askError = this.$t('graphRagRequestFailed');
        }
      );
    },
    resetCanvasSearch() {
      this.askSeq += 1; // คำตอบที่ยังค้างอยู่ถูกทิ้งเมื่อกลับมา
      this.searching = false;
      this.canvasQuery = '';
      this.canvasHits = [];
      this.askOpen = false;
      this.askResult = null;
      this.askError = '';
      this.askFocusKey = '';
    },
    /**
     * ปุ่ม ✕ ของแผง — ซ่อนแผง ไฮไลต์เหลือง (canvasHits) คงไว้จนกด Reset · เส้นทางที่ใช้ตอบ (askPath) หายตาม askOpen ⇒ ผืนกราฟเลิกหรี่
     * ปิดระหว่างรอ = เลิกรอคำตอบนั้น (ปุ่ม Search กลับมากดได้)
     */
    closeAsk() {
      if (this.searching) {
        this.askSeq += 1;
        this.searching = false;
      }
      this.askOpen = false;
    },
    /** ปุ่มพับบนหัวแผงคำตอบ — เหลือแถบแคบ ผืนกราฟได้ที่คืน · ย้ายโฟกัสไปแถบขยาย (ปุ่มเดิมหายไปแล้ว ไม่งั้นโฟกัสหลุดไป body) */
    collapseAsk() {
      this.askCollapsed = true;
      this.$nextTick(() => this.$refs.askExpandBtn?.focus());
    },
    expandAsk() {
      this.askCollapsed = false;
      this.$nextTick(() => this.$refs.askCollapseBtn?.focus());
    },
    /** คลิกเลขอ้างอิง / แถวแหล่งอ้างอิง — canvas: โฟกัสโหนดบนผืนกราฟ · Graph Commands (renderer off): เปิด Preview ของโหนดในแผงจัดการ */
    openAskSource(source) {
      if (!source?.nodeKey) return;

      if (this.canvasOn) this.focusAskNodeOnCanvas(source);
      else this.previewAskNode(source);
    },
    /**
     * ไฮไลต์เหลือโหนดเดียว ⇒ GraphCanvas.applyHighlight เลือก + เลื่อนกล้องไปหาโหนดนั้นเอง
     * แล้วเปิดแผงรายละเอียดโหนดตัวเดียวกับตอนคลิกบนผืนกราฟ — GraphCanvas ไม่มี method สาธารณะให้เลือกโหนด จึงตั้ง selectedNodeKey ผ่าน ref
     */
    focusAskNodeOnCanvas(source) {
      if (!this.canvasNodes.some((n) => n.nodeKey === source.nodeKey)) {
        this.notify(this.$t('graphRagAskNodeNotDrawn', { node: source.label || source.nodeKey }), 'warning');
        return;
      }

      this.askFocusKey = source.nodeKey;
      this.canvasHits = [source.nodeKey];

      const canvas = this.$refs.graphCanvas;
      if (canvas) canvas.selectedNodeKey = source.nodeKey;
    },
    /** กลับไปไฮไลต์ทุกแหล่งของคำตอบหลังโฟกัสทีละโหนด */
    showAllAskHits() {
      this.askFocusKey = '';
      this.canvasHits = [...this.askHitKeys];
    },
    /** โหมด Graph Commands — เลือกโหนดในช่องค้นโหนดของแผงจัดการ แล้วใช้ Preview เดิม (previewNode) แสดงรายละเอียด */
    previewAskNode(source) {
      const node = { nodeKey: source.nodeKey, label: source.label || '', nodeType: source.nodeType || '' };

      this.commandNodeOptions = [node];
      this.commandNode = node;
      this.commandNodeSearch = '';
      this.previewNode();
      this.$nextTick(() => this.$refs.cmdNodeResult?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    },
    copyAskAnswer() {
      const text = this.askAnswerText;
      if (!text) return;

      const failed = () => this.notify(this.$t('graphRagAskCopyFailed'), 'error');
      if (!navigator.clipboard?.writeText) {
        failed();
        return;
      }

      navigator.clipboard.writeText(text).then(() => this.notify(this.$t('graphRagAskCopied'), 'success'), failed);
    },
    /** ปุ่ม "ตั้งค่าโมเดล" ของผล no_model — พาไปที่ช่องโมเดลบนแถบกราฟแล้วเปิดรายการให้เลือก */
    /**
     * คลิกช่องค้นหาโมเดลแล้วเลือกข้อความทั้งช่อง — Vuetify เติมชื่อตัวที่เลือกลงช่องตอนโฟกัส
     * ถ้าไม่เลือกให้ คำที่พิมพ์จะต่อท้ายชื่อเดิม ("536 JAYTESTkanun") แล้วค้นไม่เจอ · รอ tick ให้ Vuetify เติมข้อความก่อน
     */
    selectModelText(focused, refName) {
      const input = this.$refs[refName]?.$el?.querySelector('input');
      if (!focused || !input) return;

      setTimeout(() => input.select(), 0);
    },
    /**
     * ข้อความในช่องยังเป็นชื่อตัวที่เลือกอยู่ทั้งก้อน (เพิ่งคลิก · เพิ่งกด Enter เลือก) ⇒ ตัวอักษรที่พิมพ์ = เริ่มคำค้นใหม่ แทนที่ทั้งช่อง
     * select() ใน selectModelText อย่างเดียวไม่พอ: ตอนเปิดรายการหน้าเว็บค้าง ~160ms (วาดบอทหลายสิบตัว) ปุ่มที่พิมพ์ช่วงนั้นมาก่อน timer
     * และหลังกด Enter เลือก ช่องยังโฟกัสอยู่ คลิกซ้ำจึงไม่มี focus ใหม่ ⇒ ต่อท้ายชื่อเดิมเป็น "540 test-kanunGPT-4o" แล้วค้นไม่เจอ
     */
    onModelKeydown(event, title) {
      const input = event.target;
      if (!input || input.tagName !== 'INPUT' || !title) return;
      if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

      if (input.value === title) input.select();
    },
    focusAskModel() {
      const select = this.$refs.askModelSelect;
      if (!this.canEdit || !select) return;

      select.$el?.scrollIntoView({ block: 'nearest' });
      select.focus?.();
      this.askModelMenu = true;
    },
    onCanvasNodeClick() {
      // แผงรายละเอียดถูกเรนเดอร์ใน GraphCanvas เอง (มี nodes/edges อยู่แล้ว)
      // ที่นี่ไม่ต้องทำอะไร — เก็บ handler ไว้ให้ผู้เรียกอื่นต่อยอดได้
    },
    /** ปุ่มดินสอบนแผงรายละเอียด — เปิด dialog แก้โหนดตัวเดิมที่แท็บ Nodes ใช้ */
    onCanvasEditNode(nodeKey) {
      if (!this.canEdit) return;

      const node = this.canvasNodes.find((n) => n.nodeKey === nodeKey);
      if (node) this.openNode(node);
    },
    /**
     * ปุ่ม 💾 Save บนหัวหน้ากราฟ — ลำดับเดียวกับ /save-stream (ไม่มีขั้นสรุป description ด้วย LLM ตามมติผู้ใช้):
     * 1. บันทึกชื่อกราฟ  2. สร้างเวอร์ชัน (เฉพาะเมื่อเนื้อหาเปลี่ยน)  3. re-embed โหนดที่ยังไม่มีเวกเตอร์ / ข้อความเปลี่ยน เป็นงานเบื้องหลัง
     * ขั้น 3 ไม่รอ — Save คืนปุ่มทันที ความคืบหน้าแสดงที่ชิปข้างปุ่ม
     */
    saveGraph() {
      if (!this.canEdit || this.savingName) return;

      this.savingName = true;
      const failed = () => {
        this.savingName = false;
        this.notify(this.$t('graphRagRequestFailed'), 'error');
      };

      this.$call.GraphRagUpdateGraph(
        { id: this.graph.id, name: this.graph.name, description: this.graph.description, graphType: this.graph.graphType },
        (res) => {
          if (res.status !== 0) {
            this.savingName = false;
            return this.notify(res.message, 'error');
          }

          return this.$call.GraphRagSnapshot(
            { graphKey: this.graphKey, note: this.$t('graphRagSaveNote') },
            (snap) => {
              this.savingName = false;
              if (snap.status !== 0) return this.notify(snap.message, 'error');

              // data ว่าง = เนื้อหาเท่าเวอร์ชันล่าสุด ไม่ได้เวอร์ชันใหม่ (ไม่ใช่ error)
              const savedText = snap.data?.versionNo ? this.$t('graphRagSavedVersion', { no: snap.data.versionNo }) : this.$t('graphRagSaved');
              // loadGraph โหลดรายการเวอร์ชันต่อให้เองเมื่อเลขล่าสุดเปลี่ยน
              if (snap.data) this.loadGraph();

              return this.startEmbedding(savedText);
            },
            failed
          );
        },
        failed
      );
    },
    notify(text, colour) {
      this.toast = { show: true, text, colour: colour || 'success' };
    },
    /// รับ event จาก component ลูกที่ส่ง { text, colour } มา
    onChildNotify(payload) {
      this.notify(payload?.text, payload?.colour);
    },
    fail() {
      this.saving = false;
      this.importing = false;
      this.exporting = false;
      this.savingConfig = false;
      this.notify(this.$t('graphRagRequestFailed'), 'error');
    },
    loadGraph() {
      this.$call.GraphRagGetGraph(
        this.graphKey,
        (res) => {
          if (res.status !== 0 || !res.data) return this.notify(res.message, 'error');

          this.graph = res.data;
          // โหลดกราฟใหม่ทุกครั้ง dropdown ต้องกลับมาชี้เวอร์ชันปัจจุบัน — เดิมตั้งแค่ครั้งแรก
          // หลัง Save ได้ v3 แล้วช่องยังค้างที่ "v2" ผู้ใช้เลยเห็นว่า Save ไม่ได้เวอร์ชันใหม่
          this.selectedVersion = res.data.currentVersionNo;

          // ลบกิ่ง/รวมกราฟ/นำเข้า/Snapshot ก็ mint เวอร์ชันแต่เรียกแค่ loadGraph
          // ถ้าเลขล่าสุดในรายการไม่ตรงกับกราฟ ต้องโหลดรายการใหม่ ไม่งั้น dropdown ขาดเวอร์ชันตรงกลาง (เช่นมี v1 v2 v4 ไม่มี v3)
          const latestListed = this.versions.length ? this.versions[0].versionNo : null;
          if (latestListed !== (res.data.currentVersionNo ?? null)) this.loadVersions();

          return undefined;
        },
        this.fail
      );
    },
    // ── โหนด ──────────────────────────────────────────────────────────
    onNodeSearch() {
      clearTimeout(this.nodeSearchTimer);
      this.nodeSearchTimer = setTimeout(() => {
        this.nodeOptions.page = 1;
        this.loadNodes();
      }, 400);
    },
    loadNodes() {
      this.loadingNodes = true;

      const query = new URLSearchParams({
        graphKey: this.graphKey,
        search: this.nodeSearch || '',
        page: this.nodeOptions.page,
        pageSize: this.nodeOptions.itemsPerPage,
        includeParents: true,
      }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          this.loadingNodes = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.nodes = res.data?.data || [];
          this.nodeTotal = res.data?.total || 0;
        },
        () => {
          this.loadingNodes = false;
          this.fail();
        }
      );
    },
    openNode(item) {
      this.nodeForm = item
        ? {
            nodeKey: item.nodeKey,
            label: item.label,
            secondaryLabel: item.secondaryLabel,
            nodeType: item.nodeType,
            description: item.description,
            attributesJson: item.attributesJson,
            aliasText: (item.aliases || []).join(', '),
            // ไม่มีช่องในฟอร์ม — ส่งค่าเดิมกลับไป ไม่งั้นแก้โหนดที่ถูกซ่อนแล้วโหนดโผล่กลับมาเอง
            isVisible: item.isVisible !== false,
            // โครงสร้าง — ค่าเดิมไว้เทียบตอนบันทึก (เปลี่ยนแม่ / เลื่อนลูกหลาน = ผ่าน MoveNode)
            originalType: item.nodeType,
            parentKey: item.parentNodeKey || null,
            originalParentKey: item.parentNodeKey || null,
            shiftDescendants: true,
          }
        : { nodeKey: '', label: '', secondaryLabel: '', nodeType: CONST_NODE_TYPE_GRAPH, description: '', attributesJson: '', aliasText: '', isVisible: true };

      this.nodeHierarchy = null;
      this.nodeParentSearch = '';
      this.nodeParentOptions = item?.parentNodeKey
        ? [{ nodeKey: item.parentNodeKey, label: item.parentLabel || item.parentNodeKey, nodeType: item.parentNodeType || '' }]
        : [];
      if (item) this.loadNodeHierarchy(item.nodeKey);

      // ชนิดใน Ontology เป็นตัวเลือกของช่อง Node Type ด้วย — โหลดครั้งแรกที่เปิดฟอร์ม
      if (!this.schemaTypes.length && !this.loadingSchema) this.loadSchema();
      this.showNode = true;
    },
    /** โหนดแม่ปัจจุบัน + แม่ของแม่ + พี่น้อง — ตัวเลือกและคำแนะนำของกล่องแก้โหนด */
    loadNodeHierarchy(nodeKey) {
      this.nodeHierarchyLoading = true;
      this.$call.GraphRagNodeHierarchy(
        this.graphKey,
        nodeKey,
        (res) => {
          // ปิด/เปิดโหนดอื่นไปแล้ว — คำตอบนี้ไม่ใช่ของฟอร์มตอนนี้
          if (this.nodeForm.nodeKey !== nodeKey) return;

          this.nodeHierarchyLoading = false;
          if (res.status !== 0 || !res.data) return;

          this.nodeHierarchy = res.data;
          const parentKey = res.data.parents?.[0]?.nodeKey || null;
          // ผู้ใช้ยังไม่ได้เลือกเอง ⇒ ตั้งค่าเดิมจากข้อมูลจริง (แถวจากผืนกราฟไม่มี parentNodeKey มาด้วย)
          if ((this.nodeForm.parentKey || null) === (this.nodeForm.originalParentKey || null)) this.nodeForm.parentKey = parentKey;
          this.nodeForm.originalParentKey = parentKey;
          this.nodeParentOptions = this.nodeParentBaseOptions();
        },
        () => {
          if (this.nodeForm.nodeKey === nodeKey) this.nodeHierarchyLoading = false;
        }
      );
    },
    /** ตัวเลือกตั้งต้นของช่องโหนดแม่ (ยังไม่ได้พิมพ์ค้น) — แม่ทั้งหมด · แม่ของแม่ · พี่น้อง */
    nodeParentBaseOptions() {
      const h = this.nodeHierarchy;
      const seen = new Set([this.nodeForm.nodeKey]);
      const list = [];
      for (const c of [...(h?.parents || []), h?.grandparent, ...(h?.siblings || []), this.nodeParentNode]) {
        if (!c || seen.has(c.nodeKey)) continue;
        seen.add(c.nodeKey);
        list.push(c);
      }

      return list;
    },
    searchNodeParents(term) {
      const search = String(term || '').trim();
      const selected = this.nodeParentNode;
      const keep = (list) => {
        const others = list.filter((n) => n.nodeKey !== this.nodeForm.nodeKey);
        return selected && !others.some((n) => n.nodeKey === selected.nodeKey) ? [selected, ...others] : others;
      };

      if (!search) {
        this.nodeParentLoading = false;
        this.nodeParentOptions = this.nodeParentBaseOptions();
        return;
      }

      this.nodeParentLoading = true;
      const query = new URLSearchParams({ graphKey: this.graphKey, search, page: 1, pageSize: CONST_NODE_SEARCH_PAGE_SIZE }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          if (search !== String(this.nodeParentSearch || '').trim()) return;

          this.nodeParentLoading = false;
          this.nodeParentOptions = keep(res.status === 0 ? res.data?.data || [] : []);
        },
        () => {
          this.nodeParentLoading = false;
        }
      );
    },
    /** เปิด dropdown ของช่องโหนดแม่ — ล้างคำค้นก่อน ตัวที่แนะนำจะขึ้นบนสุด */
    openNodeParentMenu() {
      this.nodeParentSearch = '';
      this.$refs.nodeParentSelect?.focus?.();
      this.nodeParentMenu = true;
    },
    /** ระดับของแถวไม่ต่อกับโหนดแม่ — ไม่มีแม่ไม่เตือน (ไม่รู้ว่าควรอยู่ใต้ใคร) */
    rowLevelMismatch(item) {
      const level = headingLevelOf(item.nodeType);
      if (!level || !item.parentNodeKey) return false;

      return headingLevelOf(item.parentNodeType) !== level - 1;
    },
    /**
     * บันทึกกล่องแก้โหนด
     * - เปลี่ยนแม่ / เลื่อนลูกหลาน: ตรวจด้วย MoveNode dryRun ก่อน (วงวน ฯลฯ ⇒ ไม่บันทึกอะไรเลย)
     *   → Save ช่องอื่นด้วยชนิดเดิม → MoveNode จริงด้วยชนิดใหม่ (ลำดับนี้เพราะ MoveNode เขียน lineage ลง attributes —
     *   ถ้า Save ทีหลัง attributes ในฟอร์มจะทับ lineage ใหม่ · ถ้า Save ด้วยชนิดใหม่ก่อน ระยะเลื่อนของลูกหลานจะเป็น 0)
     * - นอกนั้น: Save ครั้งเดียวแบบเดิม
     */
    saveNode() {
      if (this.nodeNeedsMove) {
        this.saveNodeWithHierarchy();
        return;
      }

      this.saving = true;

      const request = {
        graphKey: this.graphKey,
        nodeKey: this.nodeForm.nodeKey,
        label: this.nodeForm.label,
        secondaryLabel: this.nodeForm.secondaryLabel,
        nodeType: this.nodeForm.nodeType,
        description: this.nodeForm.description,
        attributesJson: this.nodeForm.attributesJson,
        isVisible: this.nodeForm.isVisible !== false,
        aliases: (this.nodeForm.aliasText || '')
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a.length > 0),
      };

      this.$call.GraphRagSaveNode(
        request,
        (res) => {
          this.saving = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.showNode = false;
          this.notify(this.$t('graphRagSaved'), 'success');
          this.loadGraph();
          if (this.showNodeManager) this.loadNodes();
          if (this.canvasOn) this.loadCanvas();
        },
        this.fail
      );
    },
    /** ตัวเลือกชนิดของแถว/ฟอร์ม — ค่าปัจจุบันที่ไม่อยู่ในรายการต้องยังเลือกค้างไว้ได้ */
    nodeTypeOptionsFor(current) {
      const items = this.nodeTypeBaseOptions;
      return current && !items.some((i) => i.value === current) ? [...items, { value: current, title: current }] : items;
    },
    /**
     * เปลี่ยนชนิดโหนดจากแถวในตาราง — ส่งค่าเดิมของแถวครบทุกช่อง (Save เขียนทับทั้งแถว) เปลี่ยนแค่ nodeType
     * เปลี่ยนเฉพาะโหนดนี้ ลูกหลานไม่เลื่อนระดับตาม (ปุ่ม "ย้าย…" ทำให้)
     */
    changeNodeType(item, nodeType) {
      if (!this.canEdit || !nodeType || nodeType === item.nodeType || this.nodeTypeSaving[item.nodeKey]) return;

      this.nodeTypeSaving = { ...this.nodeTypeSaving, [item.nodeKey]: true };
      const done = () => {
        const next = { ...this.nodeTypeSaving };
        delete next[item.nodeKey];
        this.nodeTypeSaving = next;
      };

      this.$call.GraphRagSaveNode(
        {
          graphKey: this.graphKey,
          nodeKey: item.nodeKey,
          label: item.label,
          secondaryLabel: item.secondaryLabel,
          nodeType,
          description: item.description,
          attributesJson: item.attributesJson,
          isVisible: item.isVisible !== false,
          aliases: item.aliases || [],
        },
        (res) => {
          done();
          if (res.status !== 0) {
            this.notify(res.message, 'error');
            return;
          }

          item.nodeType = res.data?.nodeType || nodeType;
          this.notify(this.$t('graphRagNodeTypeChanged', { name: item.label || item.nodeKey, type: item.nodeType }), 'success');
          this.loadGraph();
          if (this.canvasOn) this.loadCanvas();
        },
        () => {
          done();
          this.fail();
        }
      );
    },
    saveNodeWithHierarchy() {
      const f = this.nodeForm;
      const moveRequest = {
        graphKey: this.graphKey,
        nodeKey: f.nodeKey,
        newParentNodeKey: this.nodeParentChanged ? f.parentKey : null,
        nodeType: f.nodeType,
        adjustLevels: this.showShiftDescendants && !!f.shiftDescendants,
      };
      const saveRequest = {
        graphKey: this.graphKey,
        nodeKey: f.nodeKey,
        label: f.label,
        secondaryLabel: f.secondaryLabel,
        nodeType: f.originalType,
        description: f.description,
        attributesJson: f.attributesJson,
        isVisible: f.isVisible !== false,
        aliases: (f.aliasText || '')
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a.length > 0),
      };
      const stop = (message) => {
        this.saving = false;
        this.notify(message || this.$t('graphRagRequestFailed'), 'error');
      };
      const refresh = () => {
        this.loadGraph();
        if (this.showNodeManager) this.loadNodes();
        if (this.showEdgeManager) this.loadEdges();
        if (this.canvasOn) this.loadCanvas();
      };

      this.saving = true;
      this.$call.GraphRagMoveNode(
        { ...moveRequest, dryRun: true },
        (check) => {
          if (check.status !== 0) return stop(check.message);

          this.$call.GraphRagSaveNode(
            saveRequest,
            (saved) => {
              if (saved.status !== 0) return stop(saved.message);

              this.$call.GraphRagMoveNode(
                { ...moveRequest, dryRun: false },
                (moved) => {
                  this.saving = false;
                  if (moved.status !== 0) {
                    // ช่องอื่นบันทึกไปแล้ว — โหลดใหม่ให้เห็นสถานะจริง
                    refresh();
                    return this.notify(moved.message, 'error');
                  }

                  this.showNode = false;
                  this.notify(
                    this.$t('graphRagNodeSavedMoved', {
                      name: moved.data.nodeLabel || moved.data.nodeKey,
                      parent: moved.data.newParentLabel || moved.data.newParentNodeKey || '—',
                      type: moved.data.newNodeType || '—',
                    }),
                    'success'
                  );
                  refresh();
                  return undefined;
                },
                () => stop()
              );
              return undefined;
            },
            () => stop()
          );
          return undefined;
        },
        () => stop()
      );
    },
    deleteSelectedNodes() {
      this.$call.GraphRagDeleteNodes(
        { graphKey: this.graphKey, nodeKeys: this.selectedNodeKeys },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.selectedNodeKeys = [];
          this.notify(this.$t('graphRagDeleted'), 'success');
          this.loadNodes();
        },
        this.fail
      );
    },

    // ── เส้นเชื่อม ────────────────────────────────────────────────────
    loadEdges() {
      this.loadingEdges = true;
      this.$call.GraphRagGetEdges(
        this.graphKey,
        (res) => {
          this.loadingEdges = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.edges = res.data || [];
        },
        () => {
          this.loadingEdges = false;
          this.fail();
        }
      );
    },
    openEdge(item) {
      const attrs = parseJsonObject(item?.attributesJson);
      const { note, ...extraAttrs } = attrs;

      this.edgeForm = {
        id: item?.id || 0,
        sourceNodeKey: item?.sourceNodeKey || '',
        targetNodeKey: item?.targetNodeKey || '',
        relation: item?.relation || '',
        note: typeof note === 'string' ? note : '',
        // key อื่นใน attributesJson ของเส้นเดิม — ต้องส่งกลับครบ ไม่งั้นแก้ Note แล้วค่าอื่นหาย
        extraAttrs,
        bidirectional: false,
        bidirTouched: false,
      };
      this.edgeSourceSearch = '';
      this.edgeTargetSearch = '';
      this.edgeSourceOptions = [];
      this.edgeTargetOptions = [];
      this.prefillEdgeEnd('source', this.edgeForm.sourceNodeKey, item?.sourceLabel);
      this.prefillEdgeEnd('target', this.edgeForm.targetNodeKey, item?.targetLabel);

      // รายการ relation / ตารางอ้างอิงมาจาก Ontology — โหลดครั้งแรกที่เปิดฟอร์ม
      if (!this.schemaTypes.length && !this.loadingSchema) this.loadSchema();
      this.showEdge = true;
    },
    /** ใส่โหนดที่กำหนดมาแล้วลงช่อง (แก้เส้นเดิม / Add Edge from Here) แล้วค้นหาชนิดของมันมาเติม — ไม่เจอก็คงค่า key ไว้ */
    prefillEdgeEnd(which, nodeKey, label) {
      if (!nodeKey) return;

      this.setEdgeOptions(which, [{ nodeKey, label: label || nodeKey, nodeType: '' }]);
      const query = new URLSearchParams({ graphKey: this.graphKey, search: nodeKey, page: 1, pageSize: CONST_NODE_SEARCH_PAGE_SIZE }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          const found = res.status === 0 ? (res.data?.data || []).find((n) => n.nodeKey === nodeKey) : null;
          const current = which === 'source' ? this.edgeForm.sourceNodeKey : this.edgeForm.targetNodeKey;
          if (found && current === nodeKey) this.setEdgeOptions(which, [found]);
        },
        () => {}
      );
    },
    setEdgeOptions(which, list) {
      if (which === 'source') this.edgeSourceOptions = list;
      else this.edgeTargetOptions = list;
    },
    queueEdgeNodeSearch(which, term) {
      clearTimeout(this.edgeSearchTimers[which]);
      const selected = which === 'source' ? this.edgeSourceNode : this.edgeTargetNode;
      // ข้อความเท่ากับชื่อของโหนดที่เลือกอยู่ = Vuetify เติมหลังเลือก ไม่ต้องค้นซ้ำ
      if (selected && term === (selected.label || selected.nodeKey)) return;

      this.edgeSearchTimers[which] = setTimeout(() => this.searchEdgeNodes(which, term), CONST_NODE_SEARCH_DEBOUNCE_MS);
    },
    /** ค้นโหนดด้วย endpoint เดิมของแผง Graph Commands (search = label / secondary label / node key) */
    searchEdgeNodes(which, term) {
      const search = String(term || '').trim();
      const selected = which === 'source' ? this.edgeSourceNode : this.edgeTargetNode;
      const keepSelected = (list) => (selected && !list.some((n) => n.nodeKey === selected.nodeKey) ? [selected, ...list] : list);
      const setLoading = (value) => {
        if (which === 'source') this.edgeSourceLoading = value;
        else this.edgeTargetLoading = value;
      };

      if (!search) {
        setLoading(false);
        this.setEdgeOptions(which, keepSelected([]));
        return;
      }

      setLoading(true);
      const query = new URLSearchParams({ graphKey: this.graphKey, search, page: 1, pageSize: CONST_NODE_SEARCH_PAGE_SIZE }).toString();

      this.$call.GraphRagGetNodes(
        query,
        (res) => {
          // คำตอบของคำค้นเก่าที่มาช้า — ทิ้ง ไม่งั้นรายการกระโดดกลับ
          const latest = String((which === 'source' ? this.edgeSourceSearch : this.edgeTargetSearch) || '').trim();
          if (search !== latest) return;

          setLoading(false);
          this.setEdgeOptions(which, keepSelected(res.status === 0 ? res.data?.data || [] : []));
        },
        () => setLoading(false)
      );
    },
    /** บรรทัดใต้ช่องโหนด — ชนิดของโหนดที่เลือก (ตัวกรอง relation ตามกติกา From/To) */
    edgeEndHint(node) {
      if (!node) return '';

      return node.nodeType ? this.$t('graphRagEdgeEndType', { type: node.nodeType }) : node.nodeKey;
    },
    /** ⇄ สลับต้นทางกับปลายทาง */
    swapEdgeEnds() {
      const sourceKey = this.edgeForm.sourceNodeKey;
      const sourceOptions = this.edgeSourceOptions;

      this.edgeSourceOptions = this.edgeTargetOptions;
      this.edgeTargetOptions = sourceOptions;
      this.edgeForm.sourceNodeKey = this.edgeForm.targetNodeKey;
      this.edgeForm.targetNodeKey = sourceKey;
    },
    edgeNoteOf(item) {
      const note = parseJsonObject(item?.attributesJson).note;

      return typeof note === 'string' ? note : '';
    },
    saveEdge() {
      const form = this.edgeForm;
      const attrs = { ...(form.extraAttrs || {}) };
      const note = String(form.note || '').trim();
      if (note) attrs.note = note;

      const payload = {
        graphKey: this.graphKey,
        id: form.id || 0,
        sourceNodeKey: form.sourceNodeKey || '',
        targetNodeKey: form.targetNodeKey || '',
        relation: String(form.relation || '').trim(),
        attributesJson: Object.keys(attrs).length ? JSON.stringify(attrs) : '',
      };
      const reverse = !payload.id && form.bidirectional;

      const done = (savedReverse) => {
        this.saving = false;
        this.showEdge = false;
        this.notify(savedReverse ? this.$t('graphRagEdgeSavedBoth') : this.$t('graphRagSaved'), 'success');
        this.loadGraph();
        if (this.showEdgeManager) this.loadEdges();
        if (this.canvasOn) this.loadCanvas();
      };

      this.saving = true;
      this.$call.GraphRagSaveEdge(
        payload,
        (res) => {
          if (res.status !== 0) {
            this.saving = false;
            return this.notify(res.message, 'error');
          }
          if (!reverse) return done(false);

          // bidirectional = เพิ่มเส้นย้อนกลับ relation เดียวกัน (GraphModel.AddEdge ของ nexa ก็เติม adjacency สองทางแบบนี้)
          // มีเส้นย้อนกลับอยู่แล้ว (Duplicate) ถือว่าสำเร็จ
          return this.$call.GraphRagSaveEdge(
            { ...payload, sourceNodeKey: payload.targetNodeKey, targetNodeKey: payload.sourceNodeKey },
            (back) => {
              if (back.status !== 0 && back.status !== CONST_API_STATUS_DUPLICATE) {
                this.notify(back.message, 'warning');
                return done(false);
              }

              return done(true);
            },
            () => done(false)
          );
        },
        (err) => {
          this.saving = false;
          this.fail(err);
        }
      );
    },
    deleteEdge(item) {
      this.$call.GraphRagDeleteEdges(
        { graphKey: this.graphKey, edgeIds: [item.id] },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagDeleted'), 'success');
          this.loadEdges();
        },
        this.fail
      );
    },

    // ── ontology ─────────────────────────────────────────────────────
    loadSchema() {
      this.loadingSchema = true;
      this.$call.GraphRagGetSchemaTypes(
        this.graphKey,
        (res) => {
          this.loadingSchema = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.schemaTypes = res.data || [];
        },
        () => {
          this.loadingSchema = false;
          this.fail();
        }
      );
    },
    /**
     * สีสำรองของชนิดโหนดที่ ontology ไม่มี — ล้มก็แค่กลับไปใช้สีเทาเดิม ไม่ต้องแจ้งผู้ใช้ (ผืนกราฟยังวาดได้)
     * เรียกใหม่หลังแก้ ontology เพราะ palette เริ่มนับจากจำนวนชนิดใน ontology (แบบ nexa)
     */
    loadTypeColors() {
      if (!this.graphKey) return;

      const graphKey = this.graphKey;
      this.$call.GraphRagGetNodeTypeColors(
        graphKey,
        (res) => {
          if (graphKey !== this.graphKey || res.status !== 0) return;

          this.canvasAutoColors = res.data || {};
        },
        () => {}
      );
    },
    openSchema(item) {
      // (kind, typeId) เป็นคีย์ของแถว แก้แล้วจะกลายเป็นการสร้างแถวใหม่ จึงล็อกตอนแก้
      // ค่าเสริมใน attributeSchemaJson — แยก key ที่ฟอร์มรู้จักออกมา ที่เหลือเก็บไว้ส่งกลับครบ
      const { description, group, cost, from_types: fromTypes, to_types: toTypes, ...extraAttrs } = parseJsonObject(item?.attributeSchemaJson);

      this.schemaForm = {
        typeKind: item?.typeKind || 'node',
        typeId: item?.typeId || '',
        label: item?.label || '',
        color: item?.color || '#aaaaaa',
        sortOrder: item?.sortOrder || 0,
        locked: !!item,
        description: typeof description === 'string' ? description : '',
        group: typeof group === 'string' ? group : '',
        cost: Number.isInteger(cost) ? cost : CONST_RELATION_COST_DEFAULT,
        fromTypes: asStringList(fromTypes),
        toTypes: asStringList(toTypes),
        extraAttrs,
      };

      this.showSchema = true;
    },
    /** Traversal Cost: จำนวนเต็ม ≥ 0 — 0 = เดินผ่านฟรี */
    schemaCostRule(value) {
      const text = String(value ?? '').trim();

      return (text !== '' && /^\d+$/.test(text)) || this.$t('graphRagRelCostInvalid');
    },
    /** สรุปค่าเสริมในตาราง Ontology — relation: กลุ่ม · ต้นทุน · from → to · node: คำอธิบาย */
    schemaDetailsText(item) {
      const attrs = parseJsonObject(item?.attributeSchemaJson);
      if (item?.typeKind !== 'relation') return attrs.description || '';

      const from = asStringList(attrs.from_types).join(', ') || '*';
      const to = asStringList(attrs.to_types).join(', ') || '*';
      const cost = Number.isInteger(attrs.cost) ? attrs.cost : CONST_RELATION_COST_DEFAULT;

      return `${attrs.group || CONST_RELATION_GROUP_DEFAULT} · cost ${cost} · ${from} → ${to}`;
    },
    saveSchema() {
      const form = this.schemaForm;
      const attrs = { ...(form.extraAttrs || {}) };
      const description = String(form.description || '').trim();
      if (description) attrs.description = description;

      if (form.typeKind === 'relation') {
        if (this.schemaCostRule(form.cost) !== true) return this.notify(this.$t('graphRagRelCostInvalid'), 'error');

        // ชื่อ key เดียวกับที่ GraphSchemaModel อ่าน (RelayCost / AllowedRelationTypes / group)
        attrs.group = String(form.group || '').trim() || CONST_RELATION_GROUP_DEFAULT;
        attrs.cost = Number.parseInt(String(form.cost).trim(), 10);
        attrs.from_types = asStringList(form.fromTypes);
        attrs.to_types = asStringList(form.toTypes);
      }

      this.saving = true;
      this.$call.GraphRagSaveSchemaType(
        {
          graphKey: this.graphKey,
          typeKind: form.typeKind,
          typeId: form.typeId,
          label: form.label,
          color: form.color,
          sortOrder: Number(form.sortOrder) || 0,
          // ส่งทุกครั้ง — backend เขียนทับทั้งช่อง ถ้าไม่ส่ง แก้ Label/สีแล้วค่าเสริมเดิมหาย
          attributeSchemaJson: Object.keys(attrs).length ? JSON.stringify(attrs) : '',
        },
        (res) => {
          this.saving = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.showSchema = false;
          this.notify(this.$t('graphRagSaved'), 'success');
          this.loadSchema();
          if (this.canvasOn) this.loadTypeColors();
        },
        this.fail
      );
    },
    deleteSchema(item) {
      this.$call.GraphRagDeleteSchemaType(
        { graphKey: this.graphKey, typeKind: item.typeKind, typeId: item.typeId },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagDeleted'), 'success');
          this.loadSchema();
          if (this.canvasOn) this.loadTypeColors();
        },
        this.fail
      );
    },

    // ── ชุดตัวกรอง ───────────────────────────────────────────────────
    loadFilters() {
      this.loadingFilters = true;
      this.$call.GraphRagGetFilters(
        this.graphKey,
        (res) => {
          this.loadingFilters = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.filters = res.data || [];
        },
        () => {
          this.loadingFilters = false;
          this.fail();
        }
      );
    },
    openFilter(item) {
      this.filterForm = item
        ? { id: item.id, name: item.name, filterSpecJson: item.filterSpecJson }
        : { id: 0, name: '', filterSpecJson: '' };

      this.showFilter = true;
    },
    saveFilter() {
      this.saving = true;
      this.$call.GraphRagSaveFilter(
        { graphKey: this.graphKey, ...this.filterForm },
        (res) => {
          this.saving = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.showFilter = false;
          this.notify(this.$t('graphRagSaved'), 'success');
          this.loadFilters();
        },
        this.fail
      );
    },
    activateFilter(item) {
      this.$call.GraphRagActivateFilter(
        { graphKey: this.graphKey, filterId: item.id },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagSaved'), 'success');
          this.loadFilters();
        },
        this.fail
      );
    },

    // ── ค่าการค้นหา ──────────────────────────────────────────────────
    loadConfig() {
      this.$call.GraphRagGetConfig(
        this.graphKey,
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.config = res.data || {};
          this.savedConfig = { ...this.config };

          // ค่าเริ่มต้นของปุ่ม Canvas View มาจาก config ของกราฟ (nexa: graph_canvas_render_enabled)
          if (!this.configLoaded) {
            this.configLoaded = true;
            this.canvasOn = !!this.config.graph_canvas_render_enabled;
            if (this.canvasOn) this.loadCanvas();
          }
        },
        this.fail
      );
    },
    saveConfig() {
      this.savingConfig = true;
      this.$call.GraphRagSaveConfig(
        this.graphKey,
        this.config,
        (res) => {
          this.savingConfig = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.config = res.data || this.config;
          this.savedConfig = { ...this.config };
          this.canvasOn = !!this.config.graph_canvas_render_enabled;
          if (this.canvasOn) this.loadCanvas();
          this.notify(this.$t('graphRagSaved'), 'success');
        },
        this.fail
      );
    },
    /**
     * เปลี่ยนโมเดลจากช่องบนแถบกราฟ — บันทึกเฉพาะ key model ทับ config ที่บันทึกล่าสุด
     * ไม่ส่ง this.config ทั้งก้อน เพราะอาจมีค่าที่แก้ใน Search Settings แล้วปิดไปโดยยังไม่กด Save
     * "ไม่ตั้ง" ส่ง null ⇒ backend ลบ key ออก (GraphRagSettingsBal) · ล้มเหลวคืนค่าเดิมในช่อง
     */
    saveAskModel(value) {
      const model = value === '' || value == null ? null : String(value);
      if (!this.canEdit || model === this.askModel) return;

      const previous = this.askModel;
      this.askModel = model;
      this.savingAskModel = true;

      const restore = () => {
        this.savingAskModel = false;
        this.askModel = previous;
      };

      this.$call.GraphRagSaveConfig(
        this.graphKey,
        { ...this.savedConfig, model },
        (res) => {
          if (res.status !== 0) {
            restore();
            return this.notify(res.message, 'error');
          }

          this.savingAskModel = false;
          this.savedConfig = { ...(res.data || { ...this.savedConfig, model }) };
          this.askModel = this.savedConfig.model;
          this.notify(this.$t('graphRagSaved'), 'success');
        },
        () => {
          restore();
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },
    /** รายการโมเดลของช่อง "โมเดลที่ใช้ตอบคำถาม" — โหลดแล้ว/กำลังโหลดไม่ยิงซ้ำ · Import Wizard โหลดชุดเดียวกันไว้แล้วก็ใช้ต่อได้เลย */
    loadAskModels() {
      if (this.askModelsLoaded || this.askModelsLoading) return;
      if (this.wiz.models.length) {
        this.askModels = this.wiz.models.slice();
        this.askModelsLoaded = true;
        return;
      }

      // GetTableGPTModel คืน modelId ที่เข้ารหัสแล้ว ใช้เป็นค่า model ไม่ได้ — ต้องใช้ GraphRagGetLlmModels เหมือน Import Wizard
      this.askModelsLoading = true;
      this.$call.GraphRagGetLlmModels(
        (res) => {
          this.askModelsLoading = false;
          if (res.status !== 0) return;

          this.askModels = (res.data || []).map((m) => ({ id: m.id, name: m.name || '#' + m.id, modelDisplay: m.modelDisplay })).filter((m) => m.id);
          this.askModelsLoaded = true;
        },
        () => {
          // ล้มเหลวเงียบเหมือน wizLoadModels — เปิด dialog ครั้งหน้าจะลองโหลดใหม่
          this.askModelsLoading = false;
        }
      );
    },

    // ── สถานะเวกเตอร์ ────────────────────────────────────────────────
    /** อ่านสถานะเวกเตอร์ของกราฟ — เงียบเมื่อล้มเหลว เพราะเป็นข้อมูลประกอบไม่ใช่เนื้อหาหลักของกล่อง */
    loadEmbeddingStatus() {
      this.$call.GraphRagEmbeddingStatus(
        this.graphKey,
        (res) => {
          if (res.status !== 0) return;

          this.embedding = res.data;
        },
        () => {}
      );
    },
    /**
     * re-embed โหนดที่ยังไม่มีเวกเตอร์ หรือ label/description เปลี่ยนไปจากตอน embed ครั้งก่อน — ใช้ทั้งปุ่ม Save และปุ่มสร้างเวกเตอร์ในค่าตั้ง
     * prefix = ข้อความของขั้นก่อนหน้า (เช่น "บันทึกเรียบร้อย") ต่อหน้าผลของขั้นนี้ ให้ผู้ใช้เห็นผลรวมใน toast เดียว
     * vector store ยังไม่เปิด / ไม่มีโหนดต้องทำ = ไม่ใช่ error — บอกเฉย ๆ แล้วจบ
     */
    startEmbedding(prefix) {
      const say = (text, colour) => this.notify(prefix ? `${prefix} · ${text}` : text, colour);

      if (this.embedJob) return say(this.$t('graphRagEmbedRunning', { processed: this.embedJob.processedUnits || 0, total: this.embedJob.totalUnits || 0 }), 'info');

      return this.$call.GraphRagEmbeddingStatus(
        this.graphKey,
        (res) => {
          if (res.status !== 0) return say(res.message, 'error');

          this.embedding = res.data;
          // บอกทุกครั้งว่าข้าม — ผู้ใช้กด Save แล้วต้องรู้ว่าการค้นหาเชิงความหมายยังไม่ได้ค่าใหม่
          if (!res.data?.vectorStoreReady) return say(this.$t('graphRagEmbedSkippedStore'), 'info');
          if (!res.data.missingCount) return prefix ? this.notify(prefix, 'success') : say(this.$t('graphRagEmbedUpToDate'), 'info');

          return this.$call.GraphRagStartEmbedding(
            { graphKey: this.graphKey, mode: 'missing' },
            (start) => {
              // Duplicate = มีงานของกราฟนี้ทำอยู่แล้ว (เช่นอีกแท็บ) — ไม่ใช่ error
              if (start.status !== 0) return say(start.message, start.status === CONST_API_STATUS_DUPLICATE ? 'info' : 'error');
              if (!start.data?.jobId || start.data.status === 'succeeded') return prefix ? this.notify(prefix, 'success') : say(this.$t('graphRagEmbedUpToDate'), 'info');

              this.embedJob = start.data;
              this.embedCancelling = false;
              say(this.$t('graphRagEmbedRunning', { processed: 0, total: start.data.totalUnits || 0 }), 'success');

              return this.pollEmbedding();
            },
            () => say(this.$t('graphRagRequestFailed'), 'error')
          );
        },
        () => say(this.$t('graphRagRequestFailed'), 'error')
      );
    },
    /** ติดตามงาน re-embed จนจบ — ใช้ GetJobStatus ตัวเดียวกับงานนำเข้า (อ่านได้ทุกชนิดงานของกราฟนี้) */
    pollEmbedding() {
      clearTimeout(this.embedPollTimer);

      this.embedPollTimer = setTimeout(() => {
        const job = this.embedJob;
        if (!job) return;

        this.$call.GraphRagGetIngestJob(
          this.graphKey,
          job.jobId,
          (res) => {
            if (this.embedJob?.jobId !== job.jobId) return; // ถูกแทนด้วยงานใหม่ / ออกจากหน้าไปแล้ว
            if (res.status !== 0 || !res.data) return this.pollEmbedding();

            this.embedJob = res.data;
            if (res.data.status === 'queued' || res.data.status === 'running') return this.pollEmbedding();

            return this.finishEmbedding(res.data);
          },
          () => this.pollEmbedding()
        );
      }, CONST_EMBED_POLL_MS);
    },
    finishEmbedding(job) {
      this.embedJob = null;
      this.embedCancelling = false;
      this.loadEmbeddingStatus();

      const count = job.processedUnits || 0;
      if (job.status === 'succeeded') return this.notify(this.$t('graphRagEmbedDone', { count }), 'success');
      if (job.status === 'partial') return this.notify(this.$t('graphRagEmbedPartial', { count, failed: job.failedUnits || 0 }), 'warning');
      if (job.status === 'cancelled') return this.notify(this.$t('graphRagEmbedCancelled', { count }), 'info');

      return this.notify(job.errorMessage || this.$t('graphRagEmbedFailed'), 'error');
    },
    cancelEmbedding() {
      if (!this.embedJob || this.embedCancelling) return;

      this.embedCancelling = true;
      this.$call.GraphRagCancelEmbedding(
        { graphKey: this.graphKey, jobId: this.embedJob.jobId },
        (res) => {
          if (res.status !== 0) {
            this.embedCancelling = false;
            this.notify(res.message, 'error');
          }
          // ไม่ปิดชิปเอง — รอ poll เห็นสถานะ cancelled จากงานจริง (เวกเตอร์ที่เขียนไปแล้วยังอยู่)
        },
        () => {
          this.embedCancelling = false;
          this.notify(this.$t('graphRagRequestFailed'), 'error');
        }
      );
    },

    // ── เวอร์ชัน / นำเข้า / ส่งออก ───────────────────────────────────
    loadVersions() {
      this.loadingVersions = true;
      this.$call.GraphRagGetVersions(
        this.graphKey,
        50,
        (res) => {
          this.loadingVersions = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.versions = res.data || [];
        },
        () => {
          this.loadingVersions = false;
          this.fail();
        }
      );
    },
    exportGraph() {
      this.exporting = true;
      this.$call.GraphRagExport(
        this.graphKey,
        (res) => {
          this.exporting = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          // สร้างไฟล์ฝั่ง client จาก JSON ที่ได้ ไม่ต้องมี endpoint ดาวน์โหลดแยก
          const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.graphKey}_graph.json`;
          link.click();
          URL.revokeObjectURL(url);
        },
        this.fail
      );
    },
    importGraph() {
      const file = Array.isArray(this.importFile) ? this.importFile[0] : this.importFile;
      if (!file) return this.notify(this.$t('Required'), 'error');

      this.importing = true;
      this.importWarnings = [];

      const formData = new FormData();
      formData.append('file', file);
      formData.append('graphKey', this.graphKey);
      formData.append('mode', this.importMode);

      return this.$call.GraphRagImport(
        formData,
        (res) => {
          this.importing = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.importWarnings = res.data?.warnings || [];
          this.notify(this.$t('graphRagSaved'), 'success');

          // ปิดกล่องเฉพาะตอนที่ไม่มีอะไรต้องให้ผู้ใช้อ่าน
          if (this.importWarnings.length === 0) this.showImport = false;

          this.loadGraph();
          return undefined;
        },
        this.fail
      );
    },
    snapshot() {
      this.saving = true;
      this.$call.GraphRagSnapshot(
        { graphKey: this.graphKey, note: this.snapshotNote },
        (res) => {
          this.saving = false;
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.showSnapshot = false;
          this.snapshotNote = '';

          // Data ว่าง = เนื้อหาไม่เปลี่ยน จึงไม่ได้เวอร์ชันใหม่ ต้องบอกให้ชัด
          this.notify(res.data ? this.$t('graphRagSaved') : this.$t('graphRagNoChange'), res.data ? 'success' : 'info');
          this.loadGraph();
        },
        this.fail
      );
    },
    revertTo(item) {
      if (!globalThis.confirm(this.$t('graphRagRevertConfirm'))) return;

      this.$call.GraphRagRevert(
        { graphKey: this.graphKey, versionNo: item.versionNo },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          this.notify(this.$t('graphRagSaved'), 'success');
          this.loadGraph();
        },
        this.fail
      );
    },

    // ── Import Wizard (S12-P1) — เจ้าของ state/คิว/timer อยู่ที่นี่ตามมติ D10 ──
    /** ทางเดียวที่ ImportWizard และไฟล์ลูกของมันเปลี่ยน state ได้ */
    wizPatch(fields) {
      Object.assign(this.wiz, fields || {});
    },
    wizBeforeUnload(event) {
      if (!this.wiz.converting) return undefined;

      event.preventDefault();
      event.returnValue = this.$t('graphRagWizBeforeUnload');

      return event.returnValue;
    },
    /** เปิดกล่องนำเข้า — งานที่ยังเดินอยู่ต้องกลับไปที่เดิม ไม่ใช่เริ่มขั้น 1 ใหม่ */
    wizOnOpen() {
      if (this.wiz.converting || this.wiz.creating || this.wiz.jobId) {
        // watcher ตอนปิดล้าง ingestParentNodeKey ทิ้ง ต้องคืนปลายทางของงานที่ยังค้างอยู่
        this.ingestParentNodeKey = this.wiz.parentNodeKey;
        if (this.wiz.jobId) this.wiz.step = 5;
        this.wiz.maxStep = Math.max(this.wiz.maxStep, this.wiz.step);
      } else {
        this.wiz.parentNodeKey = this.ingestParentNodeKey;
      }

      if (!this.wiz.models.length) this.wizLoadModels();
      else this.wizSyncModel();
    },
    // GetTableGPTModel คืน modelId ที่เข้ารหัสแล้ว ลิสต์จึงว่างเสมอ — ต้องใช้ GraphRagGetLlmModels
    wizLoadModels() {
      this.$call.GraphRagGetLlmModels(
        (res) => {
          if (res.status !== 0) return;

          this.wiz.models = (res.data || []).map((m) => ({ id: m.id, name: m.name || '#' + m.id, modelDisplay: m.modelDisplay })).filter((m) => m.id);
          this.wizSyncModel();
        },
        () => {}
      );
    },
    /**
     * โมเดลของ wizard (Refine ขั้น 2 · Suggest with AI ขั้น 4 · StartExtract) — ค่าเริ่มต้น = Answer model ของกราฟบน toolbar
     * กราฟไม่ได้ตั้ง / บอทที่ตั้งไว้ไม่อยู่ในลิสต์ (ถูกลบ · ไม่มีสิทธิ์เห็น) ⇒ ตัวแรกในลิสต์
     * ผู้ใช้เปลี่ยนเองที่ขั้น 5 (modelPicked) ⇒ ใช้ค่านั้นทั้งรอบ ไม่บันทึกกลับเป็น Answer model ของกราฟ
     * งานที่กำลังสร้างไม่แตะ — StartExtract ได้ modelId ไปแล้ว
     */
    wizSyncModel() {
      const hit = this.wiz.models.find((m) => String(m.id) === this.askModel);

      this.wiz.graphModelId = hit ? hit.id : null;
      this.wiz.graphModelSet = !!this.askModel;
      if (this.wiz.creating || this.wiz.modelPicked) return;

      if (hit) this.wiz.modelId = hit.id;
      else this.wiz.modelId = this.wiz.models.length ? this.wiz.models[0].id : null;
    },
    /** คีย์ของไฟล์ = slug ของ {folderPath}::{name} — ห้ามใช้ index ของ array เพราะคิวเรียงใหม่ได้ */
    wizSlug(text) {
      return String(text || '').replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '') || 'x';
    },
    wizExt(name) {
      const dot = String(name || '').lastIndexOf('.');

      return dot < 0 ? '' : String(name).slice(dot).toLowerCase();
    },
    /**
     * โฟลเดอร์ที่ไฟล์นั้นอยู่ (6-A)
     *
     * picker ของ <input webkitdirectory> ตั้ง webkitRelativePath ให้เอง แต่ File ที่ได้จาก
     * webkitGetAsEntry().file() ของการลากวาง "ไม่มี" ค่านั้น ⇒ ImportWizard ติด wizRelativePath มาให้แทน
     * ตัดเฉพาะชื่อไฟล์ท้ายสุดด้วย lastIndexOf ไม่ใช่ replace เพราะโฟลเดอร์อาจชื่อซ้ำกับไฟล์
     */
    wizFolderPathOf(file) {
      const relative = file.webkitRelativePath || file.wizRelativePath || '';
      const cut = String(relative).lastIndexOf('/');

      return cut < 0 ? '' : String(relative).slice(0, cut);
    },
    /**
     * สลับ Files ↔ Folder — ลอก switchMode ของ nexa (public/index.html:19863)
     * ทั้งคิว ผังโฟลเดอร์ และค่าที่จับคู่ไว้ต้องถูกล้างหมด ไม่งั้นจะเหลือแถวของโหมดเก่าปนมา
     * ระหว่างแปลงห้ามสลับ — ปุ่มฝั่ง UI disabled อยู่แล้ว ตรงนี้กันอีกชั้นเผื่อถูกเรียกทางอื่น
     */
    wizSwitchMode(mode) {
      if (!this.canEdit || this.wiz.converting || this.wiz.creating) return;
      if (mode !== 'files' && mode !== 'folder') return;
      if (this.wiz.mode === mode) return;

      this.wizStopQueue();
      Object.assign(this.wiz, {
        mode: mode,
        step: 1,
        maxStep: 1,
        files: [],
        skipped: [],
        tooMany: false,
        sessionExpired: false,
        levelMapping: [],
        aiFilledLevels: [],
        folderNodeTypes: [],
        folderRelationMode: 'level',
        folderRelationsByLevel: [],
        folderRelations: [],
        tabularTypes: { file: '', sheet: '', row: '' },
        structuredSheets: [],
        sheetsLoading: [],
      });
    },
    wizAddFiles(list) {
      if (!this.canEdit || !list || !list.length) return;

      const rows = [];
      const skipped = [];
      let tooMany = false;

      list.forEach((file) => {
        const folderPath = this.wizFolderPathOf(file);
        const key = this.wizSlug(folderPath + '::' + file.name);
        if (this.wiz.files.some((f) => f.key === key) || rows.some((f) => f.key === key)) return;

        if (this.wiz.files.length + rows.length >= CONST_WIZ_MAX_FILES) {
          tooMany = true;
          return;
        }

        const ext = this.wizExt(file.name);
        // File ที่ถูก reactive proxy ครอบ ส่งเข้า FormData ไม่ได้ ต้อง markRaw ไว้
        const row = { key: key, name: file.name, folderPath: folderPath, size: file.size, ext: ext, blob: markRaw(file), status: 'pending', errorText: '', progress: 0, session: null, tableMode: '' };

        if (!CONST_WIZ_ACCEPT.includes(ext)) {
          row.status = 'unsupported';
          skipped.push(file.name);
        } else if (file.size > CONST_WIZ_MAX_BYTES) {
          row.status = 'error';
          row.errorText = this.$t('graphRagWizTooLarge');
        }

        rows.push(row);
      });

      this.wiz.files = this.wiz.files.concat(rows);
      if (skipped.length) this.wiz.skipped = this.wiz.skipped.concat(skipped);
      this.wiz.tooMany = tooMany;
      this.wizRunQueue();
    },
    wizRemoveFile(key) {
      const row = this.wiz.files.find((f) => f.key === key);
      if (row && row.status === 'converting' && this.wizAbort) this.wizAbort.abort();

      this.wiz.files = this.wiz.files.filter((f) => f.key !== key);
      // ชีตของไฟล์ที่ถูกเอาออกต้องหายไปด้วย ไม่งั้นตอนกดสร้างจะยิงนำเข้าไฟล์ที่ผู้ใช้ลบไปแล้ว
      this.wiz.structuredSheets = this.wiz.structuredSheets.filter((s) => s.fileKey !== key);
      this.wiz.sheetsLoading = this.wiz.sheetsLoading.filter((f) => f !== key);
    },
    /** ไฟล์ที่ผิดกติกาถาวรลองใหม่กี่รอบก็ไม่ผ่าน — ต้องไม่ยิงขึ้น backend และต้องคงข้อความสาเหตุเดิมไว้ */
    wizRetryBlockReason(row) {
      if (!CONST_WIZ_ACCEPT.includes(row.ext)) return 'unsupported';
      if (row.size > CONST_WIZ_MAX_BYTES) return 'tooLarge';

      return '';
    },
    wizRetryFile(key) {
      const row = this.wiz.files.find((f) => f.key === key);
      if (!row || row.status === 'unsupported') return;

      // ตรวจ size/ext ซ้ำด้วยกติกาเดียวกับตอนเพิ่มไฟล์ — ไม่งั้นปุ่มลองใหม่จะข้ามการ์ดไปอัปโหลดจริง
      const blocked = this.wizRetryBlockReason(row);
      if (blocked === 'unsupported') {
        row.status = 'unsupported';
        return;
      }

      if (blocked === 'tooLarge') {
        row.status = 'error';
        row.errorText = this.$t('graphRagWizTooLarge');
        return;
      }

      row.status = 'pending';
      row.errorText = '';
      row.progress = 0;
      this.wiz.sessionExpired = false;
      this.wizRunQueue();
    },
    wizClearQueue() {
      this.wizStopQueue();

      this.wiz.files = [];
      this.wiz.skipped = [];
      this.wiz.tooMany = false;
      this.wiz.sessionExpired = false;
      this.wiz.structuredSheets = [];
      this.wiz.sheetsLoading = [];
    },
    /**
     * หยุดคิวแปลงให้จบในจังหวะเดียว — ธง converting ต้องดับ "ก่อน" ที่ผู้เรียกจะปิด dialog
     * ไม่งั้น onIngestDialogToggle จะวนกลับมา wizRequestClose แล้วถามยืนยันซ้ำรอบสอง
     */
    wizStopQueue() {
      this.wizQueueStopped = true;
      if (this.wizAbort) this.wizAbort.abort();

      this.wizAbort = null;
      this.wiz.converting = false;
    },
    /** คิวแปลงต้องเรียงทีละไฟล์ ห้าม Promise.all — ไฟล์ล้ม 1 ตัวห้ามหยุดคิว ยกเว้นเจอ 401 */
    async wizRunQueue() {
      // กันลูปซ้อนด้วยธงนอก data — wiz.converting เป็นแค่สถานะให้ UI อ่าน และถูกดับก่อนลูปจบได้
      if (this.wizQueueRunning) return;

      this.wizQueueRunning = true;
      this.wizQueueStopped = false;
      this.wiz.converting = true;

      try {
        for (;;) {
          if (this.wizQueueStopped || this.wiz.sessionExpired) break;

          const row = this.wiz.files.find((f) => f.status === 'pending');
          if (!row) break;

          await this.wizConvertOne(row);
        }
      } finally {
        this.wizQueueRunning = false;
        this.wiz.converting = false;
        this.wizAbort = null;
      }

      // ไฟล์ที่ถูกเพิ่มระหว่างลูปเก่ากำลังคลี่ตัวจะถูกปฏิเสธไปแล้ว ต้องเดินต่อเอง ไม่งั้นค้าง "รอคิว" ถาวร
      if (!this.wizQueueStopped && !this.wiz.sessionExpired && this.wiz.files.some((f) => f.status === 'pending')) this.wizRunQueue();
    },
    /**
     * คำขออัปโหลดที่ล้มก่อนถึง ResponseModel (RequestSizeLimit คืน 400 ตัวเปล่า · 413 · 5xx · ต่อไม่ติด)
     * ต้องกลายเป็นเหตุผลรายไฟล์ที่ผู้ใช้อ่านรู้เรื่อง ไม่ใช่ "ส่งคำขอไม่สำเร็จ" ก้อนเดียวทุกกรณี
     */
    wizUploadErrorText(row, err) {
      const httpStatus = err && err.response ? err.response.status : 0;

      if (httpStatus === 413 || (httpStatus === 400 && row.size > CONST_WIZ_MAX_BYTES)) return this.$t('graphRagWizTooLarge');
      if (httpStatus === 400) return this.$t('graphRagWizConvertRejected');
      if (httpStatus >= 500) return this.$t('graphRagWizConvertServerError');
      if (!httpStatus) return this.$t('graphRagWizConvertNoServer');

      return this.$t('graphRagRequestFailed');
    },
    wizConvertOne(row) {
      return new Promise((resolve) => {
        row.status = 'converting';
        row.progress = 0;
        row.errorText = '';

        const controller = new AbortController();
        this.wizAbort = controller;

        const formData = new FormData();
        formData.append('file', row.blob);
        formData.append('graphKey', this.graphKey);

        const config = {
          signal: controller.signal,
          onUploadProgress: (event) => {
            row.progress = event.total ? Math.round((event.loaded / event.total) * 100) : 0;
          },
        };

        this.$call.GraphRagConvertDocumentAbortable(
          formData,
          config,
          (res) => {
            if (res.status !== 0) {
              row.status = 'error';
              row.errorText = res.message || this.$t('graphRagWizStatusError');
            } else {
              row.status = 'done';
              row.session = res.data;
            }

            return resolve();
          },
          (err) => {
            if (err && err.response && err.response.status === 401) {
              // เซสชันหมดอายุ ไฟล์ที่เหลือจะได้ 401 ต่อกันทั้งคิว จึงต้องหยุดตั้งแต่ตัวแรก
              this.wiz.sessionExpired = true;
              row.status = 'error';
              row.errorText = this.$t('graphRagWizSessionExpired');
            } else if (err && (err.code === 'ERR_CANCELED' || err.name === 'CanceledError')) {
              row.status = 'pending';
              row.progress = 0;
            } else {
              row.status = 'error';
              row.errorText = this.wizUploadErrorText(row, err);
            }

            return resolve();
          }
        );
      });
    },
    /** Esc และคลิกพื้นหลังต้องเดินเส้นทางเดียวกับปุ่มปิด ห้ามปิดเงียบตอนมีงานค้าง */
    onIngestDialogToggle(open) {
      if (open) {
        this.showIngest = true;
        return;
      }

      this.wizRequestClose();
    },
    /**
     * กันทำงานซ้อน: v-dialog คาย update:model-value(false) กลับมาอีกรอบหลังเราสั่งปิดเอง
     * ถ้าไม่กัน จะเด้งกล่องยืนยันซ้ำ (ต้องกด "ปิดและทิ้ง" สองครั้ง) และนับไฟล์ค้างผิด
     */
    wizRequestClose() {
      if (!this.showIngest) return;

      if (this.wiz.applied) {
        this.wizResetAll();
        this.showIngest = false;
        return;
      }

      if (this.wiz.creating) {
        // งานสร้างเป็น job ฝั่งเซิร์ฟเวอร์ ปิดแล้วไม่หาย จึงไม่ต้องถาม
        this.showIngest = false;
        this.notify(this.$t('graphRagWizRunsInBackground'), 'info');
        return;
      }

      if (this.wiz.converting) {
        this.wizConfirmKind = 'converting';
        this.wizConfirmCount = this.wizConvertingCount;
        this.wizConfirm = true;
        return;
      }

      if (this.wiz.files.length || this.wiz.step > 1) {
        this.wizConfirmKind = 'settings';
        this.wizConfirm = true;
        return;
      }

      this.showIngest = false;
    },
    wizMinimize() {
      this.showIngest = false;
    },
    wizConfirmMinimize() {
      this.wizConfirm = false;
      this.showIngest = false;
    },
    wizConfirmDiscard() {
      this.wizConfirm = false;
      // ล้างสถานะให้เรียบร้อยก่อนปิด — ปิดก่อนแล้วค่อยล้าง จะโดนถามยืนยันซ้ำอีกรอบ
      this.wizStopQueue();

      // ไฟล์ที่แปลงเสร็จแล้วต้องยังอยู่ตอนเปิดใหม่ — ทิ้งเฉพาะที่ยังค้างคิว
      this.wiz.files = this.wiz.files.filter((f) => f.status === 'done');
      this.showIngest = false;
    },
    wizResetAll() {
      clearInterval(this.wizJobTimer);
      this.wizStopQueue();

      const models = this.wiz.models;
      // ตัวเลือก "ตรวจก่อนนำเข้า" เป็นความชอบของผู้ใช้ — จำไว้ตลอดที่อยู่หน้านี้ ไม่รีเซ็ตทุกรอบ
      const reviewBeforeApply = this.wiz.reviewBeforeApply;
      Object.assign(this.wiz, emptyWizardState());
      this.wiz.models = models;
      this.wiz.reviewBeforeApply = reviewBeforeApply;
      // รอบใหม่ = ลืมโมเดลที่เลือกเองรอบก่อน กลับไปตาม Answer model ของกราฟ
      this.wizSyncModel();
    },
    /** ไฟล์ตารางที่ผู้ใช้เลือก "ตารางแบบกำหนดเอง" — เดินเส้นทาง structured import เดิม ไม่ผ่าน StartExtract */
    wizIsStructuredFile(file) {
      return (file.ext === '.csv' || file.ext === '.xlsx') && file.tableMode === 'structured';
    },
    /** ไฟล์ตารางโหมด "ตารางทั่วไป" — เดินเส้นทาง StartExtract แต่ต้องแตกเป็น ไฟล์ → ชีต → แถว (8-F4) */
    wizIsStandardTabularFile(file) {
      return (file.ext === '.csv' || file.ext === '.xlsx') && file.tableMode === 'standard';
    },
    /** ชีตที่พร้อมนำเข้าจริง = ถูกเลือกไว้ + ตั้งค่าคอลัมน์คีย์โหนดแล้ว + ไฟล์ต้นทางยังอยู่ในคิว */
    wizStructuredJobs() {
      const structuredKeys = this.wiz.files.filter((f) => f.status === 'done' && f.session && this.wizIsStructuredFile(f)).map((f) => f.key);

      return (this.wiz.structuredSheets || []).filter((row) => structuredKeys.indexOf(row.fileKey) >= 0 && row.include !== false && row.mapping && row.mapping.nodeKey);
    },
    wizCreate() {
      if (!this.canEdit || this.wiz.creating) return;

      // งานแตกโหนดรอบนี้จบแล้วแต่ยังไม่ได้เขียนลงกราฟ — ห้ามยิง StartExtract ซ้ำจากไฟล์ชุดเดียวกัน
      if (this.wiz.jobId && this.wiz.progStep === 4 && !this.wiz.applied) {
        // นำเข้าทันทีแต่ auto-apply ล้ม ⇒ เปิดข้อเสนอชุดเดิมให้นำเข้าเอง
        if (!this.wiz.reviewBeforeApply) {
          this.wiz.showProposals = true;
          return undefined;
        }

        // ตรวจแล้ว โมเดลเดิม ⇒ เขียนลงกราฟตอนนี้ · เปลี่ยนโมเดลหลังตรวจ ⇒ ไหลลงไปแตกโหนดใหม่ด้วยโมเดลใหม่
        if (this.wiz.modelId === this.wiz.jobModelId) return this.wizApplyReviewed();
      }

      // ตารางแบบกำหนดเองใช้ endpoint ของตัวเอง ⇒ เซสชันของมันต้องไม่ถูกส่งไป StartExtract ซ้ำ
      const sessions = this.wiz.files.filter((f) => f.status === 'done' && f.session && !this.wizIsStructuredFile(f)).map((f) => f.session.sessionId);
      const structuredJobs = this.wizStructuredJobs();

      if (!sessions.length && !structuredJobs.length) return undefined;
      if (sessions.length && !this.wiz.modelId) return undefined;

      this.wiz.creating = true;
      this.wiz.createError = '';
      this.wiz.duplicateJob = false;
      this.wiz.jobId = null;
      this.wiz.job = null;
      this.wiz.jobModelId = null;
      this.wiz.reviewSummary = null;
      this.wiz.progStep = 1;

      // โหมดตรวจก่อนสร้าง: รอบนี้แค่แตกโหนดให้ดู — ตารางแบบกำหนดเองเขียนลงกราฟตรง ๆ จึงเลื่อนไปทำตอนกด Create nodes จริง (wizApplyReviewed)
      if (this.wiz.reviewBeforeApply && sessions.length) return this.wizStartExtract(sessions);

      // ลำดับเดียวกับต้นทาง nexa (mdCreateBtn): ตารางก่อน แล้วค่อยงานที่ต้องให้โมเดลแตกโหนด
      if (structuredJobs.length) return this.wizRunStructured(structuredJobs, sessions);

      return this.wizStartExtract(sessions);
    },
    /**
     * โหมดตรวจก่อนสร้าง — ผู้ใช้ตรวจ/Reject แล้วกลับมากด Create nodes ที่ขั้น 5: เขียนลงกราฟตอนนี้
     * ตารางแบบกำหนดเองที่เลื่อนไว้ทำก่อน (ชีตต้องเห็นโหนดที่ชีตก่อนหน้าสร้าง) แล้วค่อยนำเข้าข้อเสนอที่ตรวจแล้ว
     */
    wizApplyReviewed() {
      this.wiz.creating = true;
      this.wiz.createError = '';
      this.wiz.progStep = 3;

      const structuredJobs = this.wizStructuredJobs();
      if (!structuredJobs.length) return this.wizAutoApply();

      return this.wizRunStructured(structuredJobs, [], () => this.wizAutoApply());
    },
    /** นับข้อเสนอของงานนี้ให้ขั้น 5 — ปฏิเสธ = rejected · ที่เหลือ (pending/accepted) คือที่จะนำเข้า */
    wizLoadReviewSummary() {
      const jobId = this.wiz.jobId;
      const tally = (rows) => ({
        keep: rows.filter((p) => p.status !== 'rejected' && p.status !== 'applied').length,
        rejected: rows.filter((p) => p.status === 'rejected').length,
      });

      this.$call.GraphRagGetProposals(
        'node',
        this.graphKey,
        jobId,
        (nodeRes) => {
          const nodes = tally(nodeRes.status === 0 ? nodeRes.data || [] : []);

          this.$call.GraphRagGetProposals(
            'edge',
            this.graphKey,
            jobId,
            (edgeRes) => {
              if (this.wiz.jobId !== jobId) return;

              const edges = tally(edgeRes.status === 0 ? edgeRes.data || [] : []);
              this.wiz.reviewSummary = { nodes: nodes.keep, edges: edges.keep, rejected: nodes.rejected + edges.rejected };
            },
            () => {}
          );
        },
        () => {}
      );
    },
    /** ยิงนำเข้าตารางทีละชีต ห้ามยิงพร้อมกัน — ชีตหลังต้องเห็นโหนดที่ชีตก่อนหน้าเพิ่งสร้าง */
    wizRunStructured(jobs, sessions, then) {
      const fail = (message) => {
        this.wiz.creating = false;
        // มีงานแตกโหนดที่ตรวจแล้วค้างอยู่ (then) ⇒ คงสถานะพร้อมสร้างไว้ ให้กด Create nodes ซ้ำได้
        this.wiz.progStep = then ? 4 : 0;
        this.wiz.createError = message || this.$t('graphRagWizCreateFailed');
      };

      const runOne = (index, done) => {
        if (index >= jobs.length && then) {
          if (done) this.notify(this.$t('graphRagWizStructuredDone', { count: done }), 'success');
          return then();
        }
        if (index >= jobs.length) return this.wizAfterStructured(done, sessions);

        const row = jobs[index];

        return this.$call.GraphRagImportExecute(
          {
            graphKey: this.graphKey,
            sessionId: row.sessionId,
            sheetName: row.sheetName,
            mapping: row.mapping,
            matchStrategy: row.matchStrategy,
            onMissing: row.onMissing,
          },
          (res) => {
            if (!res || res.status !== 0) return fail(res ? res.message : '');

            return runOne(index + 1, done + 1);
          },
          () => fail('')
        );
      };

      return runOne(0, 0);
    },
    /** นำเข้าตารางเขียนลงกราฟตรง ๆ ไม่มีข้อเสนอให้รีวิว ⇒ ถ้าไม่มีงานอื่นต่อ ให้จบที่นี่แล้วโหลดผืนกราฟใหม่ */
    wizAfterStructured(done, sessions) {
      if (done) this.notify(this.$t('graphRagWizStructuredDone', { count: done }), 'success');

      if (sessions.length) return this.wizStartExtract(sessions);

      this.wiz.progStep = 3;
      this.loadGraph();
      this.wiz.progStep = 4;
      this.wiz.creating = false;
      this.wiz.applied = true;

      // เขียนลงกราฟไปแล้ว ไม่มีข้อเสนอให้รีวิว ⇒ ปิด wizard ไม่งั้นกดปุ่มเดิมซ้ำจะนำเข้าตารางชุดเดิมอีกรอบ
      this.wizRequestClose();

      return undefined;
    },
    wizStartExtract(sessions) {
      const request = {
        graphKey: this.graphKey,
        sessionId: sessions[0],
        sessionIds: sessions,
        modelId: this.wiz.modelId,
        parentNodeKey: this.wiz.parentNodeKey || '',
        labelRule: this.wiz.labelRule,
        hierarchyDirection: this.wiz.hierarchyDirection,
        hierarchyRelation: this.wiz.hierarchyDirection === 'top_down' ? 'contains_section' : 'belongs_to_section',
        useParentLineage: this.wiz.useParentLineage,
        importMode: this.wiz.importMode,
      };

      // ค่าที่ผู้ใช้ไม่ได้ตั้งต้องไม่ถูกส่งไปเป็น default แทนผู้ใช้
      if (this.wiz.effectiveDate) request.effectiveDate = this.wiz.effectiveDate;
      if (this.wiz.expiryDate) request.expiryDate = this.wiz.expiryDate;
      if (this.wiz.levelMapping.length) request.levelMapping = this.wiz.levelMapping;
      // โหมดโฟลเดอร์ต้องส่งครบทุกครั้ง แม้ทุกโฟลเดอร์จะถูกตั้งเป็น "ไม่สร้างโหนด"
      // เพราะ backend ต้องรู้ทั้งผังและ relation เพื่อยกลูกไปแขวนบรรพบุรุษที่ใกล้ที่สุด (TC-40/41/42)
      if (this.wiz.mode === 'folder' || this.wiz.folderNodeTypes.length) {
        request.folderNodeTypes = this.wiz.folderNodeTypes;
        request.folderRelationMode = this.wiz.folderRelationMode;
        request.folderRelationsByLevel = this.wiz.folderRelationsByLevel;
        request.folderRelations = this.wiz.folderRelations;

        // 8-F2: path ของโฟลเดอร์เป็นข้อมูลฝั่งเบราว์เซอร์ล้วน (webkitRelativePath) และไม่ได้เดินทาง
        // ไปกับ ConvertToMarkdown ⇒ ถ้าไม่บอกตรงนี้ backend ไม่มีทางรู้ว่าไฟล์ไหนอยู่โฟลเดอร์ไหน
        // แล้วโหนดโฟลเดอร์จะไม่เกิดเลย (รอบเทสจริง 13 ไฟล์ ได้โหนดโฟลเดอร์ 0 ตัว)
        const folderRows = this.wiz.files.filter((f) => f.session && f.folderPath && sessions.indexOf(f.session.sessionId) >= 0);

        if (folderRows.length) request.sessionFolders = folderRows.map((f) => ({ sessionId: f.session.sessionId, folderPath: f.folderPath }));
      }

      // 8-F4: ชนิดโหนด 3 ชั้นของโหมดตารางทั่วไป — ตั้งไว้ที่ขั้น 4 แล้วแต่ยังไม่เคยถูกส่งมาก่อน
      // ⇒ backend เดินเส้นทางหัวข้อ แล้ว CSV/XLSX ได้แค่โหนดไฟล์กับโหนดชีต ไม่มีโหนดระดับแถวเลย
      // ไม่มีไฟล์ตารางโหมดทั่วไปในคิว = ไม่ส่งเลย ⇒ พฤติกรรมเดิมเป๊ะ
      const standardTabular = this.wiz.files.filter((f) => f.session && this.wizIsStandardTabularFile(f) && sessions.indexOf(f.session.sessionId) >= 0);

      if (standardTabular.length) {
        const types = this.wiz.tabularTypes || {};

        // ช่องที่ผู้ใช้ไม่ได้แตะยังเป็นค่าว่างใน state — ปล่อยว่างไปให้ backend เติมค่าเริ่มต้น
        // ชุดเดียวกับต้นทาง nexa ห้ามเดาค่าแทนผู้ใช้ที่นี่ (จะได้ชุดค่าเริ่มต้น 2 ที่ทันที)
        request.tabularTypes = { fileType: types.file || '', sheetType: types.sheet || '', rowType: types.row || '' };
      }

      return this.$call.GraphRagStartExtract(
        request,
        (res) => {
          if (res.status === 3) {
            // งานเดิมของกราฟนี้ยังวิ่งอยู่ — บอกให้ไปดูสถานะ ไม่ใช่ error แดง และห้ามยิงซ้ำเอง
            this.wiz.creating = false;
            this.wiz.progStep = 0;
            this.wiz.duplicateJob = true;
            return;
          }

          if (res.status !== 0) {
            this.wiz.creating = false;
            this.wiz.progStep = 0;
            this.wiz.createError = res.message || this.$t('graphRagWizCreateFailed');
            return;
          }

          this.wiz.job = res.data;
          this.wiz.jobId = res.data.jobId;
          this.wiz.jobModelId = request.modelId;
          this.wiz.progStep = 2;
          this.wizPollJob();
        },
        () => {
          this.wiz.creating = false;
          this.wiz.progStep = 0;
          this.wiz.createError = this.$t('graphRagWizCreateFailed');
        }
      );
    },
    /** timer เดียวเท่านั้น · clearInterval ก่อน setInterval เสมอ · เชื่อ status ที่ backend คืน */
    wizPollJob() {
      clearInterval(this.wizJobTimer);

      this.wizJobTimer = setInterval(() => {
        if (!this.wiz.jobId) return clearInterval(this.wizJobTimer);

        return this.$call.GraphRagGetIngestJob(
          this.graphKey,
          this.wiz.jobId,
          (res) => {
            if (res.status !== 0) return;

            this.wiz.job = res.data;
            if (res.data.status !== 'queued' && res.data.status !== 'running') {
              clearInterval(this.wizJobTimer);
              this.wizOnJobFinished();
            }
          },
          () => clearInterval(this.wizJobTimer)
        );
      }, 2000);
    },
    wizOnJobFinished() {
      const status = this.wiz.job ? this.wiz.job.status : '';

      if (status !== 'succeeded' && status !== 'partial') {
        this.wiz.creating = false;
        this.wiz.progStep = 0;
        this.wiz.createError = (this.wiz.job && this.wiz.job.errorMessage) || this.$t('graphRagWizCreateFailed');
        return;
      }

      if (this.wiz.reviewBeforeApply) return this.wizOpenReview();

      this.wiz.progStep = 3;
      this.wizAutoApply();
    },
    /**
     * ติ๊ก "ตรวจและ Reject ก่อนนำเข้า" — แตกโหนดเสร็จแล้วเปิดหน้าข้อเสนอแบบตรวจอย่างเดียว (ไม่มีปุ่ม Apply) ยังไม่เขียนลงกราฟ
     * ผู้ใช้ Reject รายการที่ไม่เอา → "กลับไปขั้นสร้าง" → ขั้น 5 สรุปผลการตรวจ (เปลี่ยนโมเดลได้) → Create nodes ⇒ wizApplyReviewed
     */
    wizOpenReview() {
      this.wiz.progStep = 4;
      this.wiz.creating = false;
      this.wiz.reviewSummary = null;
      this.wiz.showProposals = true;

      // wizard ถูกปิดระหว่างงานวิ่ง — หน้าข้อเสนออยู่ใน wizard จึงต้องบอกให้เปิดกลับมา
      if (!this.showIngest) this.notify(this.$t('graphRagWizReadyToReview'), 'info');
    },
    /**
     * นำเข้ากราฟทันทีที่แตกโหนดเสร็จ ไม่ต้องผ่านหน้ารีวิว — เหมือน nexa ที่ Create nodes แล้วเขียนลงกราฟเลย
     * ไม่มีอะไรถูกข้าม ⇒ ปิด wizard · มีคำเตือน ⇒ ค้างที่ขั้น 5 พร้อมปุ่มเปิดหน้าข้อเสนอไว้แก้ต่อ
     * ⚠️ สั่งจากหน้าเว็บ (ApplyAll ต้องมีผู้ใช้ login อยู่ job บน Hangfire เรียกเองไม่ได้)
     *    ⇒ ออกจากหน้ากราฟก่อนงานจบ = ข้อเสนอค้าง pending · ปิดแค่ wizard ไม่เป็นไร timer ยังเดินอยู่
     */
    wizAutoApply() {
      const jobId = this.wiz.jobId;
      const job = this.wiz.job || {};
      const jobWarnings = job.status === 'partial' ? [this.$t('graphRagWizJobPartialApplied', { count: job.failedUnits || 0 })] : [];

      this.$call.GraphRagApplyProposals(
        { graphKey: this.graphKey, jobId: jobId },
        (res) => {
          // ระหว่างรอ ผู้ใช้ล้าง wizard ไปแล้ว — ผลนี้ไม่ใช่ของรอบปัจจุบัน
          if (this.wiz.jobId !== jobId) return;
          if (!res || res.status !== 0) return this.wizAutoApplyFailed(res ? res.message : '');

          return this.wizShowApplied({ ...res.data, warnings: jobWarnings.concat(res.data.warnings || []) });
        },
        () => {
          if (this.wiz.jobId === jobId) this.wizAutoApplyFailed('');
        }
      );
    },
    wizShowApplied(result) {
      // ต้องจับไว้ก่อน wizResetAll — โมเดลของรอบนี้ (select ถูก disable ระหว่างสร้าง จึงเป็นตัวที่ส่งไปจริง)
      const usedModelId = this.wiz.modelId;
      const adopt = this.wizShouldAdoptModel(usedModelId);

      this.wiz.applied = true;
      this.wiz.applyResult = result;
      this.wiz.progStep = 4;
      this.wiz.creating = false;
      this.loadGraph();

      const summary = { nodes: result.nodeCount, edges: result.edgeCount };

      if (result.warnings.length) {
        // wizard ถูกปิดระหว่างรอ — บอกผ่าน snackbar · เปิด wizard ใหม่จะกลับมาที่ขั้น 5 พร้อมรายละเอียด
        const warn = this.showIngest ? '' : this.$t('graphRagWizAppliedWithWarnings', { ...summary, count: result.warnings.length });

        if (adopt) this.wizAdoptAnswerModel(usedModelId, warn, 'warning');
        else if (warn) this.notify(warn, 'warning');
        return;
      }

      const done = this.$t('graphRagProposalAppliedSummary', summary);

      if (adopt) this.wizAdoptAnswerModel(usedModelId, done, 'success');
      else this.notify(done, 'success');
      this.wizResetAll();
      this.showIngest = false;
    },
    /**
     * กราฟยังไม่ได้ตั้ง Answer model ⇒ นำเข้าสำเร็จแล้วตั้งโมเดลที่ใช้นำเข้าเป็น Answer model ให้ (ผู้ใช้สั่ง 2026-10-19)
     * ตั้งไว้แล้ว (แม้ตัวที่ตั้งจะไม่อยู่ในลิสต์) = ไม่ทับ · config ยังโหลดไม่เสร็จ = ไม่รู้ค่าจริง ห้ามเขียน
     */
    wizShouldAdoptModel(modelId) {
      return this.canEdit && this.configLoaded && !this.askModel && !this.savingAskModel && modelId != null;
    },
    /**
     * บันทึก config.model แบบเดียวกับ saveAskModel แต่ข้อความรวมกับสรุปผลนำเข้า (prefix) เป็น snackbar เดียว
     * ไม่งั้นข้อความนี้ทับสรุปผลจนผู้ใช้ไม่เห็น · ล้มเหลวไม่ต้องคืนค่า (ช่องยังเป็น "ไม่ตั้ง" อยู่แล้ว) แค่บอกให้ตั้งเองที่ toolbar
     */
    wizAdoptAnswerModel(modelId, prefix, colour) {
      const model = String(modelId);
      const item = this.wiz.models.find((m) => m.id === modelId);
      const label = item ? `${item.modelDisplay ?? ''} ${item.name}`.trim() : '#' + model;
      const say = (text, tone) => this.notify(prefix ? `${prefix} · ${text}` : text, tone);
      const failed = () => {
        this.savingAskModel = false;
        say(this.$t('graphRagWizAnswerModelAdoptFailed', { model: label }), 'warning');
      };

      this.savingAskModel = true;
      this.$call.GraphRagSaveConfig(
        this.graphKey,
        { ...this.savedConfig, model },
        (res) => {
          if (res.status !== 0) return failed();

          this.savingAskModel = false;
          this.savedConfig = { ...(res.data || { ...this.savedConfig, model }) };
          this.askModel = this.savedConfig.model;

          return say(this.$t('graphRagWizAnswerModelAdopted', { model: label }), colour || 'success');
        },
        failed
      );
    },
    /** นำเข้าอัตโนมัติไม่สำเร็จ — งานแตกโหนดจบแล้ว ⇒ ปุ่มหลักกลายเป็น "ตรวจสอบข้อเสนอ" ให้ลองนำเข้าเองจากหน้าข้อเสนอ */
    wizAutoApplyFailed(message) {
      this.wiz.creating = false;
      this.wiz.progStep = 4;
      this.wiz.createError = message || this.$t('graphRagWizAutoApplyFailed');

      if (!this.showIngest) this.notify(this.wiz.createError, 'error');
    },
    wizCancelJob() {
      if (!this.wiz.jobId) return;

      this.$call.GraphRagCancelIngestJob(
        { graphKey: this.graphKey, jobId: this.wiz.jobId },
        (res) => {
          if (res.status !== 0) return this.notify(res.message, 'error');

          clearInterval(this.wizJobTimer);
          this.wiz.creating = false;

          // ยกเลิกแล้วไม่ rollback — ข้อเสนอที่ทำไปแล้วยังอยู่ ต้องบอกผู้ใช้ให้ตรง
          return this.notify(this.$t('graphRagWizJobCancelled'), 'info');
        },
        () => this.notify(this.$t('graphRagRequestFailed'), 'error')
      );
    },
    /**
     * นำเข้าจากหน้าข้อเสนอเอง (ทางสำรองหลังนำเข้าอัตโนมัติมีคำเตือน/ล้ม)
     * ไม่มีอะไรถูกข้าม ⇒ ปิดทั้งหน้าต่างข้อเสนอและ wizard ทันที
     * มีคำเตือน ⇒ ค้างหน้าต่างข้อเสนอไว้ให้อ่าน แล้ว wizard จะปิดตามตอนผู้ใช้กด "เสร็จสิ้น" (watch 'wiz.showProposals')
     */
    wizOnApplied(result) {
      this.wiz.applied = true;
      this.wiz.appliedFromReview = true;
      this.loadGraph();

      // ProposalReview แจ้งสรุปผลไปแล้ว — ส่งข้อความเดิมเป็น prefix ให้รวมเป็น snackbar เดียว
      if (this.wizShouldAdoptModel(this.wiz.modelId)) {
        const done = result ? this.$t('graphRagProposalAppliedSummary', { nodes: result.nodeCount, edges: result.edgeCount }) : '';
        this.wizAdoptAnswerModel(this.wiz.modelId, done, result && result.warnings && result.warnings.length ? 'warning' : 'success');
      }

      if (result && result.warnings && result.warnings.length) return;

      this.wiz.showProposals = false;
      this.wizRequestClose();
    },
  },
};
</script>

<style scoped>
.colour-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
  border: 1px solid #dddddd;
}

/* ── แถบกราฟ + แถบค้นหา ตามหน้า graph editor ของ nexa ───────── */
.kgc-root {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  min-height: 0;
  background: #fff;
}
.kgc-toolbar,
.kgc-searchbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.kgc-toolbar { border-bottom: 1px solid #ececec; }
.kgc-searchbar { border-bottom: 1px solid #ececec; }
.kgc-spacer { flex: 1; }

.kgc-label { font-size: 11px; font-weight: 700; letter-spacing: 0.06em; color: #646464; text-transform: uppercase; }
.kgc-name {
  flex: 1;
  min-width: 220px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
  color: #171717;
  outline: none;
}
.kgc-name:focus { border-color: #c32e0e; }

.kgc-tag { border-radius: 14px; padding: 4px 12px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.kgc-tag-cloud { background: #f4f4f5; color: #545454; }
.kgc-tag-type { background: #fdeae7; color: #c32e0e; }

.kgc-version {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  background: #fff;
}
.kgc-ask-model { display: inline-flex; align-items: center; gap: 6px; }
.kgc-ask-model-select { width: 280px; flex: 0 0 auto; }
.kgc-ask-model-select :deep(.v-field) { font-size: 12px; border-radius: 8px; background: #fff; }
.kgc-ask-model-select :deep(.v-field__input) { min-height: 37px; padding-top: 6px; padding-bottom: 6px; flex-wrap: nowrap; }
.kgc-ask-model-select :deep(.v-field__prepend-inner .v-icon) { color: #c32e0e; opacity: 1; }
.kgc-ask-model-text { display: inline-flex; align-items: center; gap: 6px; min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-size: 12px; }
/* เลข model id หน้าชื่อ — ชิปเล็กโทนแดงตามธีม */
.kgc-ask-model-id { flex-shrink: 0; padding: 1px 6px; border-radius: 6px; background: #fdeae7; color: #c32e0e; font-size: 11px; font-weight: 700; }

/* ปุ่มโทนขาว-แดงตามธีมโปรเจกต์ (เหมือนหน้า GraphManager: REFRESH = outline · CREATE = gradient) */
.kgc-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #c32e0e;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 600;
  color: #c32e0e;
  background: #fff;
  cursor: pointer;
  white-space: nowrap;
}
.kgc-btn:hover:not(:disabled),
.kgc-btn[aria-pressed='true'] { background: #fdeae7; }
.kgc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.kgc-btn-primary { border-color: transparent; color: #fff; background: linear-gradient(100deg, #c32e0e 0%, #f67760 100%); }
.kgc-btn-primary:hover:not(:disabled) { background: linear-gradient(100deg, #a82508 0%, #e8634c 100%); }

/* ความคืบหน้าของงาน re-embed หลังกด Save — ชิปเล็กข้างปุ่ม ไม่บังแถบ */
.kgc-embed {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #f4c3b8;
  border-radius: 999px;
  background: #fdeae7;
  color: #c32e0e;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.kgc-embed-cancel {
  border: none;
  background: transparent;
  color: #c32e0e;
  cursor: pointer;
  font-size: 12px;
  padding: 0 2px;
}
.kgc-embed-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

/* ปุ่มที่ disabled ไม่ส่ง event เอง จึงต้องแขวน title ไว้ที่ span ครอบแทน */
.kgc-tip { display: inline-flex; }

.kgc-effort { display: flex; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; }
.kgc-effort button {
  border: none;
  background: #fff;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: #545454;
  cursor: pointer;
}
.kgc-effort button:hover:not(.active) { background: #fdeae7; color: #c32e0e; }
.kgc-effort button.active { background: #c32e0e; color: #fff; }

.kgc-query {
  flex: 1;
  min-width: 240px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 13px;
  outline: none;
}
.kgc-query:focus { border-color: #c32e0e; }

/* ── เนื้อหาหลัก + แผงคำตอบด้านขวา (.container [main | .sidebar]) — ผืนกราฟ / Graph Commands กินที่ที่เหลือ ── */
.kgc-body { flex: 1; min-height: 0; display: flex; }
/* overflow hidden = overlay ของ GraphCanvas (แผง 🔍 / แผงรายละเอียดโหนด) ไม่ล้นออกไปทับแผงคำตอบ */
.kgc-canvas { flex: 1; min-width: 0; min-height: 0; position: relative; overflow: hidden; }
.kgc-body > .gcc { min-width: 0; }

/* ── แผงคำตอบ Graph Ask — แถบขวาสูงเต็มพื้นที่ใต้แถบค้นหา · หัวแผงค้างบนสุด เนื้อหาเลื่อนในตัวเอง ── */
.kgc-ask {
  flex: 0 0 400px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #ececec;
  background: #fff;
  color: #171717;
}
.kgc-ask-head { display: flex; align-items: center; gap: 8px; flex-shrink: 0; padding: 10px 12px 10px 16px; border-bottom: 1px solid #ececec; }
.kgc-ask-body { flex: 1; min-height: 0; overflow-y: auto; padding: 14px 16px 18px; display: flex; flex-direction: column; gap: 10px; }
.kgc-ask-icon { display: inline-flex; align-items: center; justify-content: center; }
.kgc-ask-close:focus-visible { outline: 2px solid #c32e0e; outline-offset: 1px; }
/* พับแล้ว: แถบแคบทั้งแถบเป็นปุ่มขยาย · ชื่อแผงเขียนแนวตั้ง */
.kgc-ask.kgc-ask-collapsed { flex-basis: 44px; }
.kgc-ask-rail {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  border: none;
  background: #fff;
  color: #c32e0e;
  cursor: pointer;
  font-family: inherit;
}
.kgc-ask-rail:hover { background: #fff5f3; }
.kgc-ask-rail:focus-visible { background: #fff5f3; outline: 2px solid #c32e0e; outline-offset: -2px; }
.kgc-ask-rail-label { writing-mode: vertical-rl; font-size: 12px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; white-space: nowrap; }
.kgc-ask-title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: #c32e0e; }
.kgc-ask-btn {
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  background: #fff;
  color: #171717;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.kgc-ask-btn:hover { border-color: #c32e0e; color: #c32e0e; background: #fff5f3; }
.kgc-ask-close { border: none; background: transparent; color: #646464; cursor: pointer; font-size: 14px; padding: 2px 6px; border-radius: 6px; }
.kgc-ask-close:hover { color: #c32e0e; background: #fdeae7; }
.kgc-ask-loading { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #646464; }
.kgc-ask-answer { font-size: 14px; line-height: 1.65; white-space: pre-wrap; word-break: break-word; }
.kgc-ask-cite {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  margin: 0 2px;
  padding: 0 5px;
  border: 1px solid #f4c3b8;
  border-radius: 999px;
  background: #fdeae7;
  color: #c32e0e;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  vertical-align: text-top;
  white-space: nowrap;
  cursor: pointer;
}
.kgc-ask-cite:hover,
.kgc-ask-cite:focus-visible { background: #c32e0e; border-color: #c32e0e; color: #fff; outline: none; }
.kgc-ask-tip { font-size: 12px; line-height: 1.5; }
.kgc-ask-tip-title { font-weight: 700; }
.kgc-ask-tip-meta { opacity: 0.8; }
.kgc-ask-tip-excerpt { margin-top: 4px; white-space: pre-line; }
.kgc-ask-muted { font-size: 12px; color: #646464; line-height: 1.5; }
.kgc-ask-notice { display: flex; flex-direction: column; gap: 6px; }
.kgc-ask-notice-title { font-size: 14px; font-weight: 700; }
.kgc-ask-error { font-size: 13px; color: #c32e0e; line-height: 1.5; }
.kgc-ask-error-detail { margin-top: 2px; font-size: 12px; color: #646464; white-space: pre-wrap; word-break: break-word; }
.kgc-ask-warn { font-size: 12px; color: #8a5a00; background: #fff7e0; border: 1px solid #f5dfa3; border-radius: 8px; padding: 6px 10px; }
.kgc-ask-sources { display: flex; flex-direction: column; gap: 4px; border-top: 1px solid #ececec; padding-top: 8px; }
.kgc-ask-sources-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.kgc-ask-subtitle { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: #646464; }
.kgc-ask-link { border: none; background: transparent; color: #c32e0e; font-size: 12px; font-weight: 600; cursor: pointer; padding: 0; }
.kgc-ask-link:hover { text-decoration: underline; }
.kgc-ask-source {
  display: flex;
  align-items: baseline;
  gap: 6px;
  width: 100%;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  padding: 4px 8px;
  font-size: 13px;
  color: #171717;
  text-align: left;
  cursor: pointer;
}
.kgc-ask-source:hover,
.kgc-ask-source:focus-visible,
.kgc-ask-source.active { border-color: #f4c3b8; background: #fff5f3; outline: none; }
.kgc-ask-source-no { font-weight: 700; color: #c32e0e; }
.kgc-ask-source-label { font-weight: 600; overflow-wrap: anywhere; }
.kgc-ask-meta { display: flex; flex-wrap: wrap; gap: 6px; }
.kgc-ask-meta-item { font-size: 11px; color: #545454; background: #f4f4f5; border-radius: 999px; padding: 3px 10px; overflow-wrap: anywhere; }
/* จอกลาง: แผงแคบลงให้ผืนกราฟยังพอใช้ */
@media (max-width: 1280px) {
  .kgc-ask { flex-basis: 340px; }
}
/* จอแคบ (< 960px): แผงลงไปอยู่ใต้เนื้อหาหลัก สูงไม่เกินครึ่งพื้นที่ (เลื่อนในตัวเอง) · พับ = แถบแนวนอน · ลูกศรหมุนชี้ลง/ขึ้น */
@media (max-width: 959.98px) {
  .kgc-body { flex-direction: column; }
  .kgc-ask { flex: 0 0 auto; max-height: 50%; border-left: none; border-top: 1px solid #ececec; }
  .kgc-ask.kgc-ask-collapsed { flex-basis: auto; }
  .kgc-ask-rail { flex-direction: row; justify-content: center; padding: 10px 16px; }
  .kgc-ask-rail-label { writing-mode: horizontal-tb; }
  .kgc-ask-rail-icon,
  .kgc-ask-collapse-icon { transform: rotate(90deg); }
}

/* ── แผง Graph Commands (nexa: .graph-command-center) ────────── */
.gcc {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 22px;
  background: #f7f7f7;
  color: #171717;
}
.gcc-inner { max-width: 1120px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
.gcc-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; flex-wrap: wrap; }
.gcc-title { font-size: 22px; font-weight: 800; line-height: 1.1; color: #171717; }
.gcc-subtitle { margin-top: 5px; font-size: 13px; color: #646464; max-width: 660px; line-height: 1.45; }
.gcc-badge { font-size: 11px; border: 1px solid #f4c3b8; border-radius: 999px; padding: 4px 10px; background: #fdeae7; color: #c32e0e; white-space: nowrap; }
.gcc-grid { display: grid; grid-template-columns: 1fr; gap: 12px; align-items: stretch; }
.gcc-card {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.gcc-card-title { font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.06em; color: #c32e0e; }
.gcc-card-text { font-size: 12px; color: #646464; line-height: 1.45; }
.gcc-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.gcc-btn {
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #171717;
  border-radius: 7px;
  padding: 8px 11px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.gcc-btn:hover:not(:disabled) { border-color: #c32e0e; color: #c32e0e; background: #fff5f3; }
.gcc-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.gcc-btn.primary { background: linear-gradient(100deg, #c32e0e 0%, #f67760 100%); border-color: transparent; color: #fff; }
.gcc-btn.primary:hover:not(:disabled) { background: linear-gradient(100deg, #a82508 0%, #e8634c 100%); color: #fff; }
.gcc-btn.danger { background: #fff5f3; border-color: #f4c3b8; color: #c32e0e; }
.gcc-btn.danger:hover:not(:disabled) { background: #fdeae7; border-color: #c32e0e; }
.gcc-input-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.gcc-input {
  flex: 0 1 280px;
  min-width: 220px;
  border: 1px solid #e0e0e0;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 12px;
  font-family: inherit;
  background: #fff;
  color: #171717;
  outline: none;
}
.gcc-input:focus { border-color: #c32e0e; }
/* ช่องค้นโหนดด้วย label — กว้างเท่าช่อง Node ID เดิม · ขอบตอน focus แดงตามธีม */
.gcc-node-picker { flex: 0 1 320px; min-width: 240px; }
.gcc-node-picker :deep(.v-field) { font-size: 12px; background: #fff; }
.gcc-node-picker :deep(.v-field--focused .v-field__outline) { color: #c32e0e; }
.gcc-node-picker-selection { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 240px; }
/* ฟอร์มเส้น: ต้นทาง ⇄ ปลายทาง แถวเดียว · จอแคบเรียงลง */
.ge-edge-ends { display: flex; align-items: flex-start; gap: 4px; }
.ge-edge-end { flex: 1 1 0; min-width: 0; }
.ge-edge-swap { margin-top: 6px; }
.ge-edge-ref tbody tr { cursor: pointer; }
.ge-edge-ref-fit td { font-weight: 600; }
.ge-node-cell { padding: 4px 0; }
.ge-node-hier { padding: 10px 12px 6px; border-radius: 8px; background: rgba(0, 0, 0, 0.03); }
.ge-node-level-warn { cursor: pointer; }
.ge-node-open-parent { color: inherit; font-weight: 600; text-decoration: underline; }
.ge-node-key { font-size: 11px; color: #8a8a8a; word-break: break-all; line-height: 1.4; }
.ge-node-type-select { min-width: 150px; }
.ge-node-parent { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ge-node-parent-name { word-break: break-word; }
.ge-move-plan { font-size: 14px; line-height: 1.7; padding: 10px 12px; border-radius: 8px; background: rgba(0, 0, 0, 0.04); }
.ge-move-changes { margin: 4px 0 0 18px; font-size: 13px; max-height: 160px; overflow-y: auto; }
@media (max-width: 600px) {
  .ge-edge-ends { flex-direction: column; align-items: stretch; }
  .ge-edge-swap { align-self: center; margin-top: 0; }
}
.gcc-result { min-height: 20px; font-size: 12px; color: #646464; line-height: 1.5; white-space: pre-line; }
.gcc-result.error { color: #c32e0e; }
</style>

<style scoped>
/* badge ย่อหน้าต่างของ Import Wizard — ต้องลอยสูงกว่า v-dialog (2400) ไม่งั้นถูกบัง */
.wiz-mini {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2500;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  max-width: 360px;
}
.wiz-mini-text {
  font-size: 12px;
  color: #646464;
}
@media (max-width: 600px) {
  .wiz-mini {
    right: 8px;
    left: 8px;
    max-width: none;
    max-height: 56px;
  }
}
</style>
